import { showAlert, showConfirm } from './tgDialogs';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiUrl, getApiBaseUrl } from './api';
import {
  clearPendingProfileStartParam,
  getPendingProfileStartParam,
  getTelegramStartParam,
  getProfileUserIdFromPathname,
  getStoredPendingProfileUserId,
  initTelegram,
  persistProfileStartParamFromTelegram,
  stripProfileLaunchParamsFromLocation,
  waitForTelegramUser
} from './telegram';
import { payDigitalGoodWithStars } from './starsPay';
import ProfilePage from './ProfilePage';
import OrderChat from './OrderChat';
import OrderFiles from './OrderFiles';
import './App.css';
import './HomeMarketplace.css';
import './MarketplaceCards.css';
import { formatMicroUsdt, parseUsdtInputToMicro } from './usdtFormat';
import { orderRefLabel } from './orderRef';
import { orderCategoryLabel, shortCategoryLineFromUser, ORDER_CATEGORY_OPTIONS, subcategoryEntriesForMain, buildOrderCategoryToken } from './categoryLabels';
import { orderStatusLabel } from './orderStatusLabels';
import { PresenceStatusDot } from './avatarPresence';
import { FavoriteHeartIcon } from './FavoriteHeartIcon';
import { ServicesCatalogScreen, HomePopularServicesStrip } from './ExecutorServices';
import { BottomTabBar, type MainTab } from './BottomTabBar';
import { HomePromoShelf, defaultPromoSlides, type PromoSlide, type PromoSlideCta } from './HomePromoShelf';
import {
  HomeHeader,
  HomePage,
  HomeQuickActions,
  HomeSectionHeader,
  SegmentedTabs
} from './HomePage';
import BoostShop from './BoostShop';
import { CategoryEmblem, CategoryEmblemChip } from './CategoryEmblem';
import { EmptyState } from './EmptyState';
import { SkeletonOrderList } from './SkeletonCard';
import MessagesInbox, {
  fetchInboxUnreadTotal,
  markInboxThreadRead,
  type InboxThread
} from './MessagesInbox';
import './MessagesInbox.css';
import './CategoryEmblem.css';

const CATEGORY_OPTIONS = ORDER_CATEGORY_OPTIONS;

type AppNavScreen =
  | { type: 'services'; mode: 'catalog' | 'mine' | 'popular'; pane?: 'browse' | 'ordered' }
  | { type: 'profile'; userId: number };

/** Совпадает с бэкендом: заказы со свободной категорией (указать свою), не из списка slug */
const LEADERBOARD_OTHER_CATEGORY = '_other_';

type ExecutorResponseQuota = {
  free_executor_fee_waivers_left?: number | null;
  platform_commission_percent?: number;
  daily_response_limit?: number | null;
  daily_limit_applies?: boolean;
  responses_today?: number | null;
  remaining_responses_today?: number | null;
  executor_vip_active?: boolean;
  executor_vip_max_responses_per_24h?: number | null;
};

function pluralRuForms(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(Math.trunc(Number(n) || 0)) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (d === 1) return one;
  if (d >= 2 && d <= 4) return few;
  return many;
}

/** Подпись единицы срока с учётом числа: 1 день / 2 дня / 5 дней */
function durationUnitLabel(unit: string, count?: number | string | null): string {
  const n = count == null || count === '' ? 1 : Number(count);
  const v = Number.isFinite(n) ? n : 1;
  switch (unit) {
    case 'hours':
      return pluralRuForms(v, 'час', 'часа', 'часов');
    case 'days':
      return pluralRuForms(v, 'день', 'дня', 'дней');
    case 'weeks':
      return pluralRuForms(v, 'неделя', 'недели', 'недель');
    case 'months':
      return pluralRuForms(v, 'месяц', 'месяца', 'месяцев');
    default:
      return unit;
  }
}

function formatDurationValueUnit(value: number | string | null | undefined, unit: string | null | undefined): string {
  if (value == null || value === '' || !unit) return '';
  return `${value} ${durationUnitLabel(String(unit), value)}`;
}

/** Сколько полных календарных дней от сегодняшней даты до даты дедлайна (по локали устройства) */
function calendarDaysFromTodayToDate(deadlineIso: string): number {
  const d = new Date(deadlineIso);
  if (Number.isNaN(d.getTime())) return 0;
  const endDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const startDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((endDay.getTime() - startDay.getTime()) / 86400000);
}

function pluralRuDays(n: number): string {
  return pluralRuForms(n, 'день', 'дня', 'дней');
}

function pluralRuOrders(n: number): string {
  const abs = Math.abs(n) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return 'заказов';
  if (d === 1) return 'заказ';
  if (d >= 2 && d <= 4) return 'заказа';
  return 'заказов';
}

function pluralRuResponses(n: number): string {
  const abs = Math.abs(n) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return 'откликов';
  if (d === 1) return 'отклик';
  if (d >= 2 && d <= 4) return 'отклика';
  return 'откликов';
}

const HOME_ATTN_PRIORITY = { PAY: 0, CONFIRM: 1, OVERDUE: 2, SOON: 3, RESPONSES: 4 } as const;

type HomeAttentionItem = {
  orderId: number;
  order: any;
  priority: number;
  label: string;
  detail: string;
};

function buildHomeAttentionItem(order: any, role: 'customer' | 'executor'): HomeAttentionItem | null {
  const st = String(order?.status || '');
  const now = Date.now();
  if (
    role === 'customer' &&
    st === 'open' &&
    String(order?.selected_response_status || '') === 'negotiating'
  ) {
    return {
      orderId: Number(order.id),
      order,
      priority: HOME_ATTN_PRIORITY.PAY,
      label: 'Ожидает исполнителя',
      detail: order.title ? String(order.title) : `Заказ ${orderRefLabel(order.id)}`
    };
  }
  if (
    role === 'executor' &&
    st === 'open' &&
    String(order?.selected_response_status || '') === 'negotiating'
  ) {
    return {
      orderId: Number(order.id),
      order,
      priority: HOME_ATTN_PRIORITY.PAY,
      label: 'Обсудите услугу / готовность',
      detail: order.title ? String(order.title) : `Заказ ${orderRefLabel(order.id)}`
    };
  }
  if (
    role === 'customer' &&
    st === 'open' &&
    String(order?.selected_response_status || '') === 'pending_payment'
  ) {
    return {
      orderId: Number(order.id),
      order,
      priority: HOME_ATTN_PRIORITY.PAY,
      label: 'Оплатите эскроу',
      detail: order.title ? String(order.title) : `Заказ ${orderRefLabel(order.id)}`
    };
  }
  if (
    role === 'executor' &&
    st === 'open' &&
    String(order?.selected_response_status || '') === 'pending_payment'
  ) {
    return {
      orderId: Number(order.id),
      order,
      priority: HOME_ATTN_PRIORITY.PAY,
      label: 'Ожидает оплаты эскроу',
      detail: order.title ? String(order.title) : `Заказ ${orderRefLabel(order.id)}`
    };
  }
  if (st === 'awaiting_payment') {
    return {
      orderId: Number(order.id),
      order,
      priority: HOME_ATTN_PRIORITY.PAY,
      label: role === 'customer' ? 'Оплатите исполнителю' : 'Ожидает оплаты от заказчика',
      detail: order.title ? String(order.title) : `Заказ ${orderRefLabel(order.id)}`
    };
  }
  if (role === 'customer' && st === 'completed') {
    return {
      orderId: Number(order.id),
      order,
      priority: HOME_ATTN_PRIORITY.CONFIRM,
      label: 'Подтвердите выполнение',
      detail: order.title ? String(order.title) : `Заказ ${orderRefLabel(order.id)}`
    };
  }
  const dl = order.execution_deadline_at;
  if (dl && (st === 'in_progress' || st === 'completed')) {
    const end = new Date(dl).getTime();
    if (!Number.isNaN(end)) {
      if (end <= now) {
        return {
          orderId: Number(order.id),
          order,
          priority: HOME_ATTN_PRIORITY.OVERDUE,
          label: 'Срок выполнения истёк',
          detail: order.title ? String(order.title) : `Заказ ${orderRefLabel(order.id)}`
        };
      }
      const days = calendarDaysFromTodayToDate(dl);
      if (days <= 1 && days >= 0) {
        return {
          orderId: Number(order.id),
          order,
          priority: HOME_ATTN_PRIORITY.SOON,
          label: 'Скоро дедлайн',
          detail: formatOrderDeadlineRemaining(dl)
        };
      }
    }
  }
  if (role === 'customer' && st === 'open') {
    const rc = Number(order.responses_count || 0);
    if (rc > 0) {
      return {
        orderId: Number(order.id),
        order,
        priority: HOME_ATTN_PRIORITY.RESPONSES,
        label: `${rc} ${pluralRuResponses(rc)}`,
        detail: order.title ? String(order.title) : `Заказ ${orderRefLabel(order.id)}`
      };
    }
  }
  return null;
}

function chunkPairs<T>(arr: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += 2) out.push(arr.slice(i, i + 2));
  return out;
}

/** Оставшееся время до execution_deadline_at: календарные дни или часы/минуты в последний день */
function formatOrderDeadlineRemaining(deadlineIso: string | null | undefined): string {
  if (!deadlineIso) return '—';
  const end = new Date(deadlineIso).getTime();
  if (Number.isNaN(end)) return '—';
  const now = Date.now();
  if (end <= now) return 'Срок истёк';
  const days = calendarDaysFromTodayToDate(deadlineIso);
  if (days >= 1) {
    return `${days} ${pluralRuDays(days)}`;
  }
  const hours = Math.floor((end - now) / 3600000);
  if (hours >= 1) {
    return `осталось ${hours} ч`;
  }
  const minutes = Math.max(1, Math.floor((end - now) / 60000));
  return `осталось ${minutes} мин`;
}

function displayNameUser(u: any): string {
  const s = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
  return s || 'Пользователь';
}

/** Сумма сделки с выбранным исполнителем (proposed_budget), иначе бюджет карточки */
function orderDealBudgetMicro(order: any): number | null {
  const proposed = Number(order?.selected_proposed_budget);
  if (Number.isFinite(proposed) && proposed > 0) return proposed;
  const budget = Number(order?.budget);
  if (Number.isFinite(budget) && budget > 0) return budget;
  if (order?.budget != null && order.budget !== '') return Number(order.budget) || null;
  return null;
}

function leaderboardMedalEmoji(medal: string | null | undefined): string {
  if (medal === 'gold') return '🥇';
  if (medal === 'silver') return '🥈';
  if (medal === 'bronze') return '🥉';
  return '';
}

function leaderboardVolumeRightSlot(micro: number, role: 'customer' | 'executor'): React.ReactNode {
  const ico = role === 'customer' ? '💸' : '💰';
  const title = role === 'customer' ? 'Выплачено за период' : 'Заработано за период';
  if (!Number.isFinite(micro) || micro <= 0) {
    return (
      <span className="fc-lb-usdt fc-lb-usdt--stack fc-lb-usdt--zero" title="Нет оборота за период">
        <span className="fc-lb-usdt__ico" aria-hidden>
          {ico}
        </span>
        <span className="fc-lb-usdt__val">0 USDT</span>
      </span>
    );
  }
  return (
    <span className="fc-lb-usdt fc-lb-usdt--stack" title={title}>
      <span className="fc-lb-usdt__ico" aria-hidden>
        {ico}
      </span>
      <span className="fc-lb-usdt__val">
        {formatMicroUsdt(micro)} USDT
      </span>
    </span>
  );
}

function leaderboardStickyMoneyCell(micro: number, role: 'customer' | 'executor'): React.ReactNode {
  const ico = role === 'customer' ? '💸' : '💰';
  const title = role === 'customer' ? 'Выплачено за период' : 'Заработано за период';
  const isZero = !Number.isFinite(micro) || micro <= 0;
  return (
    <div className="fc-leaderboard-sticky__cell fc-leaderboard-sticky__cell--money" title={title}>
      <span className="fc-leaderboard-sticky__cell-ico" aria-hidden>
        {ico}
      </span>
      <span
        className={`fc-leaderboard-sticky__cell-text fc-leaderboard-sticky__cell-text--usdt${isZero ? ' fc-lb-usdt--zero' : ''}`.trim()}
      >
        {isZero ? '0 USDT' : `${formatMicroUsdt(micro)} USDT`}
      </span>
    </div>
  );
}

/** Название заказа и номер (#id) справа — везде в списках и карточке заказа */
function OrderTitleWithId({ title, id }: { title: string; id: number }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '12px',
        flexWrap: 'wrap',
        width: '100%'
      }}
    >
      <span style={{ minWidth: 0, flex: '1 1 auto', textAlign: 'left' }}>{title}</span>
      <span style={{ fontSize: '0.88em', color: 'var(--fc-text-muted)', fontWeight: 700, flexShrink: 0 }}>
        {orderRefLabel(id)}
      </span>
    </div>
  );
}

/** Компактная карточка заказа в списках — тот же размер/ритм, что у «Мои услуги» */
function CompactOrderListCard({
  order,
  budgetMicro,
  metaLine,
  statusAccent,
  onOpenDetails,
  onOpenProfile,
  detailsLabel = 'Подробнее',
  extraActions
}: {
  order: any;
  budgetMicro?: number | null;
  metaLine?: React.ReactNode;
  statusAccent?: React.ReactNode;
  onOpenDetails: () => void;
  onOpenProfile?: () => void;
  detailsLabel?: string;
  extraActions?: React.ReactNode;
}) {
  const deal =
    budgetMicro != null && Number.isFinite(Number(budgetMicro))
      ? Number(budgetMicro)
      : orderDealBudgetMicro(order);
  const desc = String(order?.description || '').trim();

  return (
    <div className="fc-compact-order-card">
      <div className="fc-compact-order-card__row">
        <CategoryEmblem category={order?.category} size="sm" />
        <button type="button" className="fc-compact-order-card__main" onClick={onOpenDetails}>
          <div className="fc-compact-order-card__title-row">
            <div className="fc-compact-order-card__title-block">
              <div className="fc-compact-order-card__title">{order?.title || 'Заказ'}</div>
              <div className="fc-compact-order-card__ref">{orderRefLabel(Number(order?.id))}</div>
            </div>
            <div className="fc-compact-order-card__price">
              {deal != null ? `${formatMicroUsdt(deal)} USDT` : '—'}
            </div>
          </div>
          <div className="fc-compact-order-card__cat">
            {orderCategoryLabel(order?.category)}
            {Number(order?.views_count) > 0 ? ` · 👁 ${Number(order.views_count)}` : ''}
          </div>
          {desc ? <div className="fc-compact-order-card__desc">{desc}</div> : null}
          {metaLine || statusAccent ? (
            <div className="fc-compact-order-card__meta">
              {statusAccent}
              {statusAccent && metaLine ? ' · ' : null}
              {metaLine}
            </div>
          ) : null}
        </button>
      </div>
      <div className="fc-compact-order-card__actions">
        {onOpenProfile ? (
          <button type="button" className="fc-btn fc-btn--ghost fc-btn--sm" onClick={onOpenProfile}>
            Профиль
          </button>
        ) : null}
        <button type="button" className="fc-btn fc-btn--primary fc-btn--sm" onClick={onOpenDetails}>
          {detailsLabel}
        </button>
        {extraActions}
      </div>
    </div>
  );
}

function offerStatusLabel(status: string | null | undefined): string {
  const s = String(status || '');
  if (s === 'pending') return 'ожидает ответа';
  if (s === 'accepted') return 'принято';
  if (s === 'rejected') return 'отклонено';
  if (s === 'cancelled') return 'отменено';
  return s || '—';
}

function canOpenOrderDispute(order: any, userRole: string | null): boolean {
  if (!order || !userRole) return false;
  const st = String(order.status || '');
  return (
    ['in_progress', 'completed', 'awaiting_payment'].includes(st) &&
    (userRole === 'customer' || userRole === 'executor')
  );
}

function responseStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return '⏳ Ожидает';
    case 'accepted':
      return '✅ Принят';
    case 'rejected':
      return '❌ Отклонён';
    default:
      return status;
  }
}

function isTelegraphUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
    return host === 'telegra.ph' || host === 'graph.org';
  } catch {
    return /telegra\.ph|graph\.org/i.test(url);
  }
}

function openExternalLink(url: string) {
  try {
    // @ts-ignore
    const tg = window?.Telegram?.WebApp;
    // @ts-ignore
    if (tg?.openTelegramLink && /^(https?:\/\/)?(t\.me|telegram\.me)\//i.test(url)) {
      return tg.openTelegramLink(url);
    }
    // Telegraph в боте открывается Instant View. Без флага iOS уводит telegra.ph в Safari.
    // @ts-ignore
    if (tg?.openLink) {
      if (isTelegraphUrl(url)) return tg.openLink(url, { try_instant_view: true });
      return tg.openLink(url);
    }
    window.open(url, '_blank');
  } catch {
    window.open(url, '_blank');
  }
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 20000
): Promise<Response> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: ctrl.signal });
  } finally {
    window.clearTimeout(t);
  }
}

const BOT_ORDER_CHAT_KEY = 'fcyx_bot_order_chat';

function readBotOrderChatFromLocation(): { orderId: number; peer: number | null } | null {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('focus') === 'order-chat') {
      const orderId = parseInt(q.get('order') || '', 10);
      const peer = parseInt(q.get('peer') || '', 10);
      if (Number.isFinite(orderId) && orderId > 0) {
        return { orderId, peer: Number.isFinite(peer) && peer > 0 ? peer : null };
      }
    }
  } catch {
    /* ignore */
  }
  const raw = window.location.hash.replace(/^#/, '').split('?')[0];
  const m = /^order\/(\d+)\/chat(?:\/(\d+))?/.exec(raw);
  if (!m) return null;
  const orderId = parseInt(m[1], 10);
  const peer = m[2] ? parseInt(m[2], 10) : NaN;
  if (!Number.isFinite(orderId) || orderId <= 0) return null;
  return { orderId, peer: Number.isFinite(peer) && peer > 0 ? peer : null };
}

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [supportBotUsername, setSupportBotUsername] = useState<string>('');
  const [legalDocs, setLegalDocs] = useState<
    { id: string; title_ru: string; title_en?: string; url: string }[]
  >([]);
  const [legalDocsVersion, setLegalDocsVersion] = useState('1.0');
  const [showLegalTerms, setShowLegalTerms] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  
  // Для создания задания
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showBoostShop, setShowBoostShop] = useState(false);
  const [homePromoSlides, setHomePromoSlides] = useState<PromoSlide[] | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categorySelect, setCategorySelect] = useState(''); // UI select value
  const [subcategorySelect, setSubcategorySelect] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [budget, setBudget] = useState('');
  const [responsesLimit, setResponsesLimit] = useState<string>(''); // '' = безлимитно
  const [hasExecutionDeadline, setHasExecutionDeadline] = useState(false);
  const [executionDeadlineValue, setExecutionDeadlineValue] = useState('');
  const [executionDeadlineUnit, setExecutionDeadlineUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('days');
  /** «Срочно»: списывается 1 слот (пакеты за Stars или из VIP заказчика) */
  const [orderUrgentBoost, setOrderUrgentBoost] = useState(false);
  const [orderTopBoost, setOrderTopBoost] = useState(false);
  
  // Для списка заданий заказчика
  const [myOrders, setMyOrders] = useState<any[]>([]);
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [myOrdersCategory, setMyOrdersCategory] = useState<string>('');
  const [myOrdersQuery, setMyOrdersQuery] = useState<string>('');
  
  // Для архива заказчика
  const [myArchivedCustomerOrders, setMyArchivedCustomerOrders] = useState<any[]>([]);
  const [showMyArchivedCustomerOrders, setShowMyArchivedCustomerOrders] = useState(false);
  const [myArchivedCustomerCategory, setMyArchivedCustomerCategory] = useState<string>('');
  const [myArchivedCustomerQuery, setMyArchivedCustomerQuery] = useState<string>('');
  /** Во вкладке «Мои задания»: in_work | all (ждут исполнителя) | archive */
  const [myOrdersListMode, setMyOrdersListMode] = useState<'in_work' | 'all' | 'archive'>('in_work');
  /** Витрина услуг: catalog | mine | popular */
  const [servicesScreen, setServicesScreen] = useState<'catalog' | 'mine' | 'popular' | null>(null);
  /** Сегмент таба «Задания»: заказы или услуги */
  const [dealsSegment, setDealsSegment] = useState<'orders' | 'services'>('orders');
  const [showMessagesInbox, setShowMessagesInbox] = useState(false);
  const [messagesBadge, setMessagesBadge] = useState(0);
  const [pendingOpenOrderChat, setPendingOpenOrderChat] = useState(false);
  const [chatLaunchNonce, setChatLaunchNonce] = useState(0);
  const [pendingChatPeerId, setPendingChatPeerId] = useState<number | null>(null);
  const [pendingChatResponseId, setPendingChatResponseId] = useState<number | null>(null);
  const [pendingOrderDetailsTab, setPendingOrderDetailsTab] = useState<'about' | 'executors' | null>(
    null
  );
  /** Тред открыт из «Сообщения» — назад/закрыть чата возвращает в inbox */
  const [openedFromInbox, setOpenedFromInbox] = useState(false);
  const inboxChatWasOpenRef = useRef(false);
  const orderDetailsBodyRef = useRef<HTMLDivElement | null>(null);
  /** Deep link service_N — открыть карточку */
  const [servicesFocusId, setServicesFocusId] = useState<number | null>(null);
  /** Вкладка каталога услуг заказчика: browse | ordered */
  const [servicesCatalogPane, setServicesCatalogPane] = useState<'browse' | 'ordered'>('browse');
  /** Стек «куда вернуться» после деталей заказа / профиля (иначе «Назад» кидал на главную). */
  const navStackRef = useRef<AppNavScreen[]>([]);
  const [homePopularServicesCategory, setHomePopularServicesCategory] = useState('');
  
  // Для просмотра откликов
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [responses, setResponses] = useState<any[]>([]);
  const [showResponses, setShowResponses] = useState(false);
  /** Вкладки на экране деталей заказа (заказчик, открытый заказ с откликами) */
  const [orderDetailsCustomerTab, setOrderDetailsCustomerTab] = useState<'about' | 'executors'>('about');
  const [orderDetailsResponses, setOrderDetailsResponses] = useState<any[]>([]);
  const [orderDetailsResponsesLoading, setOrderDetailsResponsesLoading] = useState(false);
  
  // Для каталога заданий (исполнитель)
  const [openOrders, setOpenOrders] = useState<any[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalogCategory, setCatalogCategory] = useState<string>('');
  const [catalogQuery, setCatalogQuery] = useState<string>('');
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogTotalPages, setCatalogTotalPages] = useState(1);
  const [catalogTotalCount, setCatalogTotalCount] = useState(0);
  /** Вкладка каталога: все / срочные / ТОП */
  const [catalogOnlyUrgent, setCatalogOnlyUrgent] = useState(false);
  const [catalogOnlyTop, setCatalogOnlyTop] = useState(false);
  const catalogFiltersRef = useRef({ category: '', q: '', onlyUrgent: false, onlyTop: false });
  const catalogSearchSkipDebounceRef = useRef(false);

  catalogFiltersRef.current = {
    category: catalogCategory,
    q: catalogQuery,
    onlyUrgent: catalogOnlyUrgent,
    onlyTop: catalogOnlyTop
  };
  
  // ДЛЯ ИСПОЛНИТЕЛЯ: активные задания (в работе)
  const [myActiveOrders, setMyActiveOrders] = useState<any[]>([]);
  const [showMyActiveOrders, setShowMyActiveOrders] = useState(false);
  const [myActiveOrdersCategory, setMyActiveOrdersCategory] = useState<string>('');
  const [myActiveOrdersQuery, setMyActiveOrdersQuery] = useState<string>('');
  
  // ДЛЯ ИСПОЛНИТЕЛЯ: архив заданий (выполненные/завершённые)
  const [myArchivedOrders, setMyArchivedOrders] = useState<any[]>([]);
  const [showMyArchivedOrders, setShowMyArchivedOrders] = useState(false);
  const [myArchivedOrdersCategory, setMyArchivedOrdersCategory] = useState<string>('');
  const [myArchivedOrdersQuery, setMyArchivedOrdersQuery] = useState<string>('');
  
  // ДЛЯ ИСПОЛНИТЕЛЯ: задания в ожидании
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [showPendingOrders, setShowPendingOrders] = useState(false);
  const [pendingOrdersCategory, setPendingOrdersCategory] = useState<string>('');
  const [pendingOrdersQuery, setPendingOrdersQuery] = useState<string>('');
  
  // ДЛЯ ЗАКАЗЧИКА: мои предложения
  const [myOffers, setMyOffers] = useState<any[]>([]);
  const [showMyOffers, setShowMyOffers] = useState(false);

  /** Главная: карусели и рекомендации */
  const [homePreviewOrders, setHomePreviewOrders] = useState<any[]>([]);
  /** Срочные/горящие в ленте открытых (только UI исполнителя) */
  const [homeUrgentOrders, setHomeUrgentOrders] = useState<any[]>([]);
  const [homeTopOrders, setHomeTopOrders] = useState<any[]>([]);
  /** Новые open-заказы на главной исполнителя (≤3 дня) */
  const [homeNewOrders, setHomeNewOrders] = useState<any[]>([]);
  /** Лента «Все задания» на главной исполнителя */
  const [homeAllFeedOrders, setHomeAllFeedOrders] = useState<any[]>([]);
  /** Новые | Все задания */
  const [homeOrdersFeedTab, setHomeOrdersFeedTab] = useState<'new' | 'all'>('new');
  /** Срочные | ТОП в объединённом блоке */
  const [homeBoostOrdersTab, setHomeBoostOrdersTab] = useState<'urgent' | 'top'>('urgent');
  /** Вкладка discovery на главной заказчика */
  const [homeCustomerDiscoverTab, setHomeCustomerDiscoverTab] = useState<
    'recommended' | 'vip' | 'top' | 'services'
  >('recommended');
  const [homeRecommendUsers, setHomeRecommendUsers] = useState<any[]>([]);
  const [homeVipUsers, setHomeVipUsers] = useState<any[]>([]);
  const [homeTopExecutors, setHomeTopExecutors] = useState<any[]>([]);
  const [homeExecutorVipUsers, setHomeExecutorVipUsers] = useState<any[]>([]);
  const [execVipDirectoryModalOpen, setExecVipDirectoryModalOpen] = useState(false);
  const [execVipDirectoryCategory, setExecVipDirectoryCategory] = useState<string>('');
  const [execVipDirectoryUsers, setExecVipDirectoryUsers] = useState<any[]>([]);
  const [execVipDirectoryLoading, setExecVipDirectoryLoading] = useState(false);
  const [topDirectoryModalOpen, setTopDirectoryModalOpen] = useState(false);
  const [topDirectoryCategory, setTopDirectoryCategory] = useState<string>('');
  const [topDirectoryUsers, setTopDirectoryUsers] = useState<any[]>([]);
  const [topDirectoryLoading, setTopDirectoryLoading] = useState(false);
  const [homeFavoriteUsers, setHomeFavoriteUsers] = useState<any[]>([]);
  const [homeFavoriteOrders, setHomeFavoriteOrders] = useState<any[]>([]);
  /** id заказов в избранном — для каталога «Найти заказы» */
  const [catalogFavoriteOrderIds, setCatalogFavoriteOrderIds] = useState<Record<number, boolean>>({});
  const [catalogFavoriteBusyId, setCatalogFavoriteBusyId] = useState<number | null>(null);
  /** Вкладка «Избранное» на главной для исполнителя */
  const [homeExecutorFavoritesTab, setHomeExecutorFavoritesTab] = useState<'orders' | 'customers'>('orders');
  /** Секция «Избранное» на главной (заказчик и исполнитель) */
  const [homeFavoritesCollapsed, setHomeFavoritesCollapsed] = useState(false);
  /** Колокольчик: список «требует внимания» на главной */
  const [homeNotifOpen, setHomeNotifOpen] = useState(false);
  /** Координаты выпадашки под колокольчиком (fixed — корректно в Telegram WebView) */
  const [homeNotifDropdownStyle, setHomeNotifDropdownStyle] = useState<React.CSSProperties | null>(null);
  const homeNotifBellRef = useRef<HTMLDivElement | null>(null);
  /** Таблица лидеров (заказчики / исполнители, топ завершённых сделок) */
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardTabRole, setLeaderboardTabRole] = useState<'customer' | 'executor' | 'referrals'>('customer');
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<'month' | 'year' | 'all'>('month');
  const [leaderboardCategory, setLeaderboardCategory] = useState<string>('');
  const [leaderboardEntries, setLeaderboardEntries] = useState<any[]>([]);
  const [leaderboardPrizeHint, setLeaderboardPrizeHint] = useState<string | null>(null);
  const [leaderboardViewer, setLeaderboardViewer] = useState<{
    rank: number | null;
    finished_count?: number;
    qualified_count?: number;
    referral_level?: number;
    next_level?: number | null;
    remaining?: number;
    slots_on_reach?: number | null;
    xp: number;
    volume_usdt_micro?: string;
  } | null>(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState<string | null>(null);
  /** Избранное (заказ) на экране «Детали задания» — только исполнитель, открытый заказ */
  const [orderDetailsOrderFavorite, setOrderDetailsOrderFavorite] = useState<boolean | null>(null);
  const [orderDetailsFavoriteBusy, setOrderDetailsFavoriteBusy] = useState(false);
  /** Облако файлов заказа перекрывает шапку «Детали задания» */
  const [orderFilesCloudOpen, setOrderFilesCloudOpen] = useState(false);
  /** Полноэкранный чат по заказу */
  const [orderChatOpen, setOrderChatOpen] = useState(false);
  /** Обновление подписи «осталось N дн.» на главной раз в минуту */
  const [deadlineTick, setDeadlineTick] = useState(0);

  /** UI главной может быть «как у исполнителя» при role === null, если user.role уже есть — для загрузки превью заказов */
  const effectiveHomeRole = useMemo(() => role ?? user?.role ?? null, [role, user?.role]);

  const homeAttentionList = useMemo(() => {
    if (effectiveHomeRole !== 'customer' && effectiveHomeRole !== 'executor') return [];
    const items: HomeAttentionItem[] = [];
    for (const order of homePreviewOrders) {
      const it = buildHomeAttentionItem(order, effectiveHomeRole);
      if (it) items.push(it);
    }
    items.sort((a, b) => a.priority - b.priority || Number(b.orderId) - Number(a.orderId));
    return items;
  }, [homePreviewOrders, effectiveHomeRole]);

  useLayoutEffect(() => {
    if (!homeNotifOpen) {
      setHomeNotifDropdownStyle(null);
      return;
    }
    const anchor = homeNotifBellRef.current;
    if (!anchor) {
      setHomeNotifDropdownStyle(null);
      return;
    }
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const m = 8;
      // Одна система координат с getBoundingClientRect (окно мини-аппа / iframe)
      const vpW = document.documentElement.clientWidth || window.innerWidth;

      const maxW = Math.min(340, vpW - 2 * m);
      const bellRight = Math.min(r.right, vpW - m);

      let width = maxW;
      let left = bellRight - width;
      if (left < m) {
        left = m;
        width = Math.min(maxW, bellRight - m);
      }
      if (width < 140) {
        width = Math.min(maxW, vpW - 2 * m);
        left = Math.max(m, bellRight - width);
        if (left < m) left = m;
      }
      if (left + width > vpW - m) {
        width = Math.max(140, vpW - m - left);
      }

      setHomeNotifDropdownStyle({
        position: 'fixed',
        left,
        top: r.bottom + m,
        width,
        right: 'auto',
        maxWidth: 'none',
        boxSizing: 'border-box',
        zIndex: 103
      });
    };
    place();
    const vv = window.visualViewport;
    window.addEventListener('resize', place);
    document.addEventListener('scroll', place, true);
    vv?.addEventListener('resize', place);
    vv?.addEventListener('scroll', place);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
      vv?.removeEventListener('resize', place);
      vv?.removeEventListener('scroll', place);
    };
  }, [homeNotifOpen]);

  /** Рекомендации: горизонтальный скролл «страниц», на каждой — 3 человека столбиком (до 24) */
  const recoUserPanels = useMemo(() => {
    const list = homeRecommendUsers.slice(0, 24);
    const out: any[][] = [];
    for (let i = 0; i < list.length; i += 3) {
      out.push(list.slice(i, i + 3));
    }
    return out;
  }, [homeRecommendUsers]);

  /** Задания на главной исполнителя: 3 в колонке, скролл вправо */
  const homeOrderFeedPanels = useMemo(() => {
    const list = (homeOrdersFeedTab === 'new' ? homeNewOrders : homeAllFeedOrders).slice(0, 24);
    const out: any[][] = [];
    for (let i = 0; i < list.length; i += 3) {
      out.push(list.slice(i, i + 3));
    }
    return out;
  }, [homeOrdersFeedTab, homeNewOrders, homeAllFeedOrders]);

  const homeBoostFeedOrders = homeBoostOrdersTab === 'urgent' ? homeUrgentOrders : homeTopOrders;

  // ДЛЯ ЗАКАЗЧИКА: поиск исполнителя и отправка предложения
  const [showFindExecutor, setShowFindExecutor] = useState(false);
  const [offerOrder, setOfferOrder] = useState<any>(null);
  const [executorQuery, setExecutorQuery] = useState<string>('');
  const [executorCategory, setExecutorCategory] = useState<string>('');
  const [executorResults, setExecutorResults] = useState<any[]>([]);
  const [executorSearchLoading, setExecutorSearchLoading] = useState(false);
  const [executorFavoriteIds, setExecutorFavoriteIds] = useState<Record<number, boolean>>({});
  const [executorFavoriteBusyId, setExecutorFavoriteBusyId] = useState<number | null>(null);
  const [offerBusyId, setOfferBusyId] = useState<number | null>(null);
  
  // Для создания отклика
  const [respondingTo, setRespondingTo] = useState<any>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseBudget, setResponseBudget] = useState('');
  /** client = согласен с бюджетом в заказе; custom = своя сумма */
  const [responseBudgetMode, setResponseBudgetMode] = useState<'client' | 'custom'>('client');
  const [responseEtaValue, setResponseEtaValue] = useState('');
  const [responseEtaUnit, setResponseEtaUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('days');
  const [minOrderBudgetMicro, setMinOrderBudgetMicro] = useState(2_000_000);
  const [executorResponseQuota, setExecutorResponseQuota] = useState<ExecutorResponseQuota | null>(null);
  // Переопределение срока выполнения заказчиком при выборе исполнителя
  const [acceptDeadlineValueByResp] = useState<Record<number, string>>({});
  const [acceptDeadlineUnitByResp] = useState<Record<number, 'hours' | 'days' | 'weeks' | 'months'>>({});
  const [showAssignExecutorModal, setShowAssignExecutorModal] = useState(false);
  const [assigningResp, setAssigningResp] = useState<any | null>(null);
  const [assignDeadlineValue, setAssignDeadlineValue] = useState<string>('');
  const [assignDeadlineUnit, setAssignDeadlineUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('days');
  const [assignBusy, setAssignBusy] = useState(false);

  // Для отображения деталей задания
  const [viewingOrder, setViewingOrder] = useState<any>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [orderChannelBusy, setOrderChannelBusy] = useState(false);
  /** Отзыв по текущему заказу (заказчик + finished) */
  const [orderReviewInfo, setOrderReviewInfo] = useState<any>(null);
  const [reviewStars, setReviewStars] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  /** Баланс (микро-USDT) для экрана оплаты промо-заказа */
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [promoPayAmount, setPromoPayAmount] = useState('');
  const [promoPayBusy, setPromoPayBusy] = useState(false);
  const [showServiceBudgetModal, setShowServiceBudgetModal] = useState(false);
  const [serviceBudgetAmount, setServiceBudgetAmount] = useState('');
  const [serviceBudgetScope, setServiceBudgetScope] = useState<'order' | 'service'>('order');
  /** service = исполнитель по услуге; order = заказчик по своему заданию */
  const [budgetModalKind, setBudgetModalKind] = useState<'service' | 'order'>('service');
  /** true = ответ на чужое предложение (POST …/budget-pending/counter) */
  const [budgetModalCounter, setBudgetModalCounter] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [disputeText, setDisputeText] = useState('');
  const [disputeBusy, setDisputeBusy] = useState(false);
  /** Смена исполнителя после просрочки дедлайна (заказчик) */
  const [deadlineReplaceInfo, setDeadlineReplaceInfo] = useState<{
    eligible?: boolean;
    penalty_xp?: number;
    other_responses?: any[];
  } | null>(null);
  const [showDeadlineReplaceModal, setShowDeadlineReplaceModal] = useState(false);
  const [deadlineReplacePickId, setDeadlineReplacePickId] = useState<number | ''>('');
  const [deadlineReplaceBusy, setDeadlineReplaceBusy] = useState(false);
  const [dlReplaceValue, setDlReplaceValue] = useState('');
  const [dlReplaceUnit, setDlReplaceUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('hours');
  const [showExtendDeadlineModal, setShowExtendDeadlineModal] = useState(false);
  const [extendDlValue, setExtendDlValue] = useState('');
  const [extendDlUnit, setExtendDlUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('hours');
  const [extendDeadlineBusy, setExtendDeadlineBusy] = useState(false);

  // Для просмотра профиля: /u/id или sessionStorage из index.html (start_param с t.me)
  const [viewingProfile, setViewingProfile] = useState<number | null>(() => {
    return getProfileUserIdFromPathname() ?? getStoredPendingProfileUserId();
  });
  /** Скролл к блоку на своём профиле: category-alerts | subscription */
  const [profileFocusSection, setProfileFocusSection] = useState<string | null>(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('focus');
      if (q === 'category-alerts' || q === 'subscription') return q;
      const h = window.location.hash.replace(/^#/, '');
      if (h === 'me/category-alerts') return 'category-alerts';
      if (h === 'me/subscription') return 'subscription';
    } catch {
      /* ignore */
    }
    return null;
  });
  const deepLinkHandled = useRef(false);

  const refreshWalletBalance = useCallback(async () => {
    if (!token) return;
    try {
      await fetch(apiUrl('/api/wallet/sync-cryptopay'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      /* сеть / таймаут — всё равно читаем баланс */
    }
    try {
      const r = await fetch(apiUrl('/api/wallet/balance'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success && typeof d.balance === 'number') setWalletBalance(d.balance);
      else setWalletBalance(null);
    } catch {
      setWalletBalance(null);
    }
  }, [token]);

  useEffect(() => {
    if (
      !showOrderDetails ||
      !viewingOrder ||
      viewingOrder.status !== 'awaiting_payment' ||
      role !== 'customer'
    ) {
      return;
    }
    void refreshWalletBalance();
    if (viewingOrder.budget != null) setPromoPayAmount(formatMicroUsdt(viewingOrder.budget));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- достаточно id/status/budget, не всего объекта viewingOrder
  }, [
    showOrderDetails,
    viewingOrder?.id,
    viewingOrder?.status,
    viewingOrder?.budget,
    role,
    refreshWalletBalance
  ]);

  /** Отзыв заказчика по завершённому заказу (отдельно от viewOrderDetails — надёжнее после оплаты) */
  useEffect(() => {
    if (!showOrderDetails || !viewingOrder || !token) return;
    if (viewingOrder.status !== 'finished') {
      setOrderReviewInfo(null);
      return;
    }
    if (role !== 'customer') {
      setOrderReviewInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rr = await fetch(apiUrl(`/api/reviews/order/${viewingOrder.id}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const rev = await rr.json();
        if (cancelled) return;
        if (rev && rev.success) {
          setOrderReviewInfo(rev);
        } else {
          setOrderReviewInfo({
            success: true,
            can_review: false,
            has_review: false,
            review: null
          });
        }
      } catch {
        if (!cancelled) {
          setOrderReviewInfo({
            success: true,
            can_review: false,
            has_review: false,
            review: null
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- достаточно id/status, не всего объекта viewingOrder
  }, [showOrderDetails, viewingOrder?.id, viewingOrder?.status, role, token]);

  /** Просрочка дедлайна + «в работе»: опции смены исполнителя */
  useEffect(() => {
    if (!showOrderDetails || !viewingOrder?.id || !token || role !== 'customer') {
      setDeadlineReplaceInfo(null);
      return;
    }
    if (viewingOrder.status !== 'in_progress') {
      setDeadlineReplaceInfo(null);
      return;
    }
    const at = viewingOrder.execution_deadline_at;
    if (!at || !Number.isFinite(new Date(at).getTime()) || new Date(at).getTime() >= Date.now()) {
      setDeadlineReplaceInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`/api/orders/${viewingOrder.id}/deadline-replacement`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!cancelled && data.success) {
          setDeadlineReplaceInfo(data);
        }
      } catch {
        if (!cancelled) setDeadlineReplaceInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showOrderDetails,
    viewingOrder?.id,
    viewingOrder?.status,
    viewingOrder?.execution_deadline_at,
    role,
    token
  ]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
    console.log('🔄 App загрузился');
    console.log('📊 URL параметры:', window.location.search);
    
        initTelegram();

        // Публичные параметры (без JWT): username support-bot для ссылки “Связаться с поддержкой”
        try {
          const cfgRes = await fetchWithTimeout(apiUrl('/api/public/config'), {}, 8000);
          const cfg = await cfgRes.json();
          setSupportBotUsername(String(cfg?.support_bot_username || '').trim());
          if (Array.isArray(cfg?.legal_documents?.docs)) {
            setLegalDocs(
              cfg.legal_documents.docs.filter(
                (d: { url?: string }) => d?.url && String(d.url).trim()
              )
            );
            setLegalDocsVersion(String(cfg?.legal_documents?.version || '1.0'));
          }
          if (cfg?.min_order_budget_usdt != null) {
            const n = Number(cfg.min_order_budget_usdt);
            if (Number.isFinite(n) && n >= 1) {
              setMinOrderBudgetMicro(Math.round(n * 1_000_000));
            }
          }
        } catch {
          // Не критично: если username не пришёл — покажем alert при нажатии
        }

        const readHandoffTicket = () => {
          try {
            const fromPath = /\/ah\/(v1\.\d+\.\d+\.[a-f0-9]{64})\/?$/i.exec(window.location.pathname || '');
            if (fromPath) return fromPath[1];
          } catch {
            /* ignore */
          }
          try {
            const fromQuery = /(?:^|[?&])ah=(v1\.\d+\.\d+\.[a-f0-9]{64})/i.exec(window.location.search || '');
            if (fromQuery) return fromQuery[1];
          } catch {
            /* ignore */
          }
          return '';
        };

        const stripAhFromUrl = () => {
          try {
            const path = String(window.location.pathname || '').replace(
              /\/ah\/v1\.\d+\.\d+\.[a-f0-9]{64}\/?$/i,
              ''
            );
            const qs = String(window.location.search || '')
              .replace(/([?&])ah=v1\.\d+\.\d+\.[a-f0-9]{64}&?/i, '$1')
              .replace(/[?&]$/, '');
            window.history.replaceState({}, '', (path || '/') + qs + window.location.hash);
          } catch {
            /* ignore */
          }
        };

        /** Билет в пути /ah/v1…: нижняя кнопка на iPhone не приносит query и initData. */
        const ahParam = readHandoffTicket();
        if (ahParam) {
          try {
            const hr = await fetchWithTimeout(
              apiUrl('/api/auth/telegram-handoff'),
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ah: ahParam })
              },
              15000
            );
            const hd = await hr.json().catch(() => ({}));
            if (hr.ok && hd.success && hd.token && hd.user && !cancelled) {
              localStorage.setItem('token', hd.token);
              stripAhFromUrl();
              setToken(hd.token);
              const u = hd.user;
              setUser({
                ...u,
                first_name: u.first_name,
                last_name: u.last_name
              });
              if (u.role === 'pending') {
                setShowRoleSelection(true);
    } else {
                setRole(u.role);
              }
              setLoading(false);
              return;
            }
          } catch {
            /* обычная авторизация */
          }
          stripAhFromUrl();
        }

        /**
         * С кнопки reply keyboard Web App часто открывается без initData / user (iOS).
         * Если пользователь уже заходил — JWT в localStorage, поднимаем сессию по /profile.
         */
        const storedToken = localStorage.getItem('token')?.trim();
        if (storedToken) {
          try {
            const pr = await fetchWithTimeout(
              apiUrl('/api/auth/profile'),
              { headers: { Authorization: `Bearer ${storedToken}` } },
              15000
            );
            const pd = await pr.json().catch(() => ({}));
            if (pr.ok && pd.success && pd.user && !cancelled) {
              console.log('✅ Сессия восстановлена по сохранённому токену (reply keyboard / без initData)');
              setToken(storedToken);
              const u = pd.user;
              setUser({
                ...u,
                first_name: u.first_name,
                last_name: u.last_name
              });
              if (u.role === 'pending') {
                setShowRoleSelection(true);
              } else {
                setRole(u.role);
              }
              setLoading(false);
              return;
            }
          } catch {
            /* падаем в обычную авторизацию */
          }
          localStorage.removeItem('token');
        }

        const telegramUser = await waitForTelegramUser();
        // @ts-ignore
        const tg = window.Telegram?.WebApp;
        const initData = typeof tg?.initData === 'string' ? tg.initData : '';

        console.log('👤 Telegram user после ожидания:', telegramUser);

        if (!telegramUser?.id) {
          console.error('❌ Не удалось получить пользователя Telegram');
          if (!cancelled) {
            setAuthError(
              'Не удалось получить данные пользователя Telegram. Закройте мини-апп, откройте чат с ботом и снова нажмите «Биржа» внизу (или «Открыть биржу» в сообщении).'
            );
            setLoading(false);
          }
          return;
        }

        if (!cancelled) setUser(telegramUser);

        const authUrl = apiUrl('/api/auth/telegram');
        const res = await fetchWithTimeout(
          authUrl,
          {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ initData, user: telegramUser })
          },
          25000
        );
        const raw = await res.text();
        let data: any;
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          throw new Error(
            'Сервер вернул не JSON (проверьте nginx и что API доступен по /api). Ответ: ' +
              raw.slice(0, 120)
          );
        }
        if (!res.ok) {
          if (data?.code === 'account_banned') {
            throw new Error(
              data.ban_reason
                ? `Аккаунт заблокирован модерацией. Причина: ${data.ban_reason}`
                : 'Аккаунт заблокирован модерацией. Обратитесь в поддержку через бота.'
            );
          }
          throw new Error(data?.error || 'Ошибка авторизации');
        }
      console.log('Авторизация успешна:', data);
      localStorage.setItem('token', data.token);
        if (cancelled) return;
      setToken(data.token);

        // Имя из БД может быть null/пустое — не затираем имя из Telegram WebApp
        setUser({
          ...data.user,
          first_name: data.user.first_name || telegramUser.first_name,
          last_name: data.user.last_name || telegramUser.last_name
        });
      
      if (data.user.role === 'pending') {
        setShowRoleSelection(true);
      } else {
        setRole(data.user.role);
      }
      setLoading(false);
      } catch (err) {
      console.error('Ошибка авторизации:', err);
        if (!cancelled) {
          let msg =
            err instanceof Error
              ? err.message
              : 'Ошибка авторизации. Проверьте интернет и откройте Mini App из бота.';
          if (err instanceof Error && err.name === 'AbortError') {
            msg =
              'Сервер не ответил вовремя. Проверьте интернет, VPN и откройте биржу кнопкой «Открыть биржу» в боте.';
          }
          setAuthError(msg);
      setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const refreshUserProfile = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(apiUrl('/api/auth/profile'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success && d.user) {
        setUser((prev: any) => ({
          ...prev,
          ...d.user,
          first_name: d.user.first_name || prev?.first_name,
          last_name: d.user.last_name || prev?.last_name,
          executor_top_until: d.user.executor_top_until ?? prev?.executor_top_until,
          top_active: d.user.top_active ?? prev?.top_active
        }));
      }
    } catch (e) {
      console.error('refreshUserProfile', e);
    }
  }, [token]);

  const fetchExecutorResponseQuota = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(apiUrl('/api/responses/quota'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) {
        setExecutorResponseQuota({
          free_executor_fee_waivers_left: d.free_executor_fee_waivers_left,
          platform_commission_percent: d.platform_commission_percent,
          daily_response_limit: d.daily_response_limit,
          daily_limit_applies: d.daily_limit_applies,
          responses_today: d.responses_today,
          remaining_responses_today: d.remaining_responses_today,
          executor_vip_active: Boolean(d.executor_vip_active),
          executor_vip_max_responses_per_24h: d.executor_vip_max_responses_per_24h
        });
      }
    } catch (e) {
      console.error('fetchExecutorResponseQuota', e);
    }
  }, [token]);

  useEffect(() => {
    if (!token || role !== 'executor') {
      setExecutorResponseQuota(null);
      return;
    }
    void fetchExecutorResponseQuota();
  }, [token, role, respondingTo?.id, fetchExecutorResponseQuota]);

  const refreshHomePreview = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(apiUrl('/api/orders/home-preview'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pr = await r.json();
      if (pr?.success) setHomePreviewOrders(pr.orders || []);
    } catch {
      /* ignore */
    }
    try {
      const rNew = await fetch(apiUrl('/api/orders/open?limit=24&created_within_days=3'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const n = await rNew.json();
      if (n?.success && Array.isArray(n.orders)) setHomeNewOrders(n.orders);
      else setHomeNewOrders([]);
    } catch {
      setHomeNewOrders([]);
    }
    try {
      const rAll = await fetch(apiUrl('/api/orders/open?limit=24'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const a = await rAll.json();
      if (a?.success && Array.isArray(a.orders)) setHomeAllFeedOrders(a.orders);
      else setHomeAllFeedOrders([]);
    } catch {
      setHomeAllFeedOrders([]);
    }
    try {
      const r2 = await fetch(apiUrl('/api/orders/open?limit=15&only_urgent=1'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const u = await r2.json();
      if (u?.success && Array.isArray(u.orders)) setHomeUrgentOrders(u.orders);
      else setHomeUrgentOrders([]);
    } catch {
      setHomeUrgentOrders([]);
    }
    try {
      const r3 = await fetch(apiUrl('/api/orders/open?limit=15&only_top=1'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const t = await r3.json();
      if (t?.success && Array.isArray(t.orders)) setHomeTopOrders(t.orders);
      else setHomeTopOrders([]);
    } catch {
      setHomeTopOrders([]);
    }
  }, [token]);

  const refreshHomeFavorites = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(apiUrl('/api/favorites'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) {
        setHomeFavoriteUsers(d.favorite_users || []);
        setHomeFavoriteOrders(d.favorite_orders || []);
      }
    } catch {
      /* ignore */
    }
  }, [token]);

  /** Превью активных заказов на главной (тот же запрос, что подгружает карусель и расчёт колокольчика) */
  useEffect(() => {
    if (!token || loading) return;
    if (effectiveHomeRole !== 'customer' && effectiveHomeRole !== 'executor') return;
    void refreshHomePreview();
  }, [token, loading, effectiveHomeRole, refreshHomePreview]);

  /** Промо/реклама на главной: активные слайды из админки; пусто → дефолты */
  useEffect(() => {
    let cancelled = false;
    const roleParam =
      effectiveHomeRole === 'customer' || effectiveHomeRole === 'executor'
        ? `?role=${effectiveHomeRole}`
        : '';
    (async () => {
      try {
        const r = await fetch(apiUrl(`/api/public/home-promo${roleParam}`));
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (d?.use_defaults || !Array.isArray(d?.slides) || d.slides.length === 0) {
          setHomePromoSlides(null);
          return;
        }
        setHomePromoSlides(
          d.slides.map((s: any) => {
            const rawImg = s.imageUrl || s.image_url || null;
            const imageUrl =
              rawImg == null
                ? null
                : /^https?:\/\//i.test(String(rawImg))
                  ? String(rawImg)
                  : `${getApiBaseUrl()}${String(rawImg).startsWith('/') ? rawImg : `/${rawImg}`}`;
            const dbIdRaw = s.dbId ?? s.db_id;
            let dbId =
              dbIdRaw != null && Number.isFinite(Number(dbIdRaw)) ? Number(dbIdRaw) : undefined;
            if (dbId == null && typeof s.id === 'string') {
              const m = /^promo-(\d+)$/.exec(s.id);
              if (m) dbId = Number(m[1]);
            }
            if (dbId == null && typeof s.id === 'number') dbId = s.id;
            return {
              id: String(s.id || `promo-${dbId || Math.random()}`),
              dbId,
              title: String(s.title || ''),
              subtitle: s.subtitle ? String(s.subtitle) : undefined,
              tone: s.tone || 'blue',
              imageUrl,
              showTelegramMark: Boolean(s.showTelegramMark ?? s.show_telegram_mark),
              actions: Array.isArray(s.actions) ? s.actions : undefined,
              ctaLabel: s.ctaLabel,
              cta: s.cta
            };
          })
        );
      } catch {
        if (!cancelled) setHomePromoSlides(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveHomeRole]);

  useEffect(() => {
    if (!token || loading) return;
    if (effectiveHomeRole !== 'customer' && effectiveHomeRole !== 'executor') return;
    let cancelled = false;
    (async () => {
      try {
        const topUrl =
          effectiveHomeRole === 'customer'
            ? apiUrl('/api/users/home/directory?segment=top&limit=25')
            : '';
        const vipExecUrl =
          effectiveHomeRole === 'customer'
            ? apiUrl('/api/users/home/directory?segment=executor_vip&limit=25')
            : '';
        // VIP с короной (is_vip) — блок внизу только у исполнителя; у заказчика — executor_vip сверху
        const vipUrl =
          effectiveHomeRole === 'executor'
            ? apiUrl('/api/users/home/directory?segment=vip')
            : '';
        const [dr, dv, dt, dvx] = await Promise.all([
          fetch(apiUrl('/api/users/home/directory?segment=recommended'), {
            headers: { Authorization: `Bearer ${token}` }
          }).then((r) => r.json()),
          vipUrl
            ? fetch(vipUrl, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json())
            : Promise.resolve({ success: true, users: [] }),
          topUrl
            ? fetch(topUrl, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json())
            : Promise.resolve({ success: true, users: [] }),
          vipExecUrl
            ? fetch(vipExecUrl, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json())
            : Promise.resolve({ success: true, users: [] })
        ]);
        if (cancelled) return;
        if (dr?.success) setHomeRecommendUsers(dr.users || []);
        if (dv?.success) setHomeVipUsers(dv.users || []);
        else if (effectiveHomeRole !== 'executor') setHomeVipUsers([]);
        if (dt?.success) setHomeTopExecutors(dt.users || []);
        else if (effectiveHomeRole !== 'customer') setHomeTopExecutors([]);
        if (dvx?.success) setHomeExecutorVipUsers(dvx.users || []);
        else if (effectiveHomeRole !== 'customer') setHomeExecutorVipUsers([]);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, loading, effectiveHomeRole]);

  useEffect(() => {
    if (!topDirectoryModalOpen || !token) return;
    let cancelled = false;
    setTopDirectoryLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ segment: 'top', limit: '50' });
        if (topDirectoryCategory) params.set('category', topDirectoryCategory);
        const r = await fetch(apiUrl(`/api/users/home/directory?${params}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        if (!cancelled && d?.success) setTopDirectoryUsers(d.users || []);
      } catch {
        if (!cancelled) setTopDirectoryUsers([]);
      } finally {
        if (!cancelled) setTopDirectoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [topDirectoryModalOpen, topDirectoryCategory, token]);

  useEffect(() => {
    if (!execVipDirectoryModalOpen || !token) return;
    let cancelled = false;
    setExecVipDirectoryLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ segment: 'executor_vip', limit: '50' });
        if (execVipDirectoryCategory) params.set('category', execVipDirectoryCategory);
        const r = await fetch(apiUrl(`/api/users/home/directory?${params}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        if (!cancelled && d?.success) setExecVipDirectoryUsers(d.users || []);
      } catch {
        if (!cancelled) setExecVipDirectoryUsers([]);
      } finally {
        if (!cancelled) setExecVipDirectoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [execVipDirectoryModalOpen, execVipDirectoryCategory, token]);

  useEffect(() => {
    if (!token || loading) return;
    if (effectiveHomeRole !== 'customer' && effectiveHomeRole !== 'executor') return;
    void refreshHomeFavorites();
  }, [token, loading, effectiveHomeRole, refreshHomeFavorites]);

  useEffect(() => {
    if (!showOrderDetails || !viewingOrder?.id || !token || role !== 'executor') {
      setOrderDetailsOrderFavorite(null);
      return;
    }
    if (viewingOrder.status !== 'open') {
      setOrderDetailsOrderFavorite(null);
      return;
    }
    let cancelled = false;
    setOrderDetailsOrderFavorite(null);
    void (async () => {
      try {
        const res = await fetch(
          apiUrl(
            `/api/favorites/status?entity_type=order&entity_id=${encodeURIComponent(String(viewingOrder.id))}`
          ),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const d = await res.json();
        if (cancelled) return;
        if (d.success) setOrderDetailsOrderFavorite(!!d.is_favorite);
        else setOrderDetailsOrderFavorite(false);
      } catch {
        if (!cancelled) setOrderDetailsOrderFavorite(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showOrderDetails, viewingOrder?.id, viewingOrder?.status, role, token]);

  useEffect(() => {
    const id = window.setInterval(() => setDeadlineTick((t) => t + 1), 60000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (token && !loading) void refreshWalletBalance();
  }, [token, loading, refreshWalletBalance]);

  const handleRoleSelect = async (selectedRole: string) => {
    console.log('🎯 Выбрана роль:', selectedRole);
    console.log('🔑 Токен:', token);
    
    if (!token) {
      console.error('❌ Нет токена!');
      showAlert('Ошибка авторизации. Перезагрузите страницу.');
      return;
    }

    try {
      const response = await fetch(apiUrl('/api/auth/choose-role'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: selectedRole })
      });
      
      const data = await response.json();
      console.log('📥 Ответ от сервера:', data);
      
      if (data.success) {
        setRole(selectedRole);
        setShowRoleSelection(false);
        if (data.user) {
          setUser((prev: any) => ({
            ...prev,
            ...data.user,
            first_name: data.user.first_name || prev?.first_name,
            last_name: data.user.last_name || prev?.last_name
          }));
        }
      } else {
        showAlert('Ошибка: ' + (data.error || 'Неизвестная ошибка'));
      }
    } catch (error) {
      console.error('❌ Ошибка выбора роли:', error);
      showAlert('Ошибка соединения с сервером');
    }
  };

  const handlePurchaseUrgentPack = async (pack: '1' | '3') => {
    if (!token) return;
    try {
      const status = await payDigitalGoodWithStars(token, pack === '3' ? 'urgent_3' : 'urgent_1');
      if (status === 'paid') {
        await new Promise((r) => window.setTimeout(r, 900));
        void refreshWalletBalance();
        void refreshUserProfile();
      } else if (status === 'cancelled') {
        /* пользователь закрыл счёт */
      } else {
        showAlert('Оплата Stars не завершена. Попробуйте ещё раз или /subscription в боте.');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка оплаты Stars');
    }
  };

  const handlePurchaseOrderChannelPost = async () => {
    if (!token) return;
    try {
      const status = await payDigitalGoodWithStars(token, 'order_channel_post');
      if (status === 'paid') {
        await new Promise((r) => window.setTimeout(r, 900));
        void refreshWalletBalance();
        void refreshUserProfile();
      } else if (status !== 'cancelled') {
        showAlert('Оплата Stars не завершена. Попробуйте ещё раз или /subscription в боте.');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка оплаты Stars');
    }
  };

  const handlePurchaseOrderTopPack = async (pack: '1' | '5' | '10') => {
    if (!token) return;
    try {
      const status = await payDigitalGoodWithStars(token, `order_top_${pack}`);
      if (status === 'paid') {
        await new Promise((r) => window.setTimeout(r, 900));
        void refreshWalletBalance();
        void refreshUserProfile();
      } else if (status !== 'cancelled') {
        showAlert('Оплата Stars не завершена. Попробуйте ещё раз или /subscription в боте.');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка оплаты Stars');
    }
  };

  const handleExtendOrderTop = async (orderId: number) => {
    if (!token) return;
    const h = user?.order_top_hours ?? 24;
    if (
      !await showConfirm(
        `Продлить ТОП в каталоге на ${h} ч? Списывается 1 слот из пакета (осталось слотов: ${user?.order_top_slots_left ?? 0}).`
      )
    ) {
      return;
    }
    try {
      const r = await fetch(apiUrl(`/api/orders/${orderId}/extend-order-top`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        showAlert(String(d.error || `Ошибка (${r.status})`));
        return;
      }
      setUser((prev: any) => ({
        ...prev,
        order_top_slots_left: d.order_top_slots_left
      }));
      if (viewingOrder && Number(viewingOrder.id) === orderId && d.order) {
        setViewingOrder((prev: any) => ({ ...prev, ...d.order }));
      }
      void refreshUserProfile();
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка продления ТОП');
    }
  };

  /** Посты задания в канал: квота VIP (только пока VIP активен) + купленные «Пост задания в канал». */
  const orderChannelPostsAvailable =
    (user?.vip_active ? Number(user?.customer_channel_posts_remaining ?? 0) : 0) +
    Number(user?.customer_channel_post_credits ?? 0);

  const handlePublishOrderToChannel = async (orderId: number) => {
    if (!token) return;
    const vipLeft = user?.vip_active ? Number(user?.customer_channel_posts_remaining ?? 0) : 0;
    const bought = Number(user?.customer_channel_post_credits ?? 0);
    const source =
      vipLeft > 0 ? `1 пост VIP (осталось: ${vipLeft})` : `1 купленный пост (осталось: ${bought})`;
    if (!await showConfirm(`Опубликовать задание в канал биржи? Спишется ${source}.`)) {
      return;
    }
    setOrderChannelBusy(true);
    try {
      const r = await fetch(apiUrl(`/api/orders/${orderId}/publish-channel`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        showAlert(String(d.error || `Ошибка (${r.status})`));
        return;
      }
      setUser((prev: any) => ({
        ...prev,
        customer_channel_posts_remaining: d.customer_channel_posts_remaining,
        customer_channel_post_credits: d.customer_channel_post_credits
      }));
      if (viewingOrder && Number(viewingOrder.id) === orderId && d.order) {
        setViewingOrder((prev: any) => ({ ...prev, ...d.order }));
      }
      void refreshUserProfile();
      showAlert('Задание отправлено в канал.');
    } catch {
      showAlert('Ошибка сети');
    } finally {
      setOrderChannelBusy(false);
    }
  };

  const handlePurchaseCustomerVip = async () => {
    if (!token) return;
    const stars = user?.customer_vip_month_stars ?? 750;
    const days = user?.customer_vip_month_days ?? 30;
    const perD = user?.customer_vip_daily_orders_limit ?? 10;
    const top = user?.customer_vip_order_top_slots ?? 10;
    const urgent = user?.customer_vip_urgent_slots ?? 3;
    const chN = user?.customer_vip_channel_posts_per_period ?? 3;
    if (
      !await showConfirm(
        `Оформить VIP заказчика за ${stars} ⭐ на ${days} дн.? До ${perD} заданий в сутки, ` +
          `+${top} слотов «ТОП задания», +${urgent} «Срочно» и ${chN} поста задания в канал.`
      )
    ) {
      return;
    }
    try {
      const status = await payDigitalGoodWithStars(token, 'customer_vip');
      if (status === 'paid') {
        await new Promise((r) => window.setTimeout(r, 900));
        void refreshWalletBalance();
        void refreshUserProfile();
        showAlert('VIP заказчика оформлен.');
      } else if (status !== 'cancelled') {
        showAlert('Оплата Stars не завершена.');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка оплаты Stars');
    }
  };

  const handleCreateOrder = async () => {
    const effectiveCategory =
      categorySelect === 'custom' || categorySelect === 'other'
        ? customCategory.trim()
          ? `customMain:${customCategory.trim()}`
          : ''
        : buildOrderCategoryToken(categorySelect.trim(), subcategorySelect.trim() || undefined);

    if (!title || !effectiveCategory) {
      showAlert('Заполните название и категорию');
      return;
    }

    if (categorySelect && categorySelect !== 'custom' && categorySelect !== 'other') {
      const subs = subcategoryEntriesForMain(categorySelect).filter((s) => s.key !== 'custom');
      if (subs.length > 0 && !subcategorySelect.trim()) {
        showAlert('Выберите подкатегорию');
        return;
      }
    }

    const rawB = budget != null && String(budget).trim() !== '' ? String(budget).trim() : '';
    const budgetMicro = parseUsdtInputToMicro(rawB);
    if (budgetMicro == null || budgetMicro < minOrderBudgetMicro) {
      showAlert(
        `Укажите бюджет в USDT не менее ${formatMicroUsdt(minOrderBudgetMicro)} (например 10 или 25.5)`
      );
      return;
    }

    try {
      const response = await fetch(apiUrl('/api/orders'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title,
          description,
          category: effectiveCategory,
          budget: rawB,
          is_urgent: orderUrgentBoost,
          is_order_top: orderTopBoost,
          execution_deadline_value: hasExecutionDeadline && executionDeadlineValue.trim()
            ? Number(executionDeadlineValue.trim())
            : null,
          execution_deadline_unit: hasExecutionDeadline && executionDeadlineValue.trim()
            ? executionDeadlineUnit
            : null,
          responses_limit: responsesLimit ? Number(responsesLimit) : null
        })
      });

      const raw = await response.text();
      let data: {
        success?: boolean;
        error?: string;
        detail?: string;
        order?: { id?: number };
        moderation_blocked?: boolean;
        banned?: boolean;
        escrow_penalty_applied?: boolean;
        penalty_percent?: number;
        refund_usdt_micro?: number;
      } = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        showAlert(
          'Сервер вернул не JSON (проверьте API / туннель). Ответ: ' + raw.slice(0, 120)
        );
        return;
      }

      if (data.success) {
        showAlert('Задание создано!');
        await refreshUserProfile();
        void refreshHomePreview();
        setShowCreateOrder(false);
        setTitle('');
        setDescription('');
        setCategorySelect('');
        setSubcategorySelect('');
        setCustomCategory('');
        setBudget('');
        setResponsesLimit('');
        setResponsesLimit('');
        setHasExecutionDeadline(false);
        setExecutionDeadlineValue('');
        setExecutionDeadlineUnit('days');
        setOrderUrgentBoost(false);
        setOrderTopBoost(false);
      } else {
        let msg =
          data.error ||
          data.detail ||
          `Не удалось создать задание (код ${response.status})`;
        if (data.moderation_blocked) {
          msg += '\n\nАккаунт заблокирован.';
          if (data.escrow_penalty_applied && data.refund_usdt_micro != null) {
            const pp = data.penalty_percent ?? 10;
            msg += `\nНа баланс возвращено ${formatMicroUsdt(data.refund_usdt_micro)} USDT (удержано ${pp}% штрафа платформой).`;
          }
          void refreshUserProfile();
          void refreshWalletBalance();
        }
        showAlert(msg);
      }
    } catch (error) {
      console.error('Ошибка создания задания:', error);
      showAlert('Ошибка сети. Проверьте соединение и что бэкенд запущен.');
    }
  };

  const loadMyOrders = async (beforeShow?: () => void) => {
    try {
      const response = await fetch(apiUrl('/api/orders/my'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const orders = Array.isArray(data.orders) ? data.orders : [];
        setMyOrders(
          orders.filter((o: any) => o.status !== 'finished' && o.status !== 'cancelled')
        );
        setMyArchivedCustomerOrders(
          orders.filter((o: any) => o.status === 'finished' || o.status === 'cancelled')
        );
        beforeShow?.();
        setShowMyOrders((wasOpen) => {
          if (!wasOpen) setMyOrdersListMode('in_work');
          return true;
        });
        setShowMyArchivedCustomerOrders(false);
      }
    } catch (error) {
      console.error('Ошибка загрузки заданий:', error);
    }
  };

  // Загрузить архив заказчика (завершённые задания)
  const loadMyArchivedCustomerOrders = async () => {
    try {
      const response = await fetch(apiUrl('/api/orders/my'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const orders = Array.isArray(data.orders) ? data.orders : [];
        setMyOrders(
          orders.filter((o: any) => o.status !== 'finished' && o.status !== 'cancelled')
        );
        setMyArchivedCustomerOrders(
          orders.filter((o: any) => o.status === 'finished' || o.status === 'cancelled')
        );
        setMyOrdersListMode('archive');
        setShowMyArchivedCustomerOrders(false);
        setShowMyOrders(true);
      }
    } catch (error) {
      console.error('Ошибка загрузки архива:', error);
    }
  };

  const fetchCatalogPage = useCallback(
    async (page: number) => {
      if (!token) return;
      setCatalogLoading(true);
      try {
        const { category, q } = catalogFiltersRef.current;
        const params = new URLSearchParams();
        params.set('page', String(Math.max(1, page)));
        params.set('limit', '30');
        if (catalogFiltersRef.current.onlyTop) params.set('only_top', '1');
        else if (catalogFiltersRef.current.onlyUrgent) params.set('only_urgent', '1');
        if (category.trim()) params.set('category', category.trim());
        if (q.trim()) params.set('q', q.trim());

        const [response, favResponse] = await Promise.all([
          fetch(apiUrl(`/api/orders/open?${params.toString()}`), {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(apiUrl('/api/favorites'), {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
      const data = await response.json();
        let favData: { success?: boolean; favorite_orders?: any[] } = {};
        try {
          favData = await favResponse.json();
        } catch {
          favData = {};
        }
      if (data.success) {
          const map: Record<number, boolean> = {};
          if (favData.success && Array.isArray(favData.favorite_orders)) {
            for (const o of favData.favorite_orders) {
              const oid = Number(o.id);
              if (Number.isFinite(oid)) map[oid] = true;
            }
          }
          setCatalogFavoriteOrderIds(map);
          setOpenOrders(data.orders || []);
          setCatalogPage(Number(data.page) || page);
          setCatalogTotalPages(Math.max(1, Number(data.total_pages) || 1));
          setCatalogTotalCount(Number(data.total) || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки каталога:', error);
    } finally {
      setCatalogLoading(false);
    }
    },
    [token]
  );

  const openCatalog = useCallback(async (beforeShow?: () => void) => {
    catalogSearchSkipDebounceRef.current = true;
    setCatalogOnlyUrgent(false);
    setCatalogOnlyTop(false);
    catalogFiltersRef.current = {
      category: catalogCategory,
      q: catalogQuery,
      onlyUrgent: false,
      onlyTop: false
    };
    await fetchCatalogPage(1);
    beforeShow?.();
    setShowCatalog(true);
  }, [fetchCatalogPage, catalogCategory, catalogQuery]);

  const openCatalogUrgentOnly = useCallback(async () => {
    catalogSearchSkipDebounceRef.current = true;
    setCatalogOnlyUrgent(true);
    setCatalogOnlyTop(false);
    catalogFiltersRef.current = {
      category: catalogCategory,
      q: catalogQuery,
      onlyUrgent: true,
      onlyTop: false
    };
    await fetchCatalogPage(1);
    setShowCatalog(true);
  }, [fetchCatalogPage, catalogCategory, catalogQuery]);

  const openCatalogTopOnly = useCallback(async () => {
    catalogSearchSkipDebounceRef.current = true;
    setCatalogOnlyUrgent(false);
    setCatalogOnlyTop(true);
    catalogFiltersRef.current = {
      category: catalogCategory,
      q: catalogQuery,
      onlyUrgent: false,
      onlyTop: true
    };
    await fetchCatalogPage(1);
    setShowCatalog(true);
  }, [fetchCatalogPage, catalogCategory, catalogQuery]);

  const fetchLeaderboard = useCallback(async () => {
    if (!token) return;
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    try {
      const isReferrals = leaderboardTabRole === 'referrals';
      const params = new URLSearchParams({
        role: leaderboardTabRole,
        period: isReferrals ? 'all' : leaderboardPeriod
      });
      if (!isReferrals && leaderboardCategory.trim()) {
        params.set('category', leaderboardCategory.trim());
      }
      const res = await fetchWithTimeout(
        apiUrl(`/api/leaderboard?${params}`),
        { headers: { Authorization: `Bearer ${token}` } },
        20000
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLeaderboardEntries([]);
        setLeaderboardViewer(null);
        setLeaderboardPrizeHint(null);
        const errStr =
          typeof data.error === 'string'
            ? data.error
            : typeof data.message === 'string'
              ? data.message
              : null;
        setLeaderboardError(
          errStr ||
            (res.status === 404
              ? 'Таблица лидеров недоступна (404). Обновите и перезапустите бэкенд с маршрутом /api/leaderboard.'
              : `Не удалось загрузить таблицу лидеров (HTTP ${res.status}).`)
        );
        return;
      }
      setLeaderboardEntries(Array.isArray(data.entries) ? data.entries : []);
      setLeaderboardPrizeHint(
        isReferrals && data.prize?.description ? String(data.prize.description) : null
      );
      const v = data.viewer;
      if (v && (typeof v.rank === 'number' || isReferrals)) {
        if (isReferrals) {
          setLeaderboardViewer({
            rank: typeof v.rank === 'number' ? v.rank : null,
            qualified_count: Number(v.qualified_count) || 0,
            referral_level: Number(v.referral_level) || 0,
            next_level: v.next_level != null ? Number(v.next_level) : null,
            remaining: Number(v.remaining) || 0,
            slots_on_reach: v.slots_on_reach != null ? Number(v.slots_on_reach) : null,
            xp: Math.round(Number(v.xp) || 0)
          });
        } else if (typeof v.rank === 'number') {
          setLeaderboardViewer({
            rank: v.rank,
            finished_count: Number(v.finished_count) || 0,
            xp: Math.round(Number(v.xp) || 0),
            volume_usdt_micro: String(v.volume_usdt_micro ?? '0')
          });
        } else {
          setLeaderboardViewer(null);
        }
      } else {
        setLeaderboardViewer(null);
      }
    } catch {
      setLeaderboardEntries([]);
      setLeaderboardError('Ошибка сети');
      setLeaderboardPrizeHint(null);
    } finally {
      setLeaderboardLoading(false);
    }
  }, [token, leaderboardTabRole, leaderboardPeriod, leaderboardCategory]);

  useEffect(() => {
    if (!showLeaderboard || !token) return;
    void fetchLeaderboard();
  }, [showLeaderboard, token, fetchLeaderboard]);

  useEffect(() => {
    if (!showCatalog || !token) return;
    if (catalogSearchSkipDebounceRef.current) {
      catalogSearchSkipDebounceRef.current = false;
      return;
    }
    const id = window.setTimeout(() => {
      void fetchCatalogPage(1);
    }, 400);
    return () => window.clearTimeout(id);
  }, [catalogQuery, catalogOnlyUrgent, catalogOnlyTop, showCatalog, token, fetchCatalogPage]);

  const toggleCatalogOrderFavorite = async (orderId: number) => {
    if (!token) return;
    const id = Number(orderId);
    if (!Number.isFinite(id)) return;
    const isOn = !!catalogFavoriteOrderIds[id];
    setCatalogFavoriteBusyId(id);
    try {
      const res = await fetch(apiUrl('/api/favorites'), {
        method: isOn ? 'DELETE' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entity_type: 'order', entity_id: id })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        showAlert(typeof d.error === 'string' ? d.error : 'Не удалось обновить избранное');
        return;
      }
      setCatalogFavoriteOrderIds((prev) => {
        const next = { ...prev };
        if (isOn) delete next[id];
        else next[id] = true;
        return next;
      });
      void refreshHomeFavorites();
    } catch {
      showAlert('Ошибка сети');
    } finally {
      setCatalogFavoriteBusyId(null);
    }
  };

  const toggleOrderFavoriteFromDetails = async () => {
    if (!token || !viewingOrder?.id || orderDetailsOrderFavorite === null || orderDetailsFavoriteBusy) return;
    if (role !== 'executor' || viewingOrder.status !== 'open') return;
    const id = Number(viewingOrder.id);
    if (!Number.isFinite(id)) return;
    const isOn = orderDetailsOrderFavorite === true;
    setOrderDetailsFavoriteBusy(true);
    try {
      const res = await fetch(apiUrl('/api/favorites'), {
        method: isOn ? 'DELETE' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entity_type: 'order', entity_id: id })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        showAlert(typeof d.error === 'string' ? d.error : 'Не удалось обновить избранное');
        return;
      }
      setOrderDetailsOrderFavorite(!isOn);
      setCatalogFavoriteOrderIds((prev) => {
        const next = { ...prev };
        if (isOn) delete next[id];
        else next[id] = true;
        return next;
      });
      void refreshHomeFavorites();
    } catch {
      showAlert('Ошибка сети');
    } finally {
      setOrderDetailsFavoriteBusy(false);
    }
  };

  // Загрузить активные задания исполнителя (в работе)
  const loadMyActiveOrders = async () => {
    try {
      const response = await fetch(apiUrl('/api/responses/my'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        // В работе + сдано + ждёт оплаты (промо без эскроу)
        const activeResponses = data.responses.filter((r: any) => 
          ['in_progress', 'completed', 'awaiting_payment'].includes(r.order_status)
        );
        
        const activeOrders = [];
        for (const resp of activeResponses) {
          const orderResponse = await fetch(apiUrl(`/api/orders/with-users/${resp.order_id}`), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const orderData = await orderResponse.json();
          if (orderData.success) {
            activeOrders.push(orderData.order);
          }
        }
        
        setMyActiveOrders(activeOrders);
        setShowMyActiveOrders(true);
        void refreshHomePreview();
      }
    } catch (error) {
      console.error('Ошибка загрузки активных заданий:', error);
    }
  };

  // Загрузить архив заданий исполнителя (завершённые)
  const loadMyArchivedOrders = async () => {
    try {
      const response = await fetch(apiUrl('/api/responses/my'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        // Только задания со статусом finished
        const archivedResponses = data.responses.filter((r: any) => 
          r.order_status === 'finished'
        );
        
        const archivedOrders = [];
        for (const resp of archivedResponses) {
          const orderResponse = await fetch(apiUrl(`/api/orders/with-users/${resp.order_id}`), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const orderData = await orderResponse.json();
          if (orderData.success) {
            archivedOrders.push(orderData.order);
          }
        }
        
        setMyArchivedOrders(archivedOrders);
        setShowMyArchivedOrders(true);
      }
    } catch (error) {
      console.error('Ошибка загрузки архива:', error);
    }
  };

  // Загрузить задания в ожидании (для исполнителя)
  const loadPendingOrders = async () => {
    try {
      const response = await fetch(apiUrl('/api/responses/my'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        // Обычные отклики + выбран, но ждёт оплату эскроу
        const pendingResponses = data.responses.filter(
          (r: any) =>
            r.status === 'pending' ||
            (r.status === 'pending_payment' && String(r.order_status || '') === 'open')
        );
        
        const pending = [];
        for (const resp of pendingResponses) {
          const orderResponse = await fetch(apiUrl(`/api/orders/with-users/${resp.order_id}`), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const orderData = await orderResponse.json();
          if (orderData.success) {
            pending.push({
              ...orderData.order,
              my_response_status: resp.status
            });
          }
        }

        pending.sort((a: any, b: any) => {
          const ap = a.my_response_status === 'pending_payment' ? 0 : 1;
          const bp = b.my_response_status === 'pending_payment' ? 0 : 1;
          return ap - bp;
        });
        
        setPendingOrders(pending);
        setShowPendingOrders(true);
      }
    } catch (error) {
      console.error('Ошибка загрузки ожидающих заданий:', error);
    }
  };

  // Загрузить мои предложения (для заказчика)
  const loadMyOffers = async () => {
    try {
      const response = await fetch(apiUrl('/api/orders/my-offers'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) {
        showAlert(String(data.error || 'Не удалось загрузить предложения'));
        return;
      }
      setMyOffers(Array.isArray(data.offers) ? data.offers : []);
      setShowMyOffers(true);
    } catch (error) {
      console.error('Ошибка загрузки предложений:', error);
      showAlert('Ошибка сети');
    }
  };

  const openFindExecutorForOrder = async (order: any) => {
    setOfferOrder(order);
    setExecutorQuery('');
    const knownCats = new Set(CATEGORY_OPTIONS.map((c) => c.value));
    const orderCat = String(order?.category || '').trim();
    // Если категория кастомная — не фильтруем, показываем всех исполнителей
    setExecutorCategory(knownCats.has(orderCat) ? orderCat : '');
    setExecutorResults([]);
    setExecutorSearchLoading(true);
    setShowFindExecutor(true);
    // Подтянем список сразу (по категории, если она стандартная)
    try {
      const url = apiUrl(
        knownCats.has(orderCat)
          ? `/api/users/executors/search?category=${encodeURIComponent(orderCat)}`
          : `/api/users/executors/search`
      );
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setExecutorResults(data.executors || []);
    } catch (e) {
      console.error('openFindExecutorForOrder:', e);
    } finally {
      setExecutorSearchLoading(false);
    }
  };

  const searchExecutors = async (qRaw?: string, categoryRaw?: string) => {
    setExecutorSearchLoading(true);
    try {
      const qText = typeof qRaw === 'string' ? qRaw : executorQuery;
      const catText = typeof categoryRaw === 'string' ? categoryRaw : executorCategory;
      const qs = new URLSearchParams();
      if (qText.trim()) qs.set('q', qText.trim());
      if (catText.trim()) qs.set('category', catText.trim());
      const response = await fetch(apiUrl(`/api/users/executors/search?${qs.toString()}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const list = data.executors || [];
        setExecutorResults(list);
        const favMap: Record<number, boolean> = {};
        list.forEach((ex: any) => {
          favMap[Number(ex.id)] = !!ex.is_favorite;
        });
        setExecutorFavoriteIds(favMap);
      } else showAlert(data.error || 'Не удалось загрузить исполнителей');
    } catch (e) {
      console.error('searchExecutors:', e);
      showAlert('Ошибка сети');
    } finally {
      setExecutorSearchLoading(false);
    }
  };

  const toggleExecutorFavorite = async (executorId: number) => {
    if (executorFavoriteBusyId != null) return;
    const isOn = !!executorFavoriteIds[executorId];
    setExecutorFavoriteBusyId(executorId);
    try {
      const response = await fetch(apiUrl('/api/favorites'), {
        method: isOn ? 'DELETE' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entity_type: 'user', entity_id: executorId })
      });
      const d = await response.json().catch(() => ({}));
      if (!response.ok) {
        showAlert(typeof d.error === 'string' ? d.error : 'Не удалось обновить избранное');
        return;
      }
      setExecutorFavoriteIds((prev) => ({ ...prev, [executorId]: !isOn }));
    } catch (e) {
      console.error('toggleExecutorFavorite:', e);
      showAlert('Ошибка сети');
    } finally {
      setExecutorFavoriteBusyId(null);
    }
  };

  const sendOfferToExecutor = async (executorId: number) => {
    if (!offerOrder?.id) return;
    try {
      setOfferBusyId(executorId);
      const response = await fetch(apiUrl(`/api/orders/${offerOrder.id}/offers`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ executor_id: executorId })
      });
      const data = await response.json();
      if (data.success) {
        showAlert('Предложение отправлено. Исполнителю придёт уведомление в боте.');
      } else {
        showAlert(data.error || `Не удалось отправить (код ${response.status})`);
      }
    } catch (e) {
      console.error('sendOfferToExecutor:', e);
      showAlert('Ошибка сети');
    } finally {
      setOfferBusyId(null);
    }
  };

  const loadResponses = async (orderId: number) => {
    if (!token) return;
    try {
      const response = await fetch(apiUrl(`/api/responses/order/${orderId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!data.success) return;

      let order = myOrders.find((o) => o.id === orderId);
      if (!order) {
        const or = await fetch(apiUrl(`/api/orders/${orderId}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const od = await or.json();
        if (od.success) order = od.order;
      }
      setSelectedOrder(order || { id: orderId, title: `Заказ ${orderRefLabel(orderId)}` });
        setResponses(data.responses);
        setShowResponses(true);
    } catch (error) {
      console.error('Ошибка загрузки откликов:', error);
    }
  };

  const acceptResponse = async (
    responseId: number,
    override?: { deadlineValue?: string; deadlineUnit?: 'hours' | 'days' | 'weeks' | 'months' }
  ) => {
    try {
      const cur = responses.find((r: any) => r.id === responseId);
      const deadlineValue = (
        override?.deadlineValue ??
        acceptDeadlineValueByResp[responseId] ??
        (cur?.eta_value != null ? String(cur.eta_value) : '')
      ).trim();
      const deadlineUnit =
        override?.deadlineUnit ?? acceptDeadlineUnitByResp[responseId] ?? cur?.eta_unit ?? 'days';
      const response = await fetch(apiUrl(`/api/responses/${responseId}/accept`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deadline_value: deadlineValue ? Number(deadlineValue) : null,
          deadline_unit: deadlineValue ? deadlineUnit : null
        })
      });
      const data = await response.json();
      if (data.success) {
        const escrowMicro = Number(data.escrow_budget_micro || 0);
        const amt =
          Number.isFinite(escrowMicro) && escrowMicro > 0
            ? formatMicroUsdt(escrowMicro)
            : null;
        showAlert(
          (amt
            ? `Исполнитель выбран. Оплатите работу (${amt} USDT): сумма будет заморожена на эскроу-счёте. `
            : 'Исполнитель выбран. Оплатите работу: сумма будет заморожена на эскроу-счёте. ') +
            'После успешного выполнения и вашего подтверждения деньги перейдут исполнителю. До оплаты заказ остаётся открытым, чат уже доступен.'
        );
        setShowResponses(false);
        loadMyOrders();
        if (showOrderDetails && viewingOrder?.id) {
          setOrderDetailsCustomerTab('about');
          void viewOrderDetails({ id: viewingOrder.id });
        } else if (data.order_id) {
          void viewOrderDetails({ id: data.order_id });
        }
      } else {
        showAlert(data.error || 'Не удалось выбрать исполнителя');
      }
    } catch (error) {
      console.error('Ошибка при выборе исполнителя:', error);
    }
  };

  const rejectResponse = async (responseId: number, orderId: number) => {
    if (!await showConfirm('Отклонить этот отклик?')) return;
    try {
      const response = await fetch(apiUrl(`/api/responses/${responseId}/reject`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        if (showOrderDetails && viewingOrder?.id === orderId) {
          void viewOrderDetails({ id: orderId });
        } else {
          await loadResponses(orderId);
        }
      } else {
        showAlert(data.error || 'Не удалось отклонить');
      }
    } catch (error) {
      console.error('Ошибка отклонения отклика:', error);
      showAlert('Ошибка сети');
    }
  };

  const handleCreateResponse = async () => {
    if (!responseMessage) {
      showAlert('Напишите сообщение заказчику');
      return;
    }

    let proposedPayload: string | null = null;
    if (responseBudgetMode === 'custom') {
      const raw = responseBudget.trim();
      if (!raw) {
        showAlert('Укажите сумму в USDT или выберите «Согласен с бюджетом заказчика»');
        return;
      }
      const micro = parseUsdtInputToMicro(raw);
      if (micro == null || micro < minOrderBudgetMicro) {
        showAlert(
          `Сумма не менее ${formatMicroUsdt(minOrderBudgetMicro)} USDT (например 10 или 25.5)`
        );
        return;
      }
      proposedPayload = raw;
    }

    try {
      const response = await fetch(apiUrl('/api/responses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          order_id: respondingTo.id,
          message: responseMessage,
          proposed_budget: proposedPayload,
          eta_value: responseEtaValue.trim() ? Number(responseEtaValue.trim()) : null,
          eta_unit: responseEtaValue.trim() ? responseEtaUnit : null
        })
      });

      let data: any = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      if (!response.ok) {
        showAlert(data?.error || `Не удалось отправить отклик (HTTP ${response.status})`);
        return;
      }
      if (data.success) {
        showAlert('Отклик отправлен!');
        setRespondingTo(null);
        setResponseMessage('');
        setResponseBudget('');
        setResponseBudgetMode('client');
        setResponseEtaValue('');
        setResponseEtaUnit('days');
        void refreshUserProfile();
        void fetchExecutorResponseQuota();
      } else {
        showAlert((data as { error?: string }).error || 'Не удалось отправить отклик');
      }
    } catch (error) {
      console.error('Ошибка создания отклика:', error);
    }
  };

  // Исполнитель отмечает задание как выполненное
  const markAsCompleted = async (orderId: number) => {
    try {
      const response = await fetch(apiUrl(`/api/orders/status/${orderId}/complete`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let data: Record<string, unknown> = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      if (!response.ok) {
        showAlert((data.error as string) || 'Не удалось отметить выполнение');
        return;
      }
      if (data.success) {
        showAlert('Задание отмечено как выполненное! Ожидайте подтверждения заказчика');
        if (showMyActiveOrders) loadMyActiveOrders();
        if (showOrderDetails && viewingOrder?.id === orderId) {
          await viewOrderDetails({ id: orderId });
        } else if (showOrderDetails) {
          setShowOrderDetails(false);
        }
      }
    } catch (error) {
      console.error('Ошибка:', error);
      showAlert('Ошибка сети');
    }
  };

  // Заказчик подтверждает выполнение
  const confirmCompletion = async (orderId: number) => {
    try {
      const response = await fetch(apiUrl(`/api/orders/status/${orderId}/confirm`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const rawText = await response.text();
      let data: Record<string, unknown> = {};
      try {
        if (rawText) data = JSON.parse(rawText) as Record<string, unknown>;
      } catch {
        data = {
          error: 'Ответ сервера не JSON (часто nginx 502 или HTML)',
          detail: rawText.slice(0, 600)
        };
      }
      if (!response.ok) {
        const errText = (data.error as string) || `Ошибка ${response.status}`;
        const detail = (data.detail as string) || '';
        const parts = [errText];
        if (detail) parts.push(detail);
        else if (rawText.length && rawText.length < 1200) parts.push(rawText);
        else if (rawText.length) parts.push(rawText.slice(0, 600) + '…');
        parts.push(`HTTP ${response.status}`);
        showAlert(parts.join('\n\n'));
        return;
      }
      if (data.success) {
        if (data.awaiting_payment) {
          showAlert(
            (data.message as string) ||
              'Работа принята. Пополните баланс в боте и нажмите «Оплатить работу».'
          );
        } else {
        showAlert('Задание завершено! Средства переведены исполнителю');
        }
        if (showMyOrders) loadMyOrders();
        if (showMyActiveOrders) loadMyActiveOrders();
        if (showMyArchivedOrders) loadMyArchivedOrders();
        if (showMyArchivedCustomerOrders) loadMyArchivedCustomerOrders();
        if (showOrderDetails && viewingOrder && viewingOrder.id === orderId) {
          await viewOrderDetails({ id: orderId });
        } else if (showOrderDetails) {
          setShowOrderDetails(false);
        }
      } else {
        showAlert((data.error as string) || 'Не удалось подтвердить');
      }
    } catch (error) {
      console.error('Ошибка:', error);
      showAlert('Ошибка сети');
    }
  };

  /** Оплата эскроу после выбора исполнителя */
  const payEscrowForResponse = async (responseId: number, orderId: number) => {
    try {
      setPromoPayBusy(true);
      const response = await fetch(apiUrl(`/api/responses/${responseId}/pay-escrow`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        if (data.newBalance != null) {
          setWalletBalance(Number(data.newBalance));
        }
        const amt = data.escrow_budget_micro
          ? formatMicroUsdt(Number(data.escrow_budget_micro))
          : null;
        const workAmt =
          data.work_budget_usdt_micro != null
            ? formatMicroUsdt(Number(data.work_budget_usdt_micro))
            : null;
        const feeAmt =
          data.platform_commission_usdt_micro != null &&
          Number(data.platform_commission_usdt_micro) > 0
            ? formatMicroUsdt(Number(data.platform_commission_usdt_micro))
            : null;
        const feePct = data.platform_commission_percent;
        showAlert(
          data.commission_payer === 'customer' && workAmt && feeAmt
            ? `Оплата принята: работа ${workAmt} + комиссия ${feePct ?? 10}% (${feeAmt}) = ${amt} USDT на эскроу. Исполнителю отправлено уведомление — можно приступать.`
            : (amt
                ? `Оплата принята: ${amt} USDT заморожены на эскроу. `
                : 'Оплата принята. Средства на эскроу. ') +
                'Заказ в работе. После выполнения и вашего подтверждения деньги поступят исполнителю.'
        );
        await viewOrderDetails({ id: orderId });
        loadMyOrders();
        if (showPendingOrders) loadPendingOrders();
        if (showMyActiveOrders) loadMyActiveOrders();
        void refreshHomePreview();
      } else {
        showAlert((data.error as string) || 'Не удалось оплатить эскроу');
        if (data.code === 'insufficient_balance') {
          void refreshWalletBalance();
        }
      }
    } catch (e) {
      console.error(e);
      showAlert('Ошибка сети');
    } finally {
      setPromoPayBusy(false);
    }
  };

  /** Промо: оплата с баланса FreelanCYX (USDT) — legacy */
  const payPromoFromBalance = async (orderId: number) => {
    if (!viewingOrder || viewingOrder.id !== orderId) return;
    const budget = Math.round(Number(viewingOrder.budget));
    const amt = parseUsdtInputToMicro(String(promoPayAmount)) ?? -1;
    if (amt < 1) {
      showAlert('Укажите сумму в USDT (как в бюджете заказа)');
      return;
    }
    if (amt !== budget) {
      showAlert(`Сумма должна совпадать с бюджетом заказа: ${formatMicroUsdt(budget)} USDT`);
      return;
    }
    setPromoPayBusy(true);
    try {
      const response = await fetch(apiUrl(`/api/orders/status/${orderId}/pay-promo-balance`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: amt })
      });
      let data: Record<string, unknown> = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      if (!response.ok) {
        showAlert((data.error as string) || 'Не удалось оплатить');
        void refreshWalletBalance();
        return;
      }
      if (data.success) {
        const ex = data.executor_payout_usdt_micro as number | undefined;
        const com = data.commission_usdt_micro as number | undefined;
        let msg = (data.message as string) || 'Оплата прошла, сделка завершена';
        if (typeof ex === 'number' && typeof com === 'number') {
          msg += `\n\nС баланса списано: ${formatMicroUsdt(amt)} USDT по заказу.\nИсполнителю: ${formatMicroUsdt(ex)} USDT (комиссия: ${formatMicroUsdt(com)} USDT).`;
        }
        showAlert(msg);
        void refreshWalletBalance();
        if (showMyOrders) loadMyOrders();
        if (showMyActiveOrders) loadMyActiveOrders();
        if (showMyArchivedOrders) loadMyArchivedOrders();
        if (showMyArchivedCustomerOrders) loadMyArchivedCustomerOrders();
        if (showOrderDetails && viewingOrder && viewingOrder.id === orderId) {
          await viewOrderDetails({ id: orderId });
        }
      } else {
        showAlert((data.error as string) || 'Не удалось оплатить');
      }
    } catch (error) {
      console.error('Ошибка:', error);
      showAlert('Ошибка сети');
    } finally {
      setPromoPayBusy(false);
    }
  };


  // Исполнитель отказывается от задания (до эскроу — без штрафа; в работе — со штрафом)
  const cancelByExecutor = async (orderId: number) => {
    const isServiceDeal =
      viewingOrder &&
      Number(viewingOrder.id) === Number(orderId) &&
      Boolean(viewingOrder.executor_service_id);
    const awaitingEscrow =
      viewingOrder &&
      Number(viewingOrder.id) === Number(orderId) &&
      String(viewingOrder.status || '') === 'open' &&
      (String(viewingOrder.selected_response_status || '') === 'pending_payment' ||
        String(viewingOrder.selected_response_status || '') === 'negotiating');

    const ok = isServiceDeal
      ? await showConfirm(
          'Отказаться от сделки с этим заказчиком?\n\nЗаказ будет удалён. Эскроу ещё не оплачен — штраф не начисляется.'
        )
      : awaitingEscrow
        ? await showConfirm(
            'Отказаться от назначения?\n\nЭскроу ещё не оплачен — штраф не начисляется.'
          )
        : await showConfirm(
            '⚠️ Вы уверены, что хотите отказаться от задания?\n\nЭто снизит ваш XP на 30 и задание вернётся в каталог.'
          );
    if (!ok) return;
    
    try {
      const response = await fetch(apiUrl(`/api/orders/status/${orderId}/cancel-by-executor`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        showAlert(
          data.message ||
            (isServiceDeal
              ? 'Сделка удалена.'
              : awaitingEscrow
                ? 'Вы отказались от назначения.'
                : 'Вы отказались от задания.')
        );
        if (showMyActiveOrders) loadMyActiveOrders();
        if (showPendingOrders) loadPendingOrders();
        void refreshHomePreview();
        if (showOrderDetails) closeOrderDetails();
      } else {
        showAlert(data.error || 'Не удалось отказаться');
      }
    } catch (error) {
      console.error('Ошибка при отказе от задания:', error);
      showAlert('Ошибка сети');
    }
  };

  // Отмена задания (только для заказчика)
  const cancelOrder = async (orderId: number) => {
    if (!await showConfirm('Вы уверены, что хотите отменить задание?')) return;
    
    try {
      const response = await fetch(apiUrl(`/api/orders/status/${orderId}/cancel`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        showAlert('Задание отменено');
        if (showCatalog) void fetchCatalogPage(catalogPage);
        if (showMyOrders) loadMyOrders();
        if (showMyActiveOrders) loadMyActiveOrders();
        if (showMyArchivedOrders) loadMyArchivedOrders();
        if (showMyArchivedCustomerOrders) loadMyArchivedCustomerOrders();
        if (showOrderDetails) closeOrderDetails();
      }
    } catch (error) {
      console.error('Ошибка:', error);
    }
  };

  const submitOpenDispute = async () => {
    if (!viewingOrder || !token) return;
    const t = disputeText.trim();
    if (t.length < 10) {
      showAlert('Опишите спор не короче 10 символов.');
      return;
    }
    setDisputeBusy(true);
    try {
      const res = await fetch(apiUrl(`/api/orders/${viewingOrder.id}/dispute`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: t })
      });
      const data = await res.json();
      if (!res.ok) {
        showAlert(data.error || 'Не удалось отправить');
        return;
      }
      showAlert(data.message || 'Спор отправлен администрации.');
      setDisputeModalOpen(false);
      setDisputeText('');
    } catch (e) {
      console.error(e);
      showAlert('Ошибка сети');
    } finally {
      setDisputeBusy(false);
    }
  };

  const pushNav = useCallback((screen: AppNavScreen) => {
    navStackRef.current = [...navStackRef.current, screen];
  }, []);

  const applyNavScreen = useCallback((screen: AppNavScreen) => {
    if (screen.type === 'services') {
      setServicesScreen(screen.mode);
      setServicesCatalogPane(screen.pane || 'browse');
      return;
    }
    if (screen.type === 'profile') {
      setViewingProfile(screen.userId);
    }
  }, []);

  const popAndRestoreNav = useCallback(() => {
    const s = navStackRef.current;
    if (s.length === 0) return;
    const last = s[s.length - 1];
    navStackRef.current = s.slice(0, -1);
    applyNavScreen(last);
  }, [applyNavScreen]);

  const closeOrderDetails = useCallback(
    (opts?: { restore?: boolean }) => {
      const fromInbox = openedFromInbox;
      setShowOrderDetails(false);
      setOrderDetailsCustomerTab('about');
      setOrderReviewInfo(null);
      setDisputeModalOpen(false);
      setDisputeText('');
      setOpenedFromInbox(false);
      setPendingOpenOrderChat(false);
      setPendingChatPeerId(null);
      setPendingChatResponseId(null);
      setPendingOrderDetailsTab(null);
      inboxChatWasOpenRef.current = false;
      try {
        sessionStorage.removeItem(BOT_ORDER_CHAT_KEY);
      } catch {
        /* ignore */
      }
      if (fromInbox) {
        setShowMessagesInbox(true);
        if (token) {
          void fetchInboxUnreadTotal(token).then(setMessagesBadge);
        }
        return;
      }
      if (opts?.restore !== false) popAndRestoreNav();
    },
    [popAndRestoreNav, openedFromInbox, token]
  );

  const viewOrderDetails = async (order: any) => {
    try {
      setShowDeadlineReplaceModal(false);
      setShowExtendDeadlineModal(false);
      const response = await fetch(apiUrl(`/api/orders/with-users/${order.id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setViewingOrder(data.order);
        setShowOrderDetails(true);
        setOrderReviewInfo(null);
        setReviewStars(5);
        setReviewComment('');
        setDeadlineReplacePickId('');
        setDlReplaceValue('');
        // Отзыв подгружается в useEffect (ниже) — иначе при сбое API блок пропадал совсем
      }
    } catch (error) {
      console.error('Ошибка загрузки деталей заказа:', error);
    }
  };

  const submitDeadlineReplacement = async () => {
    if (!viewingOrder || !token) return;
    const others = deadlineReplaceInfo?.other_responses || [];
    if (others.length > 0 && (deadlineReplacePickId === '' || deadlineReplacePickId == null)) {
      showAlert('Выберите исполнителя из списка');
      return;
    }
    setDeadlineReplaceBusy(true);
    try {
      const body: Record<string, unknown> =
        others.length === 0 ? { response_id: null } : { response_id: Number(deadlineReplacePickId) };
      if (others.length > 0 && dlReplaceValue.trim()) {
        const v = parseInt(dlReplaceValue.trim(), 10);
        if (!Number.isFinite(v) || v < 1) {
          showAlert('Укажите срок: целое число ≥ 1 или оставьте поле пустым (возьмём срок из отклика)');
          setDeadlineReplaceBusy(false);
          return;
        }
        body.deadline_value = v;
        body.deadline_unit = dlReplaceUnit;
      }
      const res = await fetch(apiUrl(`/api/orders/${viewingOrder.id}/deadline-replacement`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) {
        showAlert(String(data.error || 'Не удалось выполнить'));
        return;
      }
      showAlert(String(data.message || 'Готово'));
      setShowDeadlineReplaceModal(false);
      await viewOrderDetails({ id: viewingOrder.id });
      void refreshHomePreview();
      void fetch(apiUrl('/api/orders/my'), { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => {
          if (d?.success && Array.isArray(d.orders)) setMyOrders(d.orders);
        })
        .catch(() => {});
    } catch (e) {
      console.error(e);
      showAlert('Ошибка сети');
    } finally {
      setDeadlineReplaceBusy(false);
    }
  };

  const submitExtendDeadline = async () => {
    if (!viewingOrder || !token) return;
    const v = parseInt(String(extendDlValue).trim(), 10);
    if (!Number.isFinite(v) || v < 1) {
      showAlert('Укажите срок: целое число ≥ 1');
      return;
    }
    setExtendDeadlineBusy(true);
    try {
      const res = await fetch(apiUrl(`/api/orders/${viewingOrder.id}/extend-execution-deadline`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ deadline_value: v, deadline_unit: extendDlUnit })
      });
      const data = await res.json();
      if (!res.ok) {
        showAlert(String(data.error || 'Не удалось продлить срок'));
        return;
      }
      showAlert(String(data.message || 'Срок продлён'));
      setShowExtendDeadlineModal(false);
      await viewOrderDetails({ id: viewingOrder.id });
      void refreshHomePreview();
      void fetch(apiUrl('/api/orders/my'), { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => {
          if (d?.success && Array.isArray(d.orders)) setMyOrders(d.orders);
        })
        .catch(() => {});
    } catch (e) {
      console.error(e);
      showAlert('Ошибка сети');
    } finally {
      setExtendDeadlineBusy(false);
    }
  };

  useEffect(() => {
    if (!showOrderDetails) {
      setOrderFilesCloudOpen(false);
      setOrderChatOpen(false);
      setPendingOpenOrderChat(false);
      setPendingChatPeerId(null);
      setPendingChatResponseId(null);
      setPendingOrderDetailsTab(null);
    }
  }, [showOrderDetails]);

  /** Из inbox: закрытие чата (крестик / Назад Telegram) → снова список сообщений */
  useEffect(() => {
    if (!openedFromInbox) {
      inboxChatWasOpenRef.current = false;
      return;
    }
    if (orderChatOpen) {
      inboxChatWasOpenRef.current = true;
      return;
    }
    if (inboxChatWasOpenRef.current && showOrderDetails) {
      inboxChatWasOpenRef.current = false;
      closeOrderDetails({ restore: false });
    }
  }, [orderChatOpen, openedFromInbox, showOrderDetails, closeOrderDetails]);

  useEffect(() => {
    const onCloudState = (e: Event) => {
      const open = Boolean((e as CustomEvent<{ open?: boolean }>).detail?.open);
      setOrderFilesCloudOpen(open);
    };
    const onChatState = (e: Event) => {
      const open = Boolean((e as CustomEvent<{ open?: boolean }>).detail?.open);
      setOrderChatOpen(open);
    };
    window.addEventListener('fcyx-files-cloud-state', onCloudState);
    window.addEventListener('fcyx-order-chat-state', onChatState);
    return () => {
      window.removeEventListener('fcyx-files-cloud-state', onCloudState);
      window.removeEventListener('fcyx-order-chat-state', onChatState);
    };
  }, []);

  useEffect(() => {
    if (!showOrderDetails) {
      setOrderDetailsResponses([]);
      setOrderDetailsResponsesLoading(false);
    }
  }, [showOrderDetails]);

  useEffect(() => {
    if (!showOrderDetails || viewingOrder?.id == null) return;
    if (pendingOrderDetailsTab) {
      setOrderDetailsCustomerTab(pendingOrderDetailsTab);
      setPendingOrderDetailsTab(null);
    } else {
      setOrderDetailsCustomerTab('about');
    }
    // pendingOrderDetailsTab читаем только при смене заказа
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrderDetails, viewingOrder?.id]);

  useEffect(() => {
    if (!showOrderDetails || !token || role !== 'customer') return;
    const oid = viewingOrder?.id;
    if (oid == null) return;
    if (viewingOrder.status !== 'open') {
      setOrderDetailsResponses([]);
      setOrderDetailsResponsesLoading(false);
      return;
    }
    const rc = Number(viewingOrder.responses_count || 0);
    if (rc <= 0) {
      setOrderDetailsResponses([]);
      setOrderDetailsResponsesLoading(false);
      return;
    }
    let cancelled = false;
    setOrderDetailsResponsesLoading(true);
    void (async () => {
      try {
        const response = await fetch(apiUrl(`/api/responses/order/${oid}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (cancelled) return;
        if (data.success) {
          const list = data.responses || [];
          setOrderDetailsResponses(list);
          setResponses(list);
          setSelectedOrder(viewingOrder);
        } else {
          setOrderDetailsResponses([]);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setOrderDetailsResponses([]);
        }
      } finally {
        if (!cancelled) setOrderDetailsResponsesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Только id/status/число откликов — не вся карточка, чтобы не дёргать API на каждое поле.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setSelectedOrder(viewingOrder): снимок при срабатывании эффекта
  }, [
    showOrderDetails,
    viewingOrder?.id,
    viewingOrder?.status,
    viewingOrder?.responses_count,
    role,
    token
  ]);

  /** Переход из уведомления бота: #responses/ID или #order/ID */
  useEffect(() => {
    if (!token || loading || deepLinkHandled.current) return;

    persistProfileStartParamFromTelegram();
    const startParam = getPendingProfileStartParam() || getTelegramStartParam();
    if (startParam) {
      const om = /^order_(\d+)$/.exec(String(startParam).trim());
      if (om) {
        const oid = parseInt(om[1], 10);
        if (!Number.isNaN(oid)) {
          void viewOrderDetails({ id: oid });
          deepLinkHandled.current = true;
          clearPendingProfileStartParam();
          stripProfileLaunchParamsFromLocation();
          return;
        }
      }
      const sm = /^service_(\d+)$/.exec(String(startParam).trim());
      if (sm) {
        const sid = parseInt(sm[1], 10);
        if (!Number.isNaN(sid)) {
          setServicesFocusId(sid);
          setServicesScreen('catalog');
          setServicesCatalogPane('browse');
          deepLinkHandled.current = true;
          clearPendingProfileStartParam();
          stripProfileLaunchParamsFromLocation();
          return;
        }
      }
      const pm = /^profile_(\d+)$/.exec(String(startParam).trim());
      if (pm) {
        const pid = parseInt(pm[1], 10);
        if (!Number.isNaN(pid)) {
          setViewingProfile(pid);
          deepLinkHandled.current = true;
          clearPendingProfileStartParam();
          stripProfileLaunchParamsFromLocation();
          return;
        }
      }
    }

    const raw = window.location.hash.replace(/^#/, '');
    if (!raw) return;

    const run = async () => {
      if (raw.startsWith('responses/')) {
        const id = parseInt(raw.split('/')[1], 10);
        if (!Number.isNaN(id)) {
          await loadResponses(id);
          deepLinkHandled.current = true;
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        return;
      }
      if (raw.startsWith('order/')) {
        const pathOnly = raw.split('?')[0].split('&')[0];
        if (/^order\/\d+\/chat(?:\/|$)/.test(pathOnly)) return;
        const id = parseInt(pathOnly.split('/')[1], 10);
        if (!Number.isNaN(id)) {
          await viewOrderDetails({ id });
          deepLinkHandled.current = true;
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        return;
      }
      if (raw.startsWith('service/')) {
        const id = parseInt(raw.split('/')[1], 10);
        if (!Number.isNaN(id)) {
          setServicesFocusId(id);
          setServicesScreen('catalog');
          setServicesCatalogPane('browse');
          deepLinkHandled.current = true;
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        return;
      }
      if (raw.startsWith('profile/')) {
        const id = parseInt(raw.split('/')[1], 10);
        if (!Number.isNaN(id)) {
          setViewingProfile(id);
          deepLinkHandled.current = true;
          stripProfileLaunchParamsFromLocation();
        }
        return;
      }
      if (raw === 'me/subscription' || raw === 'me/category-alerts') {
        const uid = user?.id;
        if (uid) {
          deepLinkHandled.current = true;
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          if (raw === 'me/subscription') {
            resetMainListScreens();
            setShowBoostShop(true);
          } else {
            setProfileFocusSection('category-alerts');
            setViewingProfile(uid);
          }
        }
      }
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deep link после логина (me/subscription ждёт user.id)
  }, [token, loading, user?.id]);

  /** ?focus=category-alerts|subscription из web_app-кнопки бота (hash часто теряется) */
  useEffect(() => {
    if (!token || loading || !user?.id) return;
    let focus: string | null = null;
    try {
      focus = new URLSearchParams(window.location.search).get('focus');
    } catch {
      focus = null;
    }
    if (focus !== 'category-alerts' && focus !== 'subscription') return;
    deepLinkHandled.current = true;
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('focus');
      window.history.replaceState(null, '', u.pathname + u.search + u.hash);
    } catch {
      /* ignore */
    }
    if (focus === 'subscription') {
      /* «Подписки» в боте → экран Буст (покупки Stars), а не пустой блок профиля заказчика */
      resetMainListScreens();
      setShowBoostShop(true);
      return;
    }
    setProfileFocusSection(focus);
    setViewingProfile(user.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deep link один раз после логина
  }, [token, loading, user?.id]);

  /** Кнопка «Открыть чат» в боте: и первый заход, и повторное нажатие, пока мини-апп ещё открыт. */
  useEffect(() => {
    if (!token || loading) return;

    const launchChat = (orderId: number, peer: number | null) => {
      setPendingOrderDetailsTab(null);
      setPendingChatPeerId(peer);
      setPendingChatResponseId(null);
      setPendingOpenOrderChat(true);
      setChatLaunchNonce((n) => n + 1);
      void viewOrderDetails({ id: orderId });
    };

    const applyLaunch = (allowSaved: boolean) => {
      const fromUrl = readBotOrderChatFromLocation();
      if (fromUrl) {
        try {
          sessionStorage.setItem(BOT_ORDER_CHAT_KEY, JSON.stringify(fromUrl));
        } catch {
          /* ignore */
        }
        try {
          const u = new URL(window.location.href);
          if (u.searchParams.get('focus') === 'order-chat') {
            u.searchParams.delete('focus');
            u.searchParams.delete('order');
            u.searchParams.delete('peer');
          }
          const hashBody = u.hash.replace(/^#/, '');
          if (/^order\/\d+\/chat(?:\/\d+)?/.test(hashBody.split('?')[0])) {
            u.hash = '';
          }
          window.history.replaceState(null, '', u.pathname + u.search + u.hash);
        } catch {
          /* ignore */
        }
        launchChat(fromUrl.orderId, fromUrl.peer);
        return;
      }
      if (!allowSaved) return;
      try {
        const saved = sessionStorage.getItem(BOT_ORDER_CHAT_KEY);
        if (!saved) return;
        const parsed = JSON.parse(saved) as { orderId?: number; peer?: number | null };
        const orderId = Number(parsed?.orderId);
        if (!Number.isFinite(orderId) || orderId <= 0) return;
        const peer = Number(parsed?.peer);
        launchChat(orderId, Number.isFinite(peer) && peer > 0 ? peer : null);
      } catch {
        /* ignore */
      }
    };

    applyLaunch(false);
    let armTimer = 0;
    let armed = false;
    armTimer = window.setTimeout(() => {
      armed = true;
    }, 800);

    const onVisible = () => {
      if (!armed || document.visibilityState !== 'visible') return;
      applyLaunch(true);
    };
    const onActivated = () => {
      if (!armed) return;
      applyLaunch(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('hashchange', onActivated);
    window.addEventListener('pageshow', onActivated);
    const tg = (window as unknown as { Telegram?: { WebApp?: { onEvent?: (e: string, cb: () => void) => void; offEvent?: (e: string, cb: () => void) => void } } })
      .Telegram?.WebApp;
    try {
      tg?.onEvent?.('activated', onActivated);
    } catch {
      /* ignore */
    }
    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('hashchange', onActivated);
      window.removeEventListener('pageshow', onActivated);
      try {
        tg?.offEvent?.('activated', onActivated);
      } catch {
        /* ignore */
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- повторный вход из кнопки бота
  }, [token, loading]);

  /** Telegram иногда заполняет startParam после первого кадра — добираем profile_* несколько секунд. */
  useEffect(() => {
    if (!token || loading || deepLinkHandled.current) return;
    let ticks = 0;
    const id = window.setInterval(() => {
      if (deepLinkHandled.current) {
        window.clearInterval(id);
        return;
      }
      if (++ticks > 40) {
        window.clearInterval(id);
        return;
      }
      persistProfileStartParamFromTelegram();
      const sp = getPendingProfileStartParam() || getTelegramStartParam();
      if (!sp) return;
      const om = /^order_(\d+)$/.exec(String(sp).trim());
      if (om) {
        const oid = parseInt(om[1], 10);
        if (Number.isNaN(oid)) return;
        void viewOrderDetails({ id: oid });
        deepLinkHandled.current = true;
        clearPendingProfileStartParam();
        stripProfileLaunchParamsFromLocation();
        window.clearInterval(id);
        return;
      }
      const pm = /^profile_(\d+)$/.exec(String(sp).trim());
      if (!pm) return;
      const pid = parseInt(pm[1], 10);
      if (Number.isNaN(pid)) return;
      setViewingProfile(pid);
      deepLinkHandled.current = true;
      clearPendingProfileStartParam();
      stripProfileLaunchParamsFromLocation();
      window.clearInterval(id);
    }, 125);
    return () => window.clearInterval(id);
  }, [token, loading]);

  const submitOrderReview = async () => {
    if (!viewingOrder || !token) return;
    try {
      const res = await fetch(apiUrl('/api/reviews'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          order_id: viewingOrder.id,
          stars: reviewStars,
          comment: reviewComment.trim() || undefined
        })
      });
      const data = await res.json();
      if (!res.ok) {
        showAlert(data.error || 'Не удалось сохранить отзыв');
        return;
      }
      setOrderReviewInfo({
        success: true,
        can_review: false,
        has_review: true,
        review: {
          stars: reviewStars,
          comment: reviewComment.trim(),
          created_at: new Date().toISOString()
        }
      });
      if (data.executor) {
        setViewingOrder((o: any) => ({
          ...o,
          executor_rating: data.executor.rating,
          executor_rating_count: data.executor.total_ratings
        }));
      }
    } catch (e) {
      console.error(e);
      showAlert('Ошибка сети');
    }
  };

  const onOwnProfileTab =
    viewingProfile != null && user?.id != null && Number(viewingProfile) === Number(user.id);

  const activeMainTab: MainTab = showBoostShop
    ? 'boost'
    : showMessagesInbox
      ? 'messages'
      : servicesScreen || showMyOrders || showCatalog || showMyActiveOrders
        ? 'deals'
        : 'home';

  const showMainTabBar =
    !loading &&
    !authError &&
    !showRoleSelection &&
    !showOrderDetails &&
    !showCreateOrder &&
    !showResponses &&
    !showFindExecutor &&
    !showLegalTerms &&
    !showLeaderboard &&
    !respondingTo &&
    !orderChatOpen &&
    !orderFilesCloudOpen &&
    (showBoostShop ||
      showMessagesInbox ||
      onOwnProfileTab ||
      !!servicesScreen ||
      showMyOrders ||
      showCatalog ||
      showMyActiveOrders ||
      (!viewingProfile &&
        !showMyArchivedOrders &&
        !showMyArchivedCustomerOrders &&
        !showPendingOrders &&
        !showMyOffers));

  const resetMainListScreens = () => {
    setShowMyOrders(false);
    setShowMyArchivedCustomerOrders(false);
    setShowCatalog(false);
    setShowMyActiveOrders(false);
    setShowMyArchivedOrders(false);
    setShowPendingOrders(false);
    setShowMyOffers(false);
    setServicesScreen(null);
    setServicesFocusId(null);
    setViewingProfile(null);
    setProfileFocusSection(null);
    setShowFindExecutor(false);
    setShowLeaderboard(false);
    setShowLegalTerms(false);
    setShowResponses(false);
    setRespondingTo(null);
    setShowCreateOrder(false);
    setShowBoostShop(false);
    setShowMessagesInbox(false);
    setOrderChatOpen(false);
    setOrderFilesCloudOpen(false);
    setPendingOpenOrderChat(false);
    setPendingChatPeerId(null);
    setPendingChatResponseId(null);
    setPendingOrderDetailsTab(null);
    setOpenedFromInbox(false);
    inboxChatWasOpenRef.current = false;
    closeOrderDetails({ restore: false });
  };

  /** Номер последнего переключения Заказы/Услуги — чтобы запоздавшая загрузка не перебила новый выбор. */
  const dealsSegmentSeqRef = useRef(0);

  const openDealsSegment = (
    segment: 'orders' | 'services',
    opts?: { resetOthers?: boolean }
  ) => {
    setDealsSegment(segment);
    const seq = ++dealsSegmentSeqRef.current;
    if (segment === 'orders') {
      /* Текущий экран (услуги, сообщения, буст…) закрываем только ПОСЛЕ загрузки заказов,
         в том же рендере, что и открытие списка: иначе между кадрами нет ни одного
         открытого экрана и на мгновение мелькает главная. */
      const beforeShow = () => {
        if (opts?.resetOthers && dealsSegmentSeqRef.current === seq) resetMainListScreens();
      };
      const load =
        effectiveHomeRole === 'customer' ? loadMyOrders(beforeShow) : openCatalog(beforeShow);
      void Promise.resolve(load).finally(() => {
        if (dealsSegmentSeqRef.current === seq) setServicesScreen(null);
      });
      return;
    }
    setShowMyOrders(false);
    setShowCatalog(false);
    setShowMyActiveOrders(false);
    setServicesScreen(effectiveHomeRole === 'executor' ? 'mine' : 'catalog');
  };

  const navigateMainTab = (tab: MainTab) => {
    if (tab === 'deals' && dealsSegment !== 'services') {
      openDealsSegment('orders', { resetOthers: true });
      return;
    }
    /* Любая другая навигация отменяет ещё не пришедшую загрузку «Заказов». */
    dealsSegmentSeqRef.current += 1;
    resetMainListScreens();
    if (tab === 'home') return;
    if (tab === 'deals') {
      openDealsSegment(dealsSegment === 'services' ? 'services' : 'orders');
      return;
    }
    if (tab === 'messages') {
      setShowMessagesInbox(true);
      return;
    }
    if (tab === 'boost') {
      setShowBoostShop(true);
    }
  };

  const refreshMessagesBadge = useCallback(async () => {
    if (!token) {
      setMessagesBadge(0);
      return;
    }
    const n = await fetchInboxUnreadTotal(token);
    setMessagesBadge(n);
  }, [token]);

  useEffect(() => {
    if (!token || loading) return;
    void refreshMessagesBadge();
    const t = window.setInterval(() => void refreshMessagesBadge(), 25000);
    return () => window.clearInterval(t);
  }, [token, loading, refreshMessagesBadge]);

  const openInboxThread = async (thread: InboxThread) => {
    setShowMessagesInbox(false);
    setOpenedFromInbox(true);
    inboxChatWasOpenRef.current = false;
    if (token) {
      void markInboxThreadRead(token, thread.order_id, thread.peer?.id);
      void refreshMessagesBadge();
    }
    setPendingOrderDetailsTab(null);
    setPendingOpenOrderChat(true);
    setPendingChatPeerId(thread.peer?.id != null ? Number(thread.peer.id) : null);
    setPendingChatResponseId(
      thread.response_id != null && Number.isFinite(Number(thread.response_id))
        ? Number(thread.response_id)
        : null
    );
    await viewOrderDetails({ id: thread.order_id });
  };

  const onPlusFromTab = () => {
    if (effectiveHomeRole === 'executor') {
      resetMainListScreens();
      setDealsSegment('services');
      setServicesScreen('mine');
      return;
    }
    setShowCreateOrder(true);
  };

  const dealsSegmentBar =
    activeMainTab === 'deals' ? (
      <div className="fc-deals-tabs" role="tablist" aria-label="Задания">
        <button
          type="button"
          role="tab"
          aria-selected={dealsSegment === 'orders'}
          className={'fc-deals-tabs__btn' + (dealsSegment === 'orders' ? ' fc-deals-tabs__btn--on' : '')}
          onClick={() => openDealsSegment('orders')}
        >
          Заказы
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={dealsSegment === 'services'}
          className={'fc-deals-tabs__btn' + (dealsSegment === 'services' ? ' fc-deals-tabs__btn--on' : '')}
          onClick={() => openDealsSegment('services')}
        >
          Услуги
        </button>
      </div>
    ) : null;

  const mainTabBar = showMainTabBar
    ? createPortal(
        <BottomTabBar
          active={activeMainTab}
          onTab={navigateMainTab}
          onPlus={onPlusFromTab}
          plusLabel={effectiveHomeRole === 'executor' ? 'Мои услуги' : 'Создать заказ'}
          messagesBadge={messagesBadge}
        />,
        document.body
      )
    : null;

  if (loading) {
    return (
      <div className="App">
        <header
          className="App-header"
          style={{ justifyContent: 'flex-start', paddingTop: '12px', minHeight: '100vh', width: '100%', boxSizing: 'border-box' }}
        >
          <p>Загрузка...</p>
        </header>
      </div>
    );
  }

  if (viewingProfile && !showLegalTerms) {
    return (
      <>
        {mainTabBar}
      <ProfilePage 
        userId={viewingProfile} 
        token={token || ''} 
        onBack={() => {
          if (user?.id != null && Number(viewingProfile) === Number(user.id)) {
            navigateMainTab('home');
            return;
          }
          setViewingProfile(null);
          setProfileFocusSection(null);
          void refreshHomeFavorites();
          popAndRestoreNav();
        }}
        isOwnProfile={viewingProfile === user?.id}
        focusSection={viewingProfile === user?.id ? profileFocusSection : null}
        onFocusSectionConsumed={() => setProfileFocusSection(null)}
        onOpenLegalTerms={() => setShowLegalTerms(true)}
        viewerRole={(() => {
          const r = role ?? user?.role;
          return r == null || r === '' ? null : String(r).trim();
        })()}
        onFavoritesChanged={() => void refreshHomeFavorites()}
        onExecutorOpenCustomerOrder={(orderId: number) => {
          const pid = viewingProfile;
          if (pid != null) pushNav({ type: 'profile', userId: pid });
          setViewingProfile(null);
          setProfileFocusSection(null);
          void viewOrderDetails({ id: orderId });
        }}
        onOrderedService={(orderId) => {
          const pid = viewingProfile;
          if (pid != null) pushNav({ type: 'profile', userId: pid });
          setViewingProfile(null);
          setProfileFocusSection(null);
          void refreshUserProfile();
          void refreshHomePreview();
          if (orderId) void viewOrderDetails({ id: orderId });
        }}
      />
      </>
    );
  }

  if (showBoostShop) {
    return (
      <>
        {mainTabBar}
        <BoostShop
          token={token || ''}
          role={
            effectiveHomeRole === 'customer' || effectiveHomeRole === 'executor'
              ? effectiveHomeRole
              : null
          }
          onBack={() => navigateMainTab('home')}
          onPaid={() => {
            void refreshUserProfile();
            void refreshWalletBalance();
          }}
        />
      </>
    );
  }

  if (showMessagesInbox) {
    return (
      <>
        {mainTabBar}
        <div className="App">
          <header
            className="App-header"
            style={{
              justifyContent: 'flex-start',
              padding: '12px 0 88px',
              minHeight: '100dvh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              boxSizing: 'border-box'
            }}
          >
            <MessagesInbox
              token={token || ''}
              onOpenThread={(t) => void openInboxThread(t)}
              onOpenPeer={(userId) => {
                setShowMessagesInbox(false);
                setViewingProfile(userId);
              }}
              onDeleted={() => void refreshMessagesBadge()}
            />
          </header>
        </div>
      </>
    );
  }

  if (authError) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>FreelanCYX</h1>
          <p>{authError}</p>
        </header>
      </div>
    );
  }

  if (showRoleSelection) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>FreelanCYX</h1>
          <p>Привет, {user?.first_name || 'друг'}!</p>
          <p>Кем ты хочешь стать?</p>
          
          <div style={{ marginTop: '30px' }}>
            <button onClick={() => handleRoleSelect('customer')} className="fc-btn fc-btn--primary">
              👑 Заказчик
            </button>
            <button onClick={() => handleRoleSelect('executor')} className="fc-btn fc-btn--primary">
              🔨 Исполнитель
            </button>
          </div>
          
          <p className="fc-muted fc-muted--block">
            Заказчик — ищет исполнителей, создаёт задания<br/>
            Исполнитель — предлагает услуги, берёт заказы
          </p>
        </header>
      </div>
    );
  }

  if (showOrderDetails && viewingOrder) {
    const isCustomer = role === 'customer';
    const hasAssignedExecutor =
      viewingOrder.executor_id != null && Number(viewingOrder.executor_id) > 0;
    const showSelfAsCustomer = isCustomer && !hasAssignedExecutor;
    const displayUser = showSelfAsCustomer
      ? {
          id: viewingOrder.customer_id ?? user?.id,
          firstName: viewingOrder.customer_first_name || user?.first_name,
          lastName: viewingOrder.customer_last_name || user?.last_name,
          rating: viewingOrder.customer_rating ?? user?.rating,
          role: 'Заказчик'
        }
      : isCustomer
        ? {
      id: viewingOrder.executor_id,
      firstName: viewingOrder.executor_first_name,
      lastName: viewingOrder.executor_last_name,
      rating: viewingOrder.executor_rating,
      role: 'Исполнитель'
          }
        : {
      id: viewingOrder.customer_id,
      firstName: viewingOrder.customer_first_name,
      lastName: viewingOrder.customer_last_name,
      rating: viewingOrder.customer_rating,
      role: 'Заказчик'
    };

    const counterpartyAvatarUrl = showSelfAsCustomer
      ? viewingOrder.customer_avatar_url || user?.avatar_url
      : isCustomer
        ? viewingOrder.executor_avatar_url
        : viewingOrder.customer_avatar_url;
    const counterpartyLastSeen = showSelfAsCustomer
      ? viewingOrder.customer_last_seen_at || user?.last_seen_at
      : isCustomer
        ? viewingOrder.executor_last_seen_at
        : viewingOrder.customer_last_seen_at;

    const showCustomerPickExecutorUi =
      isCustomer &&
      viewingOrder.status === 'open' &&
      Number(viewingOrder.responses_count || 0) > 0 &&
      !['pending_payment', 'negotiating', 'accepted'].includes(
        String(viewingOrder.selected_response_status || '')
      );

    const switchOrderDetailsTab = (tab: 'about' | 'executors') => {
      setOrderDetailsCustomerTab(tab);
      requestAnimationFrame(() => {
        const el = orderDetailsBodyRef.current;
        if (el) el.scrollTop = 0;
      });
    };
    const responsesLimitShort =
      viewingOrder.responses_limit != null && Number(viewingOrder.responses_limit) > 0
        ? String(Number(viewingOrder.responses_limit))
        : '∞';
    const detailResponsesCount = Number(viewingOrder.responses_count || 0);

    return (
      <div className="App">
        <header
          className="App-header"
          style={{
            justifyContent: 'flex-start',
            paddingTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          {!orderFilesCloudOpen && !orderChatOpen ? (
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto 10px',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
              <button
                type="button"
                onClick={() => closeOrderDetails()}
                aria-label="Назад"
                title="Назад"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--fc-card-hover)',
                  color: 'var(--fc-text)',
                  border: '1px solid var(--fc-border)',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                  flexShrink: 0
                }}
              >
                ←
              </button>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, minWidth: 0 }}>Детали задания</h2>
            </div>
            {role === 'executor' && viewingOrder.status === 'open' ? (
              <button
                type="button"
                className={`fc-favorite-heart ${orderDetailsOrderFavorite ? 'fc-favorite-heart--on' : ''}`}
                disabled={orderDetailsOrderFavorite === null || orderDetailsFavoriteBusy}
                onClick={() => void toggleOrderFavoriteFromDetails()}
                title={orderDetailsOrderFavorite ? 'Убрать из избранного' : 'В избранное'}
                aria-label={orderDetailsOrderFavorite ? 'Убрать заказ из избранного' : 'Добавить заказ в избранное'}
              >
                <FavoriteHeartIcon filled={orderDetailsOrderFavorite === true} />
              </button>
            ) : null}
          </div>
          ) : null}

          <div
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              ref={orderDetailsBodyRef}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                width: '100%'
              }}
            >
              {showCustomerPickExecutorUi && orderDetailsCustomerTab === 'executors' ? (
                <div className="fc-card fc-order-card" style={{ padding: '12px 12px 16px' }}>
                  <h3 style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: 800 }}>Выбрать исполнителя</h3>
                  <p style={{ margin: '0 0 14px', fontSize: '14px', color: 'var(--fc-text-muted)' }}>
                    Откликов: <b style={{ color: 'var(--fc-text)' }}>{detailResponsesCount}</b> /{' '}
                    <b style={{ color: 'var(--fc-text)' }}>{responsesLimitShort}</b>
                  </p>
                  {orderDetailsResponsesLoading ? (
                    <p style={{ color: 'var(--fc-text-muted)', margin: 0 }}>Загрузка…</p>
                  ) : orderDetailsResponses.length === 0 ? (
                    <p style={{ color: 'var(--fc-text-muted)', margin: 0 }}>Пока нет откликов</p>
                  ) : (
                    orderDetailsResponses.map((resp) => (
                      <div
                        key={resp.id}
                        className="fc-card fc-order-card"
                        style={{
                          padding: '12px',
                          margin: '10px 0 0',
                          display: 'flex',
                          gap: 12,
                          alignItems: 'stretch'
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div
                              className="fc-avatar-presence-anchor"
                              style={{
                                width: '34px',
                                height: '34px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--fc-accent)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: 'white',
                                fontWeight: 800,
                                overflow: 'visible',
                                flexShrink: 0
                              }}
                              onClick={() => setViewingProfile(resp.executor_id)}
                              title="Профиль исполнителя"
                            >
                              {resp.avatar_url ? (
                                <img
                                  src={resp.avatar_url}
                                  alt=""
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    borderRadius: '50%'
                                  }}
                                />
                              ) : (
                                <>
                                  {resp.first_name?.[0]}
                                  {resp.last_name?.[0]}
                                </>
                              )}
                              <PresenceStatusDot lastSeenAt={resp.executor_last_seen_at} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.25 }}>
                                {resp.first_name} {resp.last_name}{' '}
                                <span style={{ fontWeight: 600, color: 'var(--fc-text-muted)' }}>⭐ {resp.rating}</span>
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--fc-text-muted)', marginTop: 2 }}>
                                {responseStatusLabel(resp.status)}
                              </div>
                            </div>
                          </div>

                          {resp.message ? (
                            <div style={{ fontSize: 13, lineHeight: 1.35, color: 'var(--fc-text)', marginTop: 4 }}>
                              {resp.message}
                            </div>
                          ) : null}

                          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.35 }}>
                            {resp.proposed_budget != null &&
                            resp.proposed_budget !== '' &&
                            Number(resp.proposed_budget) > 0 ? (
                              <>
                                💰 Предлагает: <b style={{ color: 'var(--fc-text)' }}>{formatMicroUsdt(resp.proposed_budget)} USDT</b>
                                {viewingOrder?.budget != null ? (
                                  <span style={{ color: 'var(--fc-text-muted)' }}>
                                    {' '}
                                    (в заказе {formatMicroUsdt(viewingOrder.budget)} USDT)
                                  </span>
                                ) : null}
                              </>
                            ) : (
                              <>
                                💰 Бюджет:{' '}
                                <b style={{ color: 'var(--fc-text)' }}>
                                  {viewingOrder?.budget != null ? `${formatMicroUsdt(viewingOrder.budget)} USDT` : '—'}
                                </b>
                              </>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 150 }}>
                          <button
                            type="button"
                            className="fc-btn fc-btn--dark fc-btn--sm"
                            onClick={() => setViewingProfile(resp.executor_id)}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            👤 Профиль
                          </button>
                          {resp.status === 'pending' ? (
                            <>
                              <button
                                type="button"
                                className="fc-btn fc-btn--primary fc-btn--sm"
                                onClick={() => {
                                  setAssigningResp(resp);
                                  setAssignDeadlineValue(resp?.eta_value != null ? String(resp.eta_value) : '');
                                  setAssignDeadlineUnit((resp?.eta_unit as any) || 'days');
                                  setShowAssignExecutorModal(true);
                                }}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                Выбрать
                              </button>
                              <button
                                type="button"
                                className="fc-btn fc-btn--muted fc-btn--sm"
                                onClick={() => rejectResponse(resp.id, resp.order_id)}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                ❌ Отказать
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="fc-card fc-order-card" style={{ position: 'relative' }}>
            {viewingOrder.is_urgent ||
            (viewingOrder.order_top_until &&
              new Date(String(viewingOrder.order_top_until)).getTime() > Date.now()) ? (
              <div
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 12,
                  zIndex: 2,
                  textAlign: 'right',
                  lineHeight: 1.35,
                  pointerEvents: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 4
                }}
              >
                {viewingOrder.is_urgent ? (
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fc-accent-strong)', whiteSpace: 'nowrap' }}>
                    🔥 Срочно
                  </span>
                ) : null}
                {viewingOrder.order_top_until &&
                new Date(String(viewingOrder.order_top_until)).getTime() > Date.now() ? (
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fc-usdt)', whiteSpace: 'nowrap' }}>
                    ТОП
                  </span>
                ) : null}
              </div>
            ) : null}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '15px',
                gap: '10px',
                paddingRight:
                  viewingOrder.is_urgent ||
                  (viewingOrder.order_top_until &&
                    new Date(String(viewingOrder.order_top_until)).getTime() > Date.now())
                    ? 100
                    : undefined
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <div
                  className="fc-avatar-presence-anchor fc-avatar-presence-anchor--50"
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--fc-accent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                    marginRight: '12px',
                    cursor: 'default',
                  fontSize: '20px',
                  color: 'white',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    overflow: 'visible'
                  }}
                >
                  {counterpartyAvatarUrl ? (
                    <img
                      src={counterpartyAvatarUrl}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        borderRadius: '50%'
                      }}
                    />
                  ) : (
                    <>
                      {displayUser.firstName?.[0]}
                      {displayUser.lastName?.[0]}
                    </>
                  )}
                  <PresenceStatusDot lastSeenAt={counterpartyLastSeen} />
              </div>
                <div style={{ minWidth: 0 }}>
                  <strong>
                    {displayUser.firstName} {displayUser.lastName}
                  </strong>{' '}
                  ⭐ {displayUser.rating != null && displayUser.rating !== '' ? displayUser.rating : '—'}
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--fc-text-muted)' }}>{displayUser.role}</p>
              </div>
              </div>
              {displayUser.id != null && Number(displayUser.id) > 0 ? (
                <button
                  type="button"
                  className="fc-btn fc-btn--dark fc-btn--sm"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={() => setViewingProfile(Number(displayUser.id))}
                >
                  👤 Профиль
                </button>
              ) : null}
            </div>

            <h3
              style={{
                width: '100%',
                maxWidth: '420px',
                margin: '12px 0 8px',
                paddingRight:
                  viewingOrder.is_urgent ||
                  (viewingOrder.order_top_until &&
                    new Date(String(viewingOrder.order_top_until)).getTime() > Date.now())
                    ? 100
                    : undefined,
                boxSizing: 'border-box'
              }}
            >
              <OrderTitleWithId title={viewingOrder.title || 'Заказ'} id={Number(viewingOrder.id)} />
            </h3>
            {role === 'customer' && viewingOrder.status === 'open' && (
              <p style={{ marginTop: 0 }}>
                📝 Откликов: <b>{Number(viewingOrder.responses_count || 0)}</b> /{' '}
                <b>
                  {viewingOrder.responses_limit != null && Number(viewingOrder.responses_limit) > 0
                    ? Number(viewingOrder.responses_limit)
                    : 'безлимитно'}
                </b>
              </p>
            )}
            <p>{viewingOrder.description}</p>
            <p>Категория: {orderCategoryLabel(viewingOrder.category)}</p>
            {(() => {
              const dealMicro = orderDealBudgetMicro(viewingOrder);
              const catalogMicro = Number(viewingOrder.budget);
              const hasDeal =
                Number(viewingOrder.selected_proposed_budget) > 0 &&
                Number.isFinite(Number(viewingOrder.selected_proposed_budget));
              const catalogDiffers =
                hasDeal &&
                Number.isFinite(catalogMicro) &&
                catalogMicro > 0 &&
                catalogMicro !== Number(viewingOrder.selected_proposed_budget);
              return (
                <p>
                  Бюджет:{' '}
                  {dealMicro != null ? `${formatMicroUsdt(dealMicro)} USDT` : 'не указан'}
                  {catalogDiffers ? (
                    <span style={{ color: 'var(--fc-text-muted)', fontSize: '0.92em' }}>
                      {' '}
                      (в каталоге {formatMicroUsdt(catalogMicro)} USDT)
                    </span>
                  ) : null}
                </p>
              );
            })()}
            {viewingOrder.selected_pending_budget_usdt_micro != null &&
            Number.isFinite(Number(viewingOrder.selected_pending_budget_usdt_micro)) ? (
              <div
                style={{
                  marginTop: 10,
                  maxWidth: 420,
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid rgba(var(--fc-accent-rgb), 0.45)',
                  background: 'rgba(var(--fc-accent-rgb), 0.12)',
                  textAlign: 'left'
                }}
              >
                {Number(viewingOrder.selected_pending_budget_proposer_id) === Number(user?.id) ? (
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--fc-text)', lineHeight: 1.45 }}>
                    Ожидаем подтверждения суммы{' '}
                    <strong>
                      {formatMicroUsdt(Number(viewingOrder.selected_pending_budget_usdt_micro))} USDT
                    </strong>
                    …
                  </p>
                ) : (
                  <>
                    <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--fc-text)', lineHeight: 1.45 }}>
                      Предложена новая сумма:{' '}
                      <strong>
                        {formatMicroUsdt(Number(viewingOrder.selected_pending_budget_usdt_micro))} USDT
                      </strong>
                      . Подтвердите, откажитесь от задания или укажите свою сумму.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button
                        type="button"
                        className="fc-btn fc-btn--success fc-btn--block"
                        disabled={promoPayBusy || !viewingOrder.selected_response_id}
                        onClick={async () => {
                          if (!token || !viewingOrder?.selected_response_id) return;
                          setPromoPayBusy(true);
                          try {
                            const r = await fetch(
                              apiUrl(
                                `/api/responses/${viewingOrder.selected_response_id}/budget-pending/accept`
                              ),
                              {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` }
                              }
                            );
                            const d = await r.json();
                            if (!r.ok) throw new Error(d.error || 'Ошибка');
                            showAlert(d.message || 'Сумма согласована');
                            await viewOrderDetails({ id: viewingOrder.id });
                          } catch (e: any) {
                            showAlert(e?.message || 'Не удалось подтвердить');
                          } finally {
                            setPromoPayBusy(false);
                          }
                        }}
                      >
                        ✅ Подтвердить
                      </button>
                      <button
                        type="button"
                        className="fc-btn fc-btn--danger fc-btn--block"
                        disabled={promoPayBusy || !viewingOrder.selected_response_id}
                        onClick={async () => {
                          if (!token || !viewingOrder?.selected_response_id) return;
                          if (
                            !await showConfirm(
                              'Отказаться от задания? Предложенная сумма будет отклонена, сделка закроется без штрафа.'
                            )
                          ) {
                            return;
                          }
                          setPromoPayBusy(true);
                          try {
                            const r = await fetch(
                              apiUrl(
                                `/api/responses/${viewingOrder.selected_response_id}/budget-pending/reject`
                              ),
                              {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` }
                              }
                            );
                            const d = await r.json();
                            if (!r.ok) throw new Error(d.error || 'Ошибка');
                            showAlert(d.message || 'Сделка отменена');
                            if (d.service_deal_removed) {
                              setShowOrderDetails(false);
                              setViewingOrder(null);
                            } else {
                              await viewOrderDetails({ id: viewingOrder.id });
                            }
                          } catch (e: any) {
                            showAlert(e?.message || 'Не удалось отказаться');
                          } finally {
                            setPromoPayBusy(false);
                          }
                        }}
                      >
                        ❌ Отказаться
                      </button>
                      <button
                        type="button"
                        className="fc-btn fc-btn--ghost fc-btn--block"
                        disabled={promoPayBusy}
                        onClick={() => {
                          setServiceBudgetAmount(
                            formatMicroUsdt(
                              Number(viewingOrder.selected_pending_budget_usdt_micro)
                            )
                          );
                          setServiceBudgetScope('order');
                          setBudgetModalKind(
                            viewingOrder.executor_service_id ? 'service' : 'order'
                          );
                          setBudgetModalCounter(true);
                          setShowServiceBudgetModal(true);
                        }}
                      >
                        💱 Указать свою сумму
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : null}
            <p>Статус: {orderStatusLabel(viewingOrder.status)}</p>
            {role === 'customer' &&
            viewingOrder.status === 'open' &&
            token &&
            !viewingOrder.executor_service_id ? (
              <div
                style={{
                  marginTop: 10,
                  maxWidth: 420,
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '1px solid rgba(120, 200, 140, 0.28)',
                  background: 'rgba(46, 90, 58, 0.14)',
                  textAlign: 'left'
                }}
              >
                <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--fc-text)', lineHeight: 1.45 }}>
                  {viewingOrder.order_top_until &&
                  new Date(String(viewingOrder.order_top_until)).getTime() > Date.now() ? (
                    <>
                      ТОП в каталоге до{' '}
                      <strong>
                        {new Date(String(viewingOrder.order_top_until)).toLocaleString('ru-RU')}
                      </strong>
                      .
                    </>
                  ) : (
                    <>ТОП в каталоге сейчас не активен — можно включить продлением (списывается 1 слот).</>
                  )}
                </p>
                <button
                  type="button"
                  className="fc-btn fc-btn--dark fc-btn--sm fc-btn--block"
                  disabled={(user?.order_top_slots_left ?? 0) < 1}
                  onClick={() => void handleExtendOrderTop(Number(viewingOrder.id))}
                >
                  Продлить ТОП (+{user?.order_top_hours ?? 24} ч, 1 слот)
                </button>
              </div>
            ) : null}
            {role === 'customer' &&
            viewingOrder.status === 'open' &&
            !viewingOrder.executor_service_id ? (
              <div
                style={{
                  marginTop: 10,
                  maxWidth: 420,
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '1px solid rgba(120, 160, 220, 0.35)',
                  background: 'rgba(42, 80, 140, 0.18)',
                  textAlign: 'left'
                }}
              >
                <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--fc-text)', lineHeight: 1.45 }}>
                  {viewingOrder.channel_published_at ? (
                    <>Задание уже опубликовано в канале биржи.</>
                  ) : (
                    <>
                      📢 Пост задания в Telegram-канал биржи. Доступно постов:{' '}
                      <strong>{orderChannelPostsAvailable}</strong>
                      {user?.vip_active
                        ? ` (VIP: ${Number(user?.customer_channel_posts_remaining ?? 0)} из ${
                            user?.customer_vip_channel_posts_per_period ?? 3
                          }, купленных: ${Number(user?.customer_channel_post_credits ?? 0)})`
                        : ''}
                      .
                    </>
                  )}
                </p>
                {!viewingOrder.channel_published_at && orderChannelPostsAvailable < 1 ? (
                  <button
                    type="button"
                    className="fc-btn fc-btn--primary fc-btn--sm fc-btn--block"
                    onClick={() => void handlePurchaseOrderChannelPost()}
                  >
                    Купить пост · {user?.order_channel_post_stars ?? 75} ⭐
                  </button>
                ) : (
                  <button
                    type="button"
                    className="fc-btn fc-btn--primary fc-btn--sm fc-btn--block"
                    disabled={orderChannelBusy || Boolean(viewingOrder.channel_published_at)}
                    onClick={() => void handlePublishOrderToChannel(Number(viewingOrder.id))}
                  >
                    {orderChannelBusy
                      ? '…'
                      : viewingOrder.channel_published_at
                        ? 'Уже в канале'
                        : 'Опубликовать в канал'}
                  </button>
                )}
              </div>
            ) : null}
            {viewingOrder.execution_deadline_value && viewingOrder.execution_deadline_unit && (
              <p>
                Срок выполнения: {formatDurationValueUnit(
                  viewingOrder.execution_deadline_value,
                  viewingOrder.execution_deadline_unit
                )}
                {viewingOrder.execution_deadline_at
                  ? ` (до ${new Date(viewingOrder.execution_deadline_at).toLocaleString('ru-RU')})`
                  : ''}
                {role === 'executor' &&
                  String(viewingOrder.status || '') === 'open' &&
                  String(viewingOrder.selected_response_status || '') === 'pending_payment' &&
                  Number(viewingOrder.executor_id) === Number(user?.id) && (
                    <span style={{ color: 'var(--fc-accent-strong)', fontWeight: 700 }}>
                      {' '}
                      · ожидает оплаты эскроу
                    </span>
                  )}
              </p>
            )}
            {!viewingOrder.execution_deadline_value && (
              <p>Срок выполнения заказа: не указан</p>
            )}
            {isCustomer &&
              viewingOrder.is_promo_free &&
              viewingOrder.status !== 'finished' &&
              viewingOrder.status !== 'cancelled' && (
              <p
                style={{
                  fontSize: '13px',
                  color: 'var(--fc-accent-strong)',
                  textAlign: 'left',
                  maxWidth: '420px',
                  lineHeight: 1.4
                }}
              >
                Промо-заказ: оплата с баланса FreelanCYX. Пополните USDT в боте (Crypto Pay), затем нажмите «Оплатить
                работу» — средства пойдут исполнителю и комиссии платформы.
              </p>
            )}

            {token &&
              role === 'customer' &&
              viewingOrder.status === 'in_progress' &&
              deadlineReplaceInfo?.eligible && (
                <div style={{ marginTop: '10px', maxWidth: '420px' }}>
                  <p style={{ fontSize: '13px', color: 'var(--fc-accent-strong)', margin: '0 0 8px', lineHeight: 1.4 }}>
                    Срок выполнения истёк. Можно продлить время текущему исполнителю или сменить исполнителя (штраф{' '}
                    <strong>{deadlineReplaceInfo.penalty_xp ?? 20} XP</strong> прежнему).
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      const o = deadlineReplaceInfo.other_responses || [];
                      setDeadlineReplacePickId(o.length > 0 ? Number(o[0].id) : '');
                      setDlReplaceValue('');
                      setShowDeadlineReplaceModal(true);
                    }}
                    className="fc-btn fc-btn--warn fc-btn--block"
                  >
                    🔁 Найти другого исполнителя
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setExtendDlValue('');
                      setExtendDlUnit('hours');
                      setShowExtendDeadlineModal(true);
                    }}
                    className="fc-btn fc-btn--dark fc-btn--block"
                    style={{ marginTop: '10px' }}
                  >
                    ⏱️ Продлить время выполнения
                  </button>
                </div>
              )}

            {token &&
              role &&
              (role === 'customer' || role === 'executor') &&
              viewingOrder.status === 'open' &&
              String(viewingOrder.selected_response_status || '') === 'pending_payment' && (
                <div
                  style={{
                    marginTop: 14,
                    maxWidth: 420,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1px solid rgba(255, 193, 7, 0.45)',
                    background: 'rgba(255, 193, 7, 0.1)',
                    textAlign: 'left'
                  }}
                >
                  {(() => {
                    const workMicro = Number(
                      viewingOrder.work_budget_usdt_micro ??
                        (viewingOrder.selected_proposed_budget > 0
                          ? viewingOrder.selected_proposed_budget
                          : viewingOrder.budget)
                    );
                    const customerPays =
                      String(viewingOrder.commission_payer || '') === 'customer';
                    const feePct = Number(
                      viewingOrder.platform_commission_percent ?? 10
                    );
                    const feeMicro = customerPays
                      ? Number(
                          viewingOrder.platform_commission_usdt_micro ??
                            Math.floor((workMicro * feePct) / 100)
                        )
                      : 0;
                    const holdMicro = customerPays
                      ? Number(
                          viewingOrder.escrow_hold_usdt_micro ?? workMicro + feeMicro
                        )
                      : workMicro;
                    return (
                      <>
                        <p style={{ margin: '0 0 8px', fontWeight: 700, color: 'var(--fc-text)' }}>
                          {role === 'customer'
                            ? 'Оплатите работу на эскроу'
                            : 'Ожидание оплаты эскроу'}
                        </p>
                        <p
                          style={{
                            margin: '0 0 10px',
                            fontSize: 13,
                            color: 'var(--fc-text)',
                            lineHeight: 1.45
                          }}
                        >
                          {role === 'customer' ? (
                            customerPays ? (
                              <>
                                Работа:{' '}
                                <strong>{formatMicroUsdt(workMicro)} USDT</strong> + комиссия
                                платформы {feePct}% (
                                <strong>{formatMicroUsdt(feeMicro)} USDT</strong>). К оплате на
                                эскроу:{' '}
                                <strong>{formatMicroUsdt(holdMicro)} USDT</strong>. После
                                выполнения исполнителю уйдёт {formatMicroUsdt(workMicro)} USDT.
                                До оплаты можно обсудить детали в чате
                                {viewingOrder.selected_budget_agreed_at
                                  ? ''
                                  : ' и изменить сумму задания'}
                                ; отмена доступна до оплаты.
                              </>
                            ) : (
                              <>
                                Сумма{' '}
                                <strong>{formatMicroUsdt(workMicro)} USDT</strong> будет
                                заморожена на эскроу-счёте. После успешного выполнения и вашего
                                подтверждения деньги перейдут исполнителю. До оплаты можно
                                обсудить работу в чате
                                {viewingOrder.selected_budget_agreed_at
                                  ? ''
                                  : ' и изменить сумму'}
                                ; отмена заказа доступна до оплаты.
                              </>
                            )
                          ) : customerPays ? (
                            <>
                              Заказ по вашей услуге. Заказчик оплатит работу{' '}
                              <strong>{formatMicroUsdt(workMicro)} USDT</strong> + комиссию
                              платформы. Пока эскроу не внесён, работа не стартует. Чат уже
                              доступен.
                            </>
                          ) : (
                            <>
                              Заказчик выбрал вас. Пока оплата не внесена, работа не стартует.
                              Чат уже доступен.
                            </>
                          )}
                        </p>
                        {role === 'customer' && (
                          <>
                            <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--fc-text)' }}>
                              Баланс:{' '}
                              <strong>
                                {walletBalance != null
                                  ? `${formatMicroUsdt(walletBalance)} USDT`
                                  : '…'}
                              </strong>
                            </p>
                            <button
                              type="button"
                              className="fc-btn fc-btn--success fc-btn--block"
                              disabled={
                                promoPayBusy ||
                                !viewingOrder.selected_response_id ||
                                viewingOrder.selected_pending_budget_usdt_micro != null
                              }
                              onClick={() =>
                                void payEscrowForResponse(
                                  Number(viewingOrder.selected_response_id),
                                  Number(viewingOrder.id)
                                )
                              }
                              style={{
                                marginBottom: 8,
                                opacity:
                                  promoPayBusy ||
                                  viewingOrder.selected_pending_budget_usdt_micro != null
                                    ? 0.6
                                    : 1
                              }}
                            >
                              {viewingOrder.selected_pending_budget_usdt_micro != null
                                ? '⏳ Сначала согласуйте сумму'
                                : promoPayBusy
                                  ? '⏳ Обработка…'
                                  : customerPays
                                    ? `✅ Оплатить ${formatMicroUsdt(holdMicro)} USDT на эскроу`
                                    : '✅ Оплатить на эскроу'}
                            </button>
                            {!viewingOrder.executor_service_id &&
                            !viewingOrder.selected_budget_agreed_at ? (
                              <button
                                type="button"
                                className="fc-btn fc-btn--ghost fc-btn--block"
                                disabled={promoPayBusy}
                                style={{ marginBottom: 8 }}
                                onClick={() => {
                                  setServiceBudgetAmount(
                                    formatMicroUsdt(
                                      Number(
                                        viewingOrder.selected_proposed_budget > 0
                                          ? viewingOrder.selected_proposed_budget
                                          : viewingOrder.budget
                                      )
                                    )
                                  );
                                  setServiceBudgetScope('order');
                                  setBudgetModalKind('order');
                                  setBudgetModalCounter(false);
                                  setShowServiceBudgetModal(true);
                                }}
                              >
                                💱 Изменить сумму задания
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => void refreshWalletBalance()}
                              className="fc-btn fc-btn--dark fc-btn--block fc-btn--sm"
                            >
                              🔄 Обновить баланс
                            </button>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

            {token &&
              role &&
              (role === 'customer' || role === 'executor') && (
                <OrderFiles
                  orderId={viewingOrder.id}
                  token={token}
                  orderStatus={viewingOrder.status}
                  myRole={role}
                />
              )}

            {token &&
              role &&
              (role === 'customer' || role === 'executor') && (
                <OrderChat
                  orderId={viewingOrder.id}
                  token={token}
                  orderStatus={viewingOrder.status}
                  myRole={role}
                  autoOpen={pendingOpenOrderChat}
                  openSignal={chatLaunchNonce}
                  peerId={pendingChatPeerId}
                  responseId={pendingChatResponseId}
                  isServiceOrder={viewingOrder.executor_service_id != null}
                  orderBudgetMicro={
                    viewingOrder.budget != null ? Number(viewingOrder.budget) : null
                  }
                  onAssigned={() => {
                    void viewOrderDetails({ id: viewingOrder.id });
                    void refreshMessagesBadge();
                  }}
                  onViewOrder={() => {
                    // Не возвращать в inbox — остаёмся на карточке заказа
                    inboxChatWasOpenRef.current = false;
                    window.dispatchEvent(new CustomEvent('fcyx-close-order-chat'));
                  }}
                />
              )}

            {token &&
              role &&
              (role === 'customer' || role === 'executor') &&
              viewingOrder.status === 'open' &&
              String(viewingOrder.selected_response_status || '') === 'negotiating' && (
                <div
                  style={{
                    marginTop: 14,
                    maxWidth: 420,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1px solid rgba(100, 180, 255, 0.4)',
                    background: 'rgba(60, 120, 200, 0.12)',
                    textAlign: 'left'
                  }}
                >
                  <p style={{ margin: '0 0 8px', fontWeight: 700, color: 'var(--fc-text)' }}>
                    {role === 'executor' ? 'Обсуждение услуги' : 'Ожидание исполнителя'}
                  </p>
                  <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--fc-text)', lineHeight: 1.45 }}>
                    {role === 'executor' ? (
                      <>
                        Обсудите детали в чате. Когда будете готовы — нажмите «Готов приступить к работе».
                        До этого заказчик не оплачивает эскроу.
                        {viewingOrder.selected_budget_agreed_at
                          ? ''
                          : ' Можно изменить сумму услуги.'}
                      </>
                    ) : (
                      <>
                        Чат с исполнителем уже доступен. Когда исполнитель будет готов приступить, вам
                        придёт уведомление об оплате эскроу (работа + комиссия платформы).
                      </>
                    )}
                  </p>
                  {role === 'executor' &&
                  viewingOrder.executor_service_id &&
                  viewingOrder.selected_response_id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button
                        type="button"
                        className="fc-btn fc-btn--primary fc-btn--block"
                        disabled={
                          promoPayBusy ||
                          viewingOrder.selected_pending_budget_usdt_micro != null
                        }
                        onClick={async () => {
                          setPromoPayBusy(true);
                          try {
                            const r = await fetch(
                              apiUrl(
                                `/api/responses/${viewingOrder.selected_response_id}/ready-for-work`
                              ),
                              {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` }
                              }
                            );
                            const d = await r.json();
                            if (!r.ok) throw new Error(d.error || 'Ошибка');
                            showAlert(d.message || 'Заказчик получит уведомление об оплате');
                            await viewOrderDetails({ id: viewingOrder.id });
                          } catch (e: any) {
                            showAlert(e?.message || 'Не удалось');
                          } finally {
                            setPromoPayBusy(false);
                          }
                        }}
                      >
                        {viewingOrder.selected_pending_budget_usdt_micro != null
                          ? '⏳ Сначала согласуйте сумму'
                          : '✅ Готов приступить к работе'}
                      </button>
                      {role === 'executor' && !viewingOrder.selected_budget_agreed_at ? (
                      <button
                        type="button"
                        className="fc-btn fc-btn--ghost fc-btn--block"
                        disabled={promoPayBusy}
                        onClick={() => {
                          setServiceBudgetAmount(
                            formatMicroUsdt(
                              Number(
                                viewingOrder.selected_proposed_budget > 0
                                  ? viewingOrder.selected_proposed_budget
                                  : viewingOrder.budget
                              )
                            )
                          );
                          setServiceBudgetScope('order');
                          setBudgetModalKind('service');
                          setBudgetModalCounter(false);
                          setShowServiceBudgetModal(true);
                        }}
                      >
                        💱 Изменить сумму задания
                      </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}

            {role === 'customer' && viewingOrder.status === 'finished' && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: 'var(--fc-card)',
                    borderRadius: '12px',
                    textAlign: 'left',
                    maxWidth: '420px',
                    border: '1px solid var(--fc-border)'
                  }}
                >
                  <p style={{ margin: '0 0 8px', fontWeight: 600 }}>⭐ Отзыв об исполнителе</p>
                  {orderReviewInfo === null ? (
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--fc-text-muted)' }}>Загрузка…</p>
                  ) : orderReviewInfo.has_review && orderReviewInfo.review ? (
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--fc-text)' }}>
                      {'★'.repeat(Number(orderReviewInfo.review.stars) || 5)} ({orderReviewInfo.review.stars}/5)
                      {orderReviewInfo.review.comment ? ` — ${orderReviewInfo.review.comment}` : ''}
                    </p>
                  ) : orderReviewInfo.can_review ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          gap: '6px',
                          flexWrap: 'wrap',
                          marginBottom: '8px',
                          justifyContent: 'center'
                        }}
                      >
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setReviewStars(n)}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '8px',
                              border: n === reviewStars ? '2px solid var(--fc-accent)' : '1px solid var(--fc-border)',
                              background: n <= reviewStars ? 'rgba(var(--fc-accent-rgb), 0.18)' : 'var(--fc-card-hover)',
                              color: 'var(--fc-text)',
                              cursor: 'pointer'
                            }}
                          >
                            {n}★
                          </button>
                        ))}
                      </div>
                      <textarea
                        placeholder="Комментарий (необязательно)"
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        style={{
                          width: '100%',
                          minHeight: '72px',
                          marginBottom: '8px',
                          borderRadius: '8px',
                          padding: '8px',
                          boxSizing: 'border-box',
                          border: '1px solid var(--fc-input-border)',
                          background: 'var(--fc-input-bg)',
                          color: 'var(--fc-input-text)',
                          fontFamily: 'inherit'
                        }}
                      />
                      <button type="button" onClick={() => void submitOrderReview()} className="fc-btn fc-btn--primary">
                        Отправить отзыв
                      </button>
                    </>
                  ) : (
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--fc-text-muted)' }}>
                      Отзыв по этому заказу недоступен.
                    </p>
                  )}
                </div>
              )}
            
            <div style={{ marginTop: '20px', width: '100%', maxWidth: '420px' }}>
              {role === 'executor' && viewingOrder.status === 'in_progress' && (
                <div className="fc-btn-row">
                  <button
                    type="button"
                    onClick={() => markAsCompleted(viewingOrder.id)}
                    className="fc-btn fc-btn--primary fc-btn--block"
                  >
                    ✅ Задание выполнено
                  </button>
                  <button 
                    type="button"
                    onClick={() => cancelByExecutor(viewingOrder.id)} 
                    className="fc-btn fc-btn--warn fc-btn--block"
                  >
                    ⚠️ Отказаться (штраф 15 баллов)
                  </button>
                  {canOpenOrderDispute(viewingOrder, role) && (
                    <button
                      type="button"
                      onClick={() => setDisputeModalOpen(true)}
                      className="fc-btn fc-btn--dark fc-btn--block"
                    >
                      ⚖️ Открыть спор
                    </button>
                  )}
                </div>
              )}

              {role === 'customer' && viewingOrder.status === 'completed' && (
                <div className="fc-btn-row" style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => confirmCompletion(viewingOrder.id)}
                    className="fc-btn fc-btn--primary fc-btn--block"
                  >
                  ✅ Подтвердить выполнение
                </button>
                </div>
              )}

              {role === 'customer' && viewingOrder.status === 'awaiting_payment' && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: 'rgba(var(--fc-usdt-rgb), 0.10)',
                    borderRadius: '10px',
                    textAlign: 'left',
                    maxWidth: '340px',
                    border: '1px solid rgba(var(--fc-usdt-rgb), 0.35)'
                  }}
                >
                  <p style={{ margin: '0 0 10px', fontSize: '14px', color: 'var(--fc-text)', fontWeight: 600 }}>
                    💳 Оплата работы
                  </p>
                  <p style={{ margin: '0 0 10px', fontSize: '13px', color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
                    1) Пополните баланс в боте командой <b>/pay {viewingOrder.budget != null ? formatMicroUsdt(viewingOrder.budget) : '…'}</b> USDT (ровно под
                    бюджет) или кнопками. 2) Нажмите «Обновить баланс» — пополнение подтянется с Crypto Pay, если вебхук
                    задержался. 3) «Оплатить работу» — с баланса спишется сумма заказа, исполнителю уйдёт выплата за вычетом
                    комиссии.
                  </p>
                  <p style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--fc-text)' }}>
                    Ваш баланс:{' '}
                    <strong>
                      {walletBalance === null ? '…' : `${formatMicroUsdt(walletBalance)} USDT`}
                    </strong>
                  </p>
                  {viewingOrder.payment_deadline_at && (
                    <p style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--fc-danger)' }}>
                      Дедлайн оплаты:{' '}
                      <strong>
                        {new Date(viewingOrder.payment_deadline_at).toLocaleString('ru-RU')}
                      </strong>
                    </p>
                  )}
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--fc-text-muted)', marginBottom: '6px' }}>
                    Сумма к списанию (по бюджету заказа)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    value={promoPayAmount}
                    onChange={(e) => setPromoPayAmount(e.target.value)}
                    className="fc-input"
                    style={{ marginBottom: '10px', maxWidth: '200px', width: '100%' }}
                  />
                  <button
                    type="button"
                    disabled={promoPayBusy}
                    onClick={() => payPromoFromBalance(viewingOrder.id)}
                    className="fc-btn fc-btn--success fc-btn--block"
                    style={{
                      marginBottom: '8px',
                      opacity: promoPayBusy ? 0.6 : 1
                    }}
                  >
                    {promoPayBusy ? '⏳ Обработка…' : '✅ Оплатить работу'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void refreshWalletBalance()}
                    className="fc-btn fc-btn--dark fc-btn--block fc-btn--sm"
                  >
                    🔄 Обновить баланс (подтянуть Crypto Pay)
                  </button>
                </div>
              )}

              {role === 'executor' && viewingOrder.status === 'awaiting_payment' && (
                <p style={{ color: 'var(--fc-accent-strong)', fontSize: '14px', marginTop: '8px' }}>
                  Заказчик принял работу. Ожидайте оплату: заказчик пополнит баланс в боте и нажмёт
                  «Оплатить работу» в этом заказе — USDT поступят на ваш счёт FreelanCYX.
                </p>
              )}
              
              {((role === 'customer' && viewingOrder.status === 'open') ||
                (canOpenOrderDispute(viewingOrder, role) &&
                  !(role === 'executor' && viewingOrder.status === 'in_progress'))) && (
                <div className="fc-btn-row" style={{ marginTop: '10px' }}>
                  {role === 'customer' && viewingOrder.status === 'open' && (
                      <button
                        type="button"
                        onClick={() => cancelOrder(viewingOrder.id)}
                        className="fc-btn fc-btn--danger fc-btn--block"
                      >
                  ❌ Отменить задание
                </button>
                    )}
                  {canOpenOrderDispute(viewingOrder, role) &&
                    !(role === 'executor' && viewingOrder.status === 'in_progress') && (
                      <button
                        type="button"
                        onClick={() => setDisputeModalOpen(true)}
                        className="fc-btn fc-btn--dark fc-btn--block"
                      >
                        ⚖️ Открыть спор
                      </button>
                    )}
                </div>
              )}

              {role === 'executor' &&
                viewingOrder.status === 'open' &&
                (String(viewingOrder.selected_response_status || '') === 'pending_payment' ||
                  String(viewingOrder.selected_response_status || '') === 'negotiating') &&
                Number(viewingOrder.executor_id) === Number(user?.id) && (
                  <button
                    type="button"
                    onClick={() => cancelByExecutor(viewingOrder.id)}
                    className="fc-btn fc-btn--warn fc-btn--block"
                    style={{ marginTop: '12px' }}
                  >
                    ⚠️ Отказаться от назначения
                  </button>
                )}

              {role === 'executor' &&
                viewingOrder.status === 'open' &&
                !viewingOrder.executor_service_id &&
                !(
                  (String(viewingOrder.selected_response_status || '') === 'pending_payment' ||
                    String(viewingOrder.selected_response_status || '') === 'negotiating') &&
                  Number(viewingOrder.executor_id) === Number(user?.id)
                ) && (
                <button
                  type="button"
                  onClick={() => {
                    setResponseBudgetMode('client');
                    setResponseBudget('');
                    setRespondingTo(viewingOrder);
                    setShowOrderDetails(false);
                    setOrderDetailsCustomerTab('about');
                    setOrderReviewInfo(null);
                    setDisputeModalOpen(false);
                    setDisputeText('');
                  }}
                  className="fc-btn fc-btn--primary fc-btn--block"
                  style={{ marginTop: '12px' }}
                >
                  📝 Откликнуться
                </button>
              )}

              <button
                type="button"
                onClick={() => closeOrderDetails()}
                className="fc-btn fc-btn--secondary"
                style={{ marginTop: '12px' }}
              >
                Назад
              </button>
            </div>
          </div>
              )}

            </div>

            {showCustomerPickExecutorUi ? (
              <div
                role="tablist"
                aria-label="Разделы карточки заказа"
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  gap: 8,
                  paddingTop: 10,
                  paddingBottom: 12,
                  borderTop: '1px solid var(--fc-border)'
                }}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={orderDetailsCustomerTab === 'about'}
                  onClick={() => switchOrderDetailsTab('about')}
                  className={`fc-btn fc-btn--sm${orderDetailsCustomerTab === 'about' ? ' fc-btn--primary' : ' fc-btn--dark'}`}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  О заказе
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={orderDetailsCustomerTab === 'executors'}
                  onClick={() => switchOrderDetailsTab('executors')}
                  className={`fc-btn fc-btn--sm${orderDetailsCustomerTab === 'executors' ? ' fc-btn--primary' : ' fc-btn--dark'}`}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  Выбрать исполнителя{' '}
                  <span style={{ color: 'var(--fc-text-muted)', fontWeight: 700 }}>
                    ({detailResponsesCount}/{responsesLimitShort})
                  </span>
                </button>
              </div>
            ) : null}

          </div>

            {showServiceBudgetModal && viewingOrder?.selected_response_id ? (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.78)',
                  zIndex: 5002,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  boxSizing: 'border-box'
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="service-budget-modal-title"
              >
                <div
                  style={{
                    background: 'var(--fc-card)',
                    borderRadius: 14,
                    padding: 18,
                    maxWidth: 420,
                    width: '100%',
                    border: '1px solid var(--fc-border)',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.45)'
                  }}
                >
                  <h3 id="service-budget-modal-title" style={{ margin: '0 0 10px', fontSize: 17 }}>
                    {budgetModalCounter
                      ? 'Ваша сумма в USDT'
                      : budgetModalKind === 'order'
                        ? 'Новая сумма задания в USDT'
                        : 'Новая сумма услуги в USDT'}
                  </h3>
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
                    {budgetModalCounter
                      ? 'Предложение уйдёт второй стороне на подтверждение. Только работа, без комиссии платформы.'
                      : 'Предложение уйдёт второй стороне на подтверждение. Только работа, без комиссии платформы.'}
                  </p>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={serviceBudgetAmount}
                    onChange={(e) => setServiceBudgetAmount(e.target.value)}
                    autoFocus
                    placeholder="Сумма"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '12px 14px',
                      borderRadius: 10,
                      border: '1px solid var(--fc-input-border)',
                      background: 'var(--fc-input-bg)',
                      color: 'var(--fc-input-text)',
                      fontSize: 16,
                      marginBottom: 14
                    }}
                  />
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--fc-text)', fontWeight: 600 }}>
                    Применить цену:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: 10,
                        borderRadius: 10,
                        border:
                          serviceBudgetScope === 'order'
                            ? '1px solid var(--fc-accent)'
                            : '1px solid var(--fc-border)',
                        background: serviceBudgetScope === 'order' ? 'rgba(var(--fc-accent-rgb), 0.12)' : 'var(--fc-card-hover)',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="radio"
                        name="service-budget-scope"
                        checked={serviceBudgetScope === 'order'}
                        onChange={() => setServiceBudgetScope('order')}
                        style={{ marginTop: 3, accentColor: 'var(--fc-accent)' }}
                      />
                      <span style={{ fontSize: 14, lineHeight: 1.4, color: 'var(--fc-text)' }}>
                        {budgetModalKind === 'order'
                          ? 'Только этому исполнителю'
                          : 'Только этому заказчику'}
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--fc-text-muted)', marginTop: 2 }}>
                          {budgetModalKind === 'order'
                            ? 'Бюджет задания в каталоге останется без изменений'
                            : 'Витрина услуги останется без изменений'}
                        </span>
                      </span>
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: 10,
                        borderRadius: 10,
                        border:
                          serviceBudgetScope === 'service'
                            ? '1px solid var(--fc-accent)'
                            : '1px solid var(--fc-border)',
                        background: serviceBudgetScope === 'service' ? 'rgba(var(--fc-accent-rgb), 0.12)' : 'var(--fc-card-hover)',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="radio"
                        name="service-budget-scope"
                        checked={serviceBudgetScope === 'service'}
                        onChange={() => setServiceBudgetScope('service')}
                        style={{ marginTop: 3, accentColor: 'var(--fc-accent)' }}
                      />
                      <span style={{ fontSize: 14, lineHeight: 1.4, color: 'var(--fc-text)' }}>
                        Для всех
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--fc-text-muted)', marginTop: 2 }}>
                          {budgetModalKind === 'order'
                            ? 'Обновить бюджет задания в каталоге'
                            : 'Обновить цену услуги в каталоге'}
                        </span>
                      </span>
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      className="fc-btn fc-btn--secondary"
                      style={{ flex: 1 }}
                      disabled={promoPayBusy}
                      onClick={() => {
                        setShowServiceBudgetModal(false);
                        setBudgetModalCounter(false);
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="fc-btn fc-btn--primary"
                      style={{ flex: 1 }}
                      disabled={promoPayBusy || !serviceBudgetAmount.trim()}
                      onClick={async () => {
                        const trimmed = serviceBudgetAmount.trim();
                        if (!trimmed || !viewingOrder?.selected_response_id || !token) return;
                        setPromoPayBusy(true);
                        try {
                          const scopePayload =
                            budgetModalKind === 'order'
                              ? serviceBudgetScope === 'service'
                                ? 'listing'
                                : 'order'
                              : serviceBudgetScope;
                          const path = budgetModalCounter
                            ? `/api/responses/${viewingOrder.selected_response_id}/budget-pending/counter`
                            : budgetModalKind === 'order'
                              ? `/api/responses/${viewingOrder.selected_response_id}/update-order-budget`
                              : `/api/responses/${viewingOrder.selected_response_id}/update-service-budget`;
                          const r = await fetch(apiUrl(path), {
                            method: 'POST',
                            headers: {
                              Authorization: `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              amount: trimmed,
                              scope: scopePayload
                            })
                          });
                          const d = await r.json();
                          if (!r.ok) throw new Error(d.error || 'Ошибка');
                          setShowServiceBudgetModal(false);
                          setBudgetModalCounter(false);
                          showAlert(d.message || 'Предложение отправлено');
                          await viewOrderDetails({ id: viewingOrder.id });
                        } catch (e: any) {
                          showAlert(e?.message || 'Не удалось изменить сумму');
                        } finally {
                          setPromoPayBusy(false);
                        }
                      }}
                    >
                      {promoPayBusy ? '…' : budgetModalCounter ? 'Отправить' : 'Предложить'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {showDeadlineReplaceModal && deadlineReplaceInfo?.eligible && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.78)',
                  zIndex: 5001,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  boxSizing: 'border-box'
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="deadline-replace-title"
              >
                <div
                  style={{
                    background: 'var(--fc-card)',
                    borderRadius: 14,
                    padding: 18,
                    maxWidth: 420,
                    width: '100%',
                    border: '1px solid var(--fc-border)',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
                    maxHeight: '90vh',
                    overflow: 'auto'
                  }}
                >
                  <h3 id="deadline-replace-title" style={{ margin: '0 0 10px', fontSize: '17px' }}>
                    Смена исполнителя
                  </h3>
                  {(deadlineReplaceInfo.other_responses || []).length === 0 ? (
                    <>
                      <p style={{ margin: '0 0 12px', fontSize: '14px', color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
                        Других откликов на это задание не было. Задание будет снова в ленте поиска — скоро появятся
                        новые отклики. Текущему исполнителю спишется{' '}
                        <strong>{deadlineReplaceInfo.penalty_xp ?? 20} XP</strong>.
                      </p>
                      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          disabled={deadlineReplaceBusy}
                          onClick={() => void submitDeadlineReplacement()}
                          className="fc-btn fc-btn--primary"
                          style={{ flex: '1 1 140px' }}
                        >
                          {deadlineReplaceBusy ? '…' : 'Вернуть в ленту'}
                        </button>
                        <button
                          type="button"
                          disabled={deadlineReplaceBusy}
                          onClick={() => setShowDeadlineReplaceModal(false)}
                          className="fc-btn fc-btn--secondary"
                          style={{ flex: '1 1 140px' }}
                        >
                          Отмена
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p style={{ margin: '0 0 10px', fontSize: '13px', color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
                        Выберите исполнителя из тех, кто откликался ранее. Новый срок можно задать ниже или оставить
                        пустым — возьмём оценку из отклика. Штраф текущему исполнителю:{' '}
                        <strong>{deadlineReplaceInfo.penalty_xp ?? 20} XP</strong>.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                        {(deadlineReplaceInfo.other_responses || []).map((r: any) => (
                          <label
                            key={r.id}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 10,
                              padding: 10,
                              borderRadius: 10,
                              border:
                                deadlineReplacePickId === r.id
                                  ? '1px solid var(--fc-accent)'
                                  : '1px solid var(--fc-border)',
                              background: deadlineReplacePickId === r.id ? 'rgba(var(--fc-accent-rgb), 0.12)' : 'var(--fc-card-hover)',
                              cursor: 'pointer'
                            }}
                          >
                            <input
                              type="radio"
                              name="deadline-replace-exec"
                              checked={deadlineReplacePickId === r.id}
                              onChange={() => setDeadlineReplacePickId(Number(r.id))}
                              style={{ marginTop: 3 }}
                            />
                            <span style={{ fontSize: '14px', lineHeight: 1.35 }}>
                              <strong>
                                {r.first_name} {r.last_name}
                              </strong>{' '}
                              ⭐ {r.rating != null ? Number(r.rating).toFixed(2) : '—'}
                              {r.eta_value && r.eta_unit ? (
                                <span style={{ color: 'var(--fc-text-muted)' }}>
                                  {' '}
                                  · срок в отклике: {formatDurationValueUnit(r.eta_value, r.eta_unit)}
                                </span>
                              ) : null}
                            </span>
                          </label>
                        ))}
                      </div>
                      <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--fc-text-muted)' }}>
                        Новый дедлайн (необязательно)
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          placeholder="Число"
                          value={dlReplaceValue}
                          onChange={(e) => setDlReplaceValue(e.target.value)}
                          className="fc-input"
                          style={{ width: '88px' }}
                        />
                        <select
                          value={dlReplaceUnit}
                          onChange={(e) =>
                            setDlReplaceUnit(e.target.value as 'hours' | 'days' | 'weeks' | 'months')
                          }
                          className="fc-input"
                          style={{ flex: '1 1 120px' }}
                        >
                          <option value="hours">часов</option>
                          <option value="days">дней</option>
                          <option value="weeks">недель</option>
                          <option value="months">месяцев</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          disabled={deadlineReplaceBusy}
                          onClick={() => void submitDeadlineReplacement()}
                          className="fc-btn fc-btn--primary"
                          style={{ flex: '1 1 140px' }}
                        >
                          {deadlineReplaceBusy ? '…' : 'Назначить выбранного'}
                        </button>
                        <button
                          type="button"
                          disabled={deadlineReplaceBusy}
                          onClick={() => setShowDeadlineReplaceModal(false)}
                          className="fc-btn fc-btn--secondary"
                          style={{ flex: '1 1 140px' }}
                        >
                          Отмена
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {showExtendDeadlineModal && deadlineReplaceInfo?.eligible && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.78)',
                  zIndex: 5002,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  boxSizing: 'border-box'
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="extend-deadline-title"
              >
                <div
                  style={{
                    background: 'var(--fc-card)',
                    borderRadius: 14,
                    padding: 18,
                    maxWidth: 420,
                    width: '100%',
                    border: '1px solid var(--fc-border)',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.45)'
                  }}
                >
                  <h3 id="extend-deadline-title" style={{ margin: '0 0 10px', fontSize: '17px' }}>
                    Продлить время выполнения
                  </h3>
                  <p style={{ margin: '0 0 12px', fontSize: '14px', color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
                    Новый дедлайн считается от <strong>текущего момента</strong>: укажите, на сколько времени даёте
                    исполнителю. Срок в карточке заказа и таймер обновятся. Если снова не уложится — снова можно
                    продлить или сменить исполнителя.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      placeholder="Количество"
                      value={extendDlValue}
                      onChange={(e) => setExtendDlValue(e.target.value)}
                      className="fc-input"
                      style={{ width: '100px' }}
                    />
                    <select
                      value={extendDlUnit}
                      onChange={(e) =>
                        setExtendDlUnit(e.target.value as 'hours' | 'days' | 'weeks' | 'months')
                      }
                      className="fc-input"
                      style={{ flex: '1 1 140px' }}
                    >
                      <option value="hours">часов</option>
                      <option value="days">дней</option>
                      <option value="weeks">недель</option>
                      <option value="months">месяцев</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      disabled={extendDeadlineBusy}
                      onClick={() => void submitExtendDeadline()}
                      className="fc-btn fc-btn--primary"
                      style={{ flex: '1 1 140px' }}
                    >
                      {extendDeadlineBusy ? '…' : 'Продлить'}
                    </button>
                    <button
                      type="button"
                      disabled={extendDeadlineBusy}
                      onClick={() => setShowExtendDeadlineModal(false)}
                      className="fc-btn fc-btn--secondary"
                      style={{ flex: '1 1 140px' }}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              </div>
            )}

            {disputeModalOpen && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.78)',
                  zIndex: 5000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  boxSizing: 'border-box'
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="dispute-modal-title"
              >
                <div
                  style={{
                    background: 'var(--fc-card)',
                    borderRadius: 14,
                    padding: 18,
                    maxWidth: 400,
                    width: '100%',
                    border: '1px solid var(--fc-border)',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.45)'
                  }}
                >
                  <h3 id="dispute-modal-title" style={{ margin: '0 0 8px', fontSize: '18px' }}>
                    Открыть спор
                  </h3>
                  <p style={{ margin: '0 0 10px', fontSize: '14px', color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
                    Опишите проблему спора. Администрация рассмотрит обращение и свяжется с участниками через
                    бота.
                  </p>
                  <textarea
                    value={disputeText}
                    onChange={(e) => setDisputeText(e.target.value)}
                    placeholder="Не менее 10 символов…"
                    rows={5}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      borderRadius: 10,
                      padding: 10,
                      border: '1px solid var(--fc-input-border)',
                      background: 'var(--fc-input-bg)',
                      color: 'var(--fc-input-text)',
                      fontSize: 14,
                      resize: 'vertical',
                      minHeight: 100
                    }}
                  />
                  <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      disabled={disputeBusy}
                      onClick={() => void submitOpenDispute()}
                      className="fc-btn fc-btn--primary"
                      style={{ flex: '1 1 120px' }}
                    >
                      {disputeBusy ? '…' : 'Отправить'}
                    </button>
                    <button
                      type="button"
                      disabled={disputeBusy}
                      onClick={() => {
                        setDisputeModalOpen(false);
                        setDisputeText('');
                      }}
                      className="fc-btn fc-btn--secondary"
                      style={{ flex: '1 1 120px' }}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showAssignExecutorModal && assigningResp && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.78)',
                  zIndex: 5001,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  boxSizing: 'border-box'
                }}
                role="dialog"
                aria-modal="true"
              >
                <div className="fc-card fc-order-card" style={{ width: '100%', maxWidth: 420, padding: 14 }}>
                  <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 6 }}>
                    ✅ Назначить исполнителем
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--fc-text-muted)', lineHeight: 1.35, marginBottom: 12 }}>
                    Исполнитель:{' '}
                    <b style={{ color: 'var(--fc-text)' }}>
                      {assigningResp.first_name} {assigningResp.last_name}
                    </b>
                    <br />
                    После подтверждения нужно будет оплатить работу на эскроу. Чат откроется сразу; работа
                    стартует после оплаты.
                  </div>

                  <div style={{ width: '100%', textAlign: 'left', marginBottom: 10 }}>
                    <label style={{ display: 'block', fontSize: 13, color: 'var(--fc-text-muted)', marginBottom: 6 }}>
                      Срок выполнения
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="number"
                        min={1}
                        placeholder="Количество"
                        value={assignDeadlineValue}
                        onChange={(e) => setAssignDeadlineValue(e.target.value)}
                        className="fc-input"
                        style={{ width: '45%', margin: 0 }}
                      />
                      <select
                        value={assignDeadlineUnit}
                        onChange={(e) =>
                          setAssignDeadlineUnit(e.target.value as 'hours' | 'days' | 'weeks' | 'months')
                        }
                        className="fc-select"
                        style={{ width: '55%', margin: 0, flex: 'none' }}
                      >
                        <option value="hours">Часы</option>
                        <option value="days">Дни</option>
                        <option value="weeks">Недели</option>
                        <option value="months">Месяцы</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="fc-btn fc-btn--primary fc-btn--block"
                    disabled={assignBusy}
                    onClick={async () => {
                      const v = String(assignDeadlineValue || '').trim();
                      const n = Number(v);
                      if (!v || !Number.isFinite(n) || n <= 0) {
                        showAlert('Укажите срок: целое число ≥ 1');
                        return;
                      }
                      try {
                        setAssignBusy(true);
                        await acceptResponse(assigningResp.id, {
                          deadlineValue: v,
                          deadlineUnit: assignDeadlineUnit
                        });
                        setShowAssignExecutorModal(false);
                        setAssigningResp(null);
                      } finally {
                        setAssignBusy(false);
                      }
                    }}
                  >
                    {assignBusy ? 'Подтверждение…' : 'Подтвердить'}
                  </button>
                  <button
                    type="button"
                    className="fc-btn fc-btn--secondary fc-btn--block"
                    onClick={() => {
                      setShowAssignExecutorModal(false);
                      setAssigningResp(null);
                    }}
                    disabled={assignBusy}
                    style={{ marginTop: 8 }}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            )}
        </header>
      </div>
    );
  }

  if (respondingTo) {
    return (
      <div className="App">
        <header className="App-header">
          <h2>Отклик на задание</h2>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <div 
              className="fc-avatar-presence-anchor fc-avatar-presence-anchor--50"
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: 'var(--fc-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '15px',
                cursor: 'pointer',
                fontSize: '20px',
                color: 'white',
                fontWeight: 'bold',
                overflow: 'visible'
              }}
              onClick={() => setViewingProfile(respondingTo.customer_id)}
            >
              {respondingTo.customer_avatar_url ? (
                <img
                  src={respondingTo.customer_avatar_url}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    borderRadius: '50%'
                  }}
                />
              ) : (
                respondingTo.customer_first_name?.[0] || 'З'
              )}
              <PresenceStatusDot lastSeenAt={respondingTo.customer_last_seen_at} />
            </div>
            <div>
              <strong>{respondingTo.customer_first_name} {respondingTo.customer_last_name}</strong>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--fc-text-muted)' }}>Заказчик</p>
            </div>
          </div>
          <h3 style={{ width: '100%', maxWidth: '420px' }}>
            <OrderTitleWithId title={respondingTo.title || 'Заказ'} id={Number(respondingTo.id)} />
          </h3>
          
          <div className="fc-form">
            <p
              style={{
                width: '100%',
                maxWidth: '300px',
                textAlign: 'left',
                fontSize: 15,
                color: 'var(--fc-text)',
                lineHeight: 1.45,
                margin: '0 0 12px'
              }}
            >
              Заказчик предложил{' '}
              <strong>
                {respondingTo.budget != null
                  ? `${formatMicroUsdt(respondingTo.budget)} USDT`
                  : '—'}
              </strong>{' '}
              за выполнение задания.
            </p>

            {role === 'executor' ? (
              <div
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  textAlign: 'left',
                  margin: '0 0 12px',
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: 'var(--fc-text-muted)'
                }}
              >
                {Number(user?.free_executor_fee_waivers_left ?? 0) > 0 ? (
                  <>
                    Сделок <b style={{ color: 'var(--fc-text)' }}>без комиссии</b> осталось:{' '}
                    <b style={{ color: 'var(--fc-text)' }}>{user?.free_executor_fee_waivers_left}</b>.{' '}
                  </>
                ) : (
                  <>
                    Комиссия платформы{' '}
                    <b style={{ color: 'var(--fc-text)' }}>
                      {executorResponseQuota?.platform_commission_percent ?? 10}%
                    </b>{' '}
                    удерживается из бюджета заказа.{' '}
                  </>
                )}
                Откликов за 24 часа осталось:{' '}
                <b style={{ color: 'var(--fc-text)' }}>
                  {executorResponseQuota?.remaining_responses_today != null
                    ? executorResponseQuota.remaining_responses_today
                    : '—'}
                </b>{' '}
                из{' '}
                <b style={{ color: 'var(--fc-text)' }}>
                  {executorResponseQuota?.daily_response_limit ?? 20}
                </b>
                {executorResponseQuota?.executor_vip_active
                  ? ' (VIP).'
                  : ` · с VIP — до ${executorResponseQuota?.executor_vip_max_responses_per_24h ?? 50}.`}
              </div>
            ) : null}

            <div
              style={{
                width: '100%',
                maxWidth: '300px',
                textAlign: 'left',
                marginBottom: 12,
                fontSize: 14,
                color: 'var(--fc-text-muted)'
              }}
            >
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="respBudgetMode"
                  checked={responseBudgetMode === 'client'}
                  onChange={() => {
                    setResponseBudgetMode('client');
                    setResponseBudget('');
                  }}
                />
                <span>Согласен с этой суммой</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="respBudgetMode"
                  checked={responseBudgetMode === 'custom'}
                  onChange={() => setResponseBudgetMode('custom')}
                />
                <span>Предложить свою сумму (USDT)</span>
              </label>
            </div>

            {responseBudgetMode === 'custom' && (
              <input
                type="text"
                inputMode="decimal"
                placeholder={`Мин. ${formatMicroUsdt(minOrderBudgetMicro)} USDT, напр. 10`}
                value={responseBudget}
                onChange={(e) => setResponseBudget(e.target.value)}
                className="fc-input fc-input--full"
              />
            )}

            <textarea
              placeholder="Ваше сообщение заказчику"
              value={responseMessage}
              onChange={(e) => setResponseMessage(e.target.value)}
              className="fc-input fc-input--full"
                style={{ minHeight: '100px' }}
            />
            <div style={{ width: '100%', maxWidth: '300px', textAlign: 'left' }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--fc-text-muted)', marginBottom: 6 }}>
                Срок выполнения (укажите количество и единицу: часы/дни/недели/месяцы)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
                  min={1}
                  placeholder="Количество"
                  value={responseEtaValue}
                  onChange={(e) => setResponseEtaValue(e.target.value)}
                  className="fc-input"
                  style={{ width: '45%', margin: 0 }}
                />
                <select
                  value={responseEtaUnit}
                  onChange={(e) => setResponseEtaUnit(e.target.value as 'hours' | 'days' | 'weeks' | 'months')}
                  className="fc-select"
                  style={{ width: '55%', margin: 0, flex: 'none' }}
                >
                  <option value="hours">Часы</option>
                  <option value="days">Дни</option>
                  <option value="weeks">Недели</option>
                  <option value="months">Месяцы</option>
                </select>
              </div>
            </div>
            
            <div style={{ marginTop: '20px' }}>
              <button onClick={handleCreateResponse} className="fc-btn fc-btn--primary">
                ✅ Отправить отклик
              </button>
              <button 
                type="button"
                onClick={() => {
                  setRespondingTo(null);
                  setResponseBudgetMode('client');
                  setResponseBudget('');
                }}
                className="fc-btn fc-btn--secondary"
              >
                ❌ Отмена
              </button>
            </div>
          </div>
        </header>
      </div>
    );
  }

  if (showCreateOrder) {
    return (
      <div className="App">
        <header className="App-header" style={{ justifyContent: 'flex-start', paddingTop: 12 }}>
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              margin: '0 auto 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              boxSizing: 'border-box'
            }}
          >
            <button
              type="button"
              onClick={() => setShowCreateOrder(false)}
              aria-label="Назад"
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                flexShrink: 0
              }}
            >
              ←
            </button>
            <h2 style={{ margin: 0, flex: 1, textAlign: 'left' }}>Создание задания</h2>
          </div>
          <p
            style={{
              fontSize: 13,
              color: 'var(--fc-text-muted)',
              lineHeight: 1.45,
              textAlign: 'left',
              margin: '0 0 14px',
              maxWidth: 420
            }}
          >
            Размещение бесплатное, оплата — только после выбора исполнителя. Мат и задания, нарушающие закон,
            запрещены — за это блокировка.
          </p>

          <div className="fc-form fc-form--spaced">
            <input
              type="text"
              placeholder="Название"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="fc-input fc-input--full"
            />

            <textarea
              placeholder="Указывайте ТЗ максимально подробно и четко для точного выполнения вашего задания."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="fc-input fc-input--full"
              style={{ minHeight: '100px' }}
            />
            
            <div className="fc-cat-chip-row" role="listbox" aria-label="Категория задания">
              {CATEGORY_OPTIONS.map((o) => (
                <CategoryEmblemChip
                  key={o.value}
                  value={o.value}
                  label={o.label}
                  selected={categorySelect === o.value}
                  onSelect={(v) => {
                    setCategorySelect(v);
                    setSubcategorySelect('');
                    setCustomCategory('');
                  }}
                />
              ))}
              {/* «Прочее» уже есть в CATEGORY_OPTIONS (other) — отдельный чип custom дублировал его */}
            </div>

            {categorySelect && categorySelect !== 'custom' && subcategoryEntriesForMain(categorySelect).filter((s) => s.key !== 'custom').length > 0 && (
              <select
                value={subcategorySelect}
                onChange={(e) => setSubcategorySelect(e.target.value)}
                className="fc-input fc-input--full"
              >
                <option value="">Выберите подкатегорию</option>
                {subcategoryEntriesForMain(categorySelect)
                  .filter((s) => s.key !== 'custom')
                  .map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            )}

            {(categorySelect === 'custom' || categorySelect === 'other') && (
              <input
                type="text"
                placeholder="Своя категория / тема"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                className="fc-input fc-input--full"
              />
            )}
            
            <input
              type="number"
              placeholder={`Бюджет (USDT), мин. ${formatMicroUsdt(minOrderBudgetMicro)}`}
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="fc-input fc-input--full"
 />
            <p className="fc-create-hint">
              Мин. {formatMicroUsdt(minOrderBudgetMicro)} USDT · размещение <strong>бесплатно</strong> · сегодня{' '}
              <strong>
                {user?.customer_orders_today ?? 0} из{' '}
                {user?.customer_daily_orders_limit_effective ??
                  (user?.vip_active
                    ? user?.customer_vip_daily_orders_limit ?? 10
                    : user?.customer_daily_orders_limit ?? 5)}
              </strong>
            </p>

            {/* Продвижение: «Срочно» и «ТОП» — компактные карточки с переключателем.
                Если слотов нет — вместо переключателя кнопка покупки 1 слота. */}
            <div className="fc-boost-opts">
              <label
                className={
                  'fc-boost-opt' +
                  (orderUrgentBoost ? ' fc-boost-opt--on' : '') +
                  ((user?.urgent_slots_left ?? 0) < 1 ? ' fc-boost-opt--empty' : '')
                }
              >
                <span className="fc-boost-opt__icon" aria-hidden>
                  🔥
                </span>
                <span className="fc-boost-opt__body">
                  <span className="fc-boost-opt__title">Срочно</span>
                  <span className="fc-boost-opt__desc">Выше в ленте и во вкладке «Срочные»</span>
                </span>
                {(user?.urgent_slots_left ?? 0) > 0 ? (
                  <span className="fc-boost-opt__side">
                    <span className="fc-boost-opt__count">{user?.urgent_slots_left} шт.</span>
                    <input
                      type="checkbox"
                      className="fc-switch"
                      checked={orderUrgentBoost}
                      onChange={(e) => setOrderUrgentBoost(e.target.checked)}
                    />
                  </span>
                ) : (
                  <button
                    type="button"
                    className="fc-boost-opt__buy"
                    onClick={(e) => {
                      e.preventDefault();
                      void handlePurchaseUrgentPack('1');
                    }}
                  >
                    {user?.urgent_pack_1_price_stars ?? 150} ⭐
                  </button>
                )}
              </label>

              <label
                className={
                  'fc-boost-opt' +
                  (orderTopBoost ? ' fc-boost-opt--on' : '') +
                  ((user?.order_top_slots_left ?? 0) < 1 ? ' fc-boost-opt--empty' : '')
                }
              >
                <span className="fc-boost-opt__icon" aria-hidden>
                  ⭐
                </span>
                <span className="fc-boost-opt__body">
                  <span className="fc-boost-opt__title">ТОП на {user?.order_top_hours ?? 24} ч</span>
                  <span className="fc-boost-opt__desc">Закреплено вверху каталога</span>
                </span>
                {(user?.order_top_slots_left ?? 0) > 0 ? (
                  <span className="fc-boost-opt__side">
                    <span className="fc-boost-opt__count">{user?.order_top_slots_left} шт.</span>
                    <input
                      type="checkbox"
                      className="fc-switch"
                      checked={orderTopBoost}
                      onChange={(e) => setOrderTopBoost(e.target.checked)}
                    />
                  </span>
                ) : (
                  <button
                    type="button"
                    className="fc-boost-opt__buy"
                    onClick={(e) => {
                      e.preventDefault();
                      void handlePurchaseOrderTopPack('1');
                    }}
                  >
                    {user?.order_top_pack_1_price_stars ?? 75} ⭐
                  </button>
                )}
              </label>

              {/* VIP — такая же карточка с кнопкой, чтобы было видно, что на неё можно нажать */}
              <div
                className={'fc-boost-opt fc-boost-opt--vip' + (user?.vip_active ? ' fc-boost-opt--on' : '')}
                role="button"
                tabIndex={0}
                onClick={() => void handlePurchaseCustomerVip()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') void handlePurchaseCustomerVip();
                }}
              >
                <span className="fc-boost-opt__icon" aria-hidden>
                  👑
                </span>
                <span className="fc-boost-opt__body">
                  <span className="fc-boost-opt__title">
                    VIP заказчика
                    {user?.vip_active && user?.vip_expires_at
                      ? ` · до ${new Date(user.vip_expires_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}`
                      : ''}
                  </span>
                  <span className="fc-boost-opt__desc">
                    {user?.customer_vip_daily_orders_limit ?? 10} заданий в день, +{user?.customer_vip_order_top_slots ?? 10} ТОП, +
                    {user?.customer_vip_urgent_slots ?? 3} «Срочно»
                  </span>
                </span>
                <span className="fc-boost-opt__buy">
                  {user?.vip_active ? 'Продлить' : `${user?.customer_vip_month_stars ?? 750} ⭐`}
                </span>
              </div>

              <div className="fc-boost-opts__foot">
                <button type="button" className="fc-boost-opts__link fc-boost-opts__link--muted" onClick={() => setShowBoostShop(true)}>
                  Пакеты по 3, 5 и 10 — в разделе «Буст» ›
                </button>
              </div>
            </div>

            <label style={{ color: 'var(--fc-text)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={hasExecutionDeadline}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setHasExecutionDeadline(checked);
                  if (!checked) {
                    setExecutionDeadlineValue('');
                    setExecutionDeadlineUnit('days');
                  }
                }}
              /> Указать срок выполнения
            </label>

            {hasExecutionDeadline && (
              <div style={{ width: '100%', maxWidth: '300px', textAlign: 'left', marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: 'var(--fc-text-muted)', marginBottom: 6 }}>
                  Укажите количество (часы/дни/недели/месяцы)
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    min={1}
                    placeholder="Количество"
                    value={executionDeadlineValue}
                    onChange={(e) => setExecutionDeadlineValue(e.target.value)}
                    className="fc-input"
                    style={{ width: '45%', margin: 0 }}
                  />
                  <select
                    value={executionDeadlineUnit}
                    onChange={(e) => setExecutionDeadlineUnit(e.target.value as 'hours' | 'days' | 'weeks' | 'months')}
                    className="fc-input"
                    style={{ width: '55%', margin: 0 }}
                  >
                    <option value="hours">Часы</option>
                    <option value="days">Дни</option>
                    <option value="weeks">Недели</option>
                    <option value="months">Месяцы</option>
                  </select>
                </div>
              </div>
            )}

            <div style={{ width: '100%', maxWidth: 320, textAlign: 'left', marginTop: 8 }}>
              <div style={{ fontSize: 13, color: 'var(--fc-text-muted)', marginBottom: 6 }}>
                Лимит откликов исполнителей
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={responsesLimit}
                  onChange={(e) => setResponsesLimit(e.target.value)}
                  className="fc-input"
                  style={{ flex: '0 0 170px', minWidth: 0 }}
                >
                  <option value="">По умолчанию (без лимита)</option>
                  <option value="1">1 отклик</option>
                  <option value="5">5 откликов</option>
                  <option value="10">10 откликов</option>
                  <option value="15">15 откликов</option>
                  <option value="20">20 откликов</option>
                  <option value="25">25 откликов</option>
                  <option value="30">30 откликов</option>
                  <option value="35">35 откликов</option>
                </select>
                <button
                  type="button"
                  className="fc-btn fc-btn--secondary fc-btn--sm"
                  onClick={() => setResponsesLimit('')}
                  style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
                >
                  Безлимитно
                </button>
              </div>
              <p style={{ fontSize: 11, color: 'var(--fc-text-muted)', margin: '4px 0 0' }}>
                Когда лимит достигнут, новые отклики на это задание будут недоступны.
              </p>
            </div>

            <div className="fc-btn-row" style={{ marginTop: '16px' }}>
              <button type="button" onClick={handleCreateOrder} className="fc-btn fc-btn--primary fc-btn--block">
                ✅ Создать
              </button>
              <button type="button" onClick={() => setShowCreateOrder(false)} className="fc-btn fc-btn--secondary fc-btn--block">
                ❌ Отмена
              </button>
            </div>
          </div>
        </header>
      </div>
    );
  }

  if (showResponses && selectedOrder) {
    return (
      <div className="App">
        <header className="App-header">
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto 10px',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <button
              type="button"
              onClick={() => setShowResponses(false)}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                flexShrink: 0
              }}
            >
              ←
            </button>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Отклики на задание</h2>
              <div style={{ fontSize: 13, color: 'var(--fc-text-muted)', marginTop: 2 }}>
                {selectedOrder?.title ? String(selectedOrder.title) : 'Заказ'}{' '}
                {orderRefLabel(Number(selectedOrder.id))}
              </div>
            </div>
          </div>
          
          {responses.length === 0 ? (
            <p>Пока нет откликов</p>
          ) : (
            responses.map((resp) => (
                  <div 
                key={resp.id}
                className="fc-card fc-order-card"
                    style={{
                  padding: '12px',
                  margin: '8px auto',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'stretch'
                }}
              >
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div
                      className="fc-avatar-presence-anchor"
                      style={{
                        width: '34px',
                        height: '34px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--fc-accent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                        fontSize: '14px',
                      color: 'white',
                        fontWeight: 800,
                        overflow: 'visible',
                        flexShrink: 0
                    }}
                    onClick={() => setViewingProfile(resp.executor_id)}
                      title="Профиль исполнителя"
                    >
                      {resp.avatar_url ? (
                        <img
                          src={resp.avatar_url}
                          alt=""
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            borderRadius: '50%'
                          }}
                        />
                      ) : (
                        <>
                          {resp.first_name?.[0]}
                          {resp.last_name?.[0]}
                        </>
                      )}
                      <PresenceStatusDot lastSeenAt={resp.executor_last_seen_at} />
                  </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.25 }}>
                        {resp.first_name} {resp.last_name}{' '}
                        <span style={{ fontWeight: 600, color: 'var(--fc-text-muted)' }}>⭐ {resp.rating}</span>
                  </div>
                      <div style={{ fontSize: 12, color: 'var(--fc-text-muted)', marginTop: 2 }}>
                        {responseStatusLabel(resp.status)}
                </div>
                    </div>
                  </div>

                  {resp.message ? (
                    <div style={{ fontSize: 13, lineHeight: 1.35, color: 'var(--fc-text)', marginTop: 4 }}>
                      {resp.message}
                    </div>
                  ) : null}

                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.35 }}>
                    {resp.proposed_budget != null &&
                    resp.proposed_budget !== '' &&
                    Number(resp.proposed_budget) > 0 ? (
                      <>
                        💰 Предлагает: <b style={{ color: 'var(--fc-text)' }}>{formatMicroUsdt(resp.proposed_budget)} USDT</b>
                        {selectedOrder?.budget != null ? (
                          <span style={{ color: 'var(--fc-text-muted)' }}>
                            {' '}
                            (в заказе {formatMicroUsdt(selectedOrder.budget)} USDT)
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <>
                        💰 Бюджет: <b style={{ color: 'var(--fc-text)' }}>{selectedOrder?.budget != null ? `${formatMicroUsdt(selectedOrder.budget)} USDT` : '—'}</b>
                      </>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 150 }}>
                  <button 
                    type="button"
                    className="fc-btn fc-btn--dark fc-btn--sm"
                    onClick={() => setViewingProfile(resp.executor_id)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    👤 Профиль
                  </button>
                  {resp.status === 'pending' ? (
                    <>
                      <button
                        type="button"
                        className="fc-btn fc-btn--primary fc-btn--sm"
                        onClick={() => {
                          setAssigningResp(resp);
                          setAssignDeadlineValue(resp?.eta_value != null ? String(resp.eta_value) : '');
                          setAssignDeadlineUnit((resp?.eta_unit as any) || 'days');
                          setShowAssignExecutorModal(true);
                        }}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        ✅ Назначить
                      </button>
                      <button
                        type="button"
                        className="fc-btn fc-btn--muted fc-btn--sm"
                        onClick={() => rejectResponse(resp.id, resp.order_id)}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        ❌ Отказать
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))
          )}

          {showAssignExecutorModal && assigningResp && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.78)',
                zIndex: 5001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                boxSizing: 'border-box'
              }}
              role="dialog"
              aria-modal="true"
            >
              <div className="fc-card fc-order-card" style={{ width: '100%', maxWidth: 420, padding: 14 }}>
                <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 6 }}>
                  ✅ Назначить исполнителем
                </div>
                <div style={{ fontSize: 13, color: 'var(--fc-text-muted)', lineHeight: 1.35, marginBottom: 12 }}>
                  Исполнитель: <b style={{ color: 'var(--fc-text)' }}>{assigningResp.first_name} {assigningResp.last_name}</b>
                  <div style={{ marginTop: 8, fontSize: 13, color: 'var(--fc-text-muted)', lineHeight: 1.35 }}>
                    После подтверждения нужно будет оплатить работу на эскроу. Чат откроется сразу; работа
                    стартует после оплаты.
                  </div>
                </div>

                <div style={{ width: '100%', textAlign: 'left', marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 13, color: 'var(--fc-text-muted)', marginBottom: 6 }}>
                    Срок выполнения
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="number"
                      min={1}
                      placeholder="Количество"
                      value={assignDeadlineValue}
                      onChange={(e) => setAssignDeadlineValue(e.target.value)}
                      className="fc-input"
                      style={{ width: '45%', margin: 0 }}
                    />
                    <select
                      value={assignDeadlineUnit}
                      onChange={(e) =>
                        setAssignDeadlineUnit(e.target.value as 'hours' | 'days' | 'weeks' | 'months')
                      }
                      className="fc-select"
                      style={{ width: '55%', margin: 0, flex: 'none' }}
                    >
                      <option value="hours">Часы</option>
                      <option value="days">Дни</option>
                      <option value="weeks">Недели</option>
                      <option value="months">Месяцы</option>
                    </select>
                  </div>
                </div>
          
          <button 
                  type="button"
                  className="fc-btn fc-btn--primary fc-btn--block"
                  disabled={assignBusy}
                  onClick={async () => {
                    const v = String(assignDeadlineValue || '').trim();
                    const n = Number(v);
                    if (!v || !Number.isFinite(n) || n <= 0) {
                      showAlert('Укажите срок: целое число ≥ 1');
                      return;
                    }
                    try {
                      setAssignBusy(true);
                      await acceptResponse(assigningResp.id, {
                        deadlineValue: v,
                        deadlineUnit: assignDeadlineUnit
                      });
                      setShowAssignExecutorModal(false);
                      setAssigningResp(null);
                    } finally {
                      setAssignBusy(false);
                    }
                  }}
                >
                  {assignBusy ? 'Подтверждение…' : 'Подтвердить'}
          </button>
                <button
                  type="button"
                  className="fc-btn fc-btn--secondary fc-btn--block"
                  onClick={() => {
                    setShowAssignExecutorModal(false);
                    setAssigningResp(null);
                  }}
                  disabled={assignBusy}
                  style={{ marginTop: 8 }}
                >
                  Отмена
                </button>
              </div>
            </div>
          )}
        </header>
      </div>
    );
  }

  if (showFindExecutor) {
    const list = executorResults || [];
    return (
      <div className="App">
        <header className="App-header" style={{ justifyContent: 'flex-start', paddingTop: 12 }}>
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              margin: '0 auto 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              boxSizing: 'border-box'
            }}
          >
            <button
              type="button"
              onClick={() => {
                setShowFindExecutor(false);
                setOfferOrder(null);
              }}
              aria-label="Назад"
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                flexShrink: 0
              }}
            >
              ←
            </button>
            <h2 style={{ margin: 0, flex: 1, textAlign: 'left' }}>🔎 Найти исполнителя</h2>
          </div>

          <div style={{ width: '100%', maxWidth: '420px' }}>
            {offerOrder ? (
              <div className="fc-card fc-order-card" style={{ padding: '12px' }}>
                <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                  Задание:{' '}
                  {offerOrder?.title
                    ? `${offerOrder.title} ${offerOrder?.id != null ? orderRefLabel(offerOrder.id) : ''}`
                    : offerOrder?.id != null
                      ? orderRefLabel(offerOrder.id)
                      : ''}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--fc-text-muted)' }}>
                  Категория: {offerOrder?.category != null ? orderCategoryLabel(offerOrder.category) : '—'} · Бюджет:{' '}
                  {offerOrder?.budget != null ? `${formatMicroUsdt(offerOrder.budget)} USDT` : '—'}
                </div>
              </div>
            ) : (
              <div className="fc-card fc-order-card" style={{ padding: '12px', fontSize: '13px', color: 'var(--fc-text-muted)' }}>
                Каталог исполнителей. Укажите категорию и имя или нажмите «Искать».
              </div>
            )}

            <div className="fc-card fc-order-card" style={{ padding: '12px' }}>
              <select
                value={executorCategory}
                onChange={(e) => setExecutorCategory(e.target.value)}
                className="fc-input fc-input--full"
              >
                <option value="">Все категории</option>
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                value={executorQuery}
                onChange={(e) => setExecutorQuery(e.target.value)}
                placeholder="Поиск по имени"
                className="fc-input fc-input--full"
                style={{ marginTop: '10px' }}
              />
              <button type="button" onClick={() => void searchExecutors()} className="fc-btn fc-btn--primary" style={{ marginTop: '10px' }}>
                🔍 Искать
              </button>
            </div>

            {executorSearchLoading ? (
              <p>Загрузка…</p>
            ) : list.length === 0 ? (
              <p>Ничего не найдено</p>
            ) : (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {list.map((ex: any) => {
                  const name = [ex.first_name, ex.last_name].filter(Boolean).join(' ') || 'Исполнитель';
                  return (
                    <div
                      key={ex.id}
                      className="fc-card fc-order-card"
                      style={{
                        position: 'relative',
                        borderRadius: '14px',
                        padding: '12px',
                        margin: 0
                      }}
                    >
                      <button
                        type="button"
                        className={`mk-order-card__fav${executorFavoriteIds[Number(ex.id)] ? ' mk-order-card__fav--on' : ''}`}
                        disabled={executorFavoriteBusyId === Number(ex.id)}
                        title={executorFavoriteIds[Number(ex.id)] ? 'Убрать из избранного' : 'В избранное'}
                        aria-label={executorFavoriteIds[Number(ex.id)] ? 'Убрать из избранного' : 'В избранное'}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void toggleExecutorFavorite(Number(ex.id));
                        }}
                      >
                        <FavoriteHeartIcon filled={!!executorFavoriteIds[Number(ex.id)]} />
                      </button>

                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', paddingRight: '38px' }}>
                        <div
                          className="fc-avatar-presence-anchor"
                          onClick={() => setViewingProfile(ex.id)}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--fc-accent)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            color: 'var(--fc-on-accent)',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            overflow: 'visible',
                            flexShrink: 0
                          }}
                          aria-hidden
                        >
                          {ex.avatar_url ? (
                            <img
                              src={ex.avatar_url}
                              alt="avatar"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                borderRadius: '50%'
                              }}
                            />
                          ) : (
                            <>
                              {String(ex.first_name || 'И')[0]}
                              {String(ex.last_name || '')[0]}
                            </>
                          )}
                          <PresenceStatusDot lastSeenAt={ex.last_seen_at} />
                        </div>

                        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: '15px',
                              color: 'var(--fc-text)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {name}
                          </div>
                          <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--fc-text-muted)' }}>
                            ⭐ {ex.rating != null ? Number(ex.rating).toFixed(1) : '—'} · оценок{' '}
                            {ex.total_ratings ?? 0}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                        <button
                          type="button"
                          onClick={() => sendOfferToExecutor(Number(ex.id))}
                          disabled={offerBusyId === Number(ex.id)}
                          className="fc-btn fc-btn--primary fc-btn--sm"
                          style={{ flex: 1, margin: 0, opacity: offerBusyId === Number(ex.id) ? 0.7 : 1 }}
                        >
                          🎯 Предложить
                        </button>
                        <button
                          type="button"
                          onClick={() => setViewingProfile(ex.id)}
                          className="fc-btn fc-btn--accent2 fc-btn--sm"
                          style={{ flex: 1, margin: 0 }}
                        >
                          👤 Профиль
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
          )}
          </div>
          
          <button 
            type="button"
            onClick={() => {
              setShowFindExecutor(false);
              setOfferOrder(null);
            }}
            className="fc-btn fc-btn--secondary"
            style={{ marginTop: '20px' }}
          >
            Назад
          </button>
        </header>
      </div>
    );
  }

  if (showMyArchivedCustomerOrders) {
    const filtered = myArchivedCustomerOrders.filter((o: any) => {
      const catOk = !myArchivedCustomerCategory || String(o.category || '') === myArchivedCustomerCategory;
      const q = myArchivedCustomerQuery.trim().toLowerCase();
      const qOk =
        !q ||
        String(o.title || '').toLowerCase().includes(q) ||
        String(o.description || '').toLowerCase().includes(q);
      return catOk && qOk;
    });
    return (
      <div className="App">
        <header className="App-header" style={{ justifyContent: 'flex-start', paddingTop: '12px' }}>
                  <div 
                    style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto 10px',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <button
              type="button"
              onClick={() => setShowMyArchivedCustomerOrders(false)}
              style={{
                width: '44px',
                height: '44px',
                      borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
              }}
            >
              ←
            </button>
            <h2 style={{ margin: 0 }}>📦 Архив заданий</h2>
                  </div>

          <div style={{ width: '100%', maxWidth: '420px' }}>
            <div className="fc-filter-row fc-card fc-order-card" style={{ padding: '12px' }}>
              <select
                value={myArchivedCustomerCategory}
                onChange={(e) => setMyArchivedCustomerCategory(e.target.value)}
                className="fc-select"
                style={{ flex: '0 0 170px', width: '170px', minWidth: 0, height: '44px' }}
              >
                <option value="">Все категории</option>
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                value={myArchivedCustomerQuery}
                onChange={(e) => setMyArchivedCustomerQuery(e.target.value)}
                placeholder="Поиск по названию или описанию"
                className="fc-input"
                style={{ flex: 1, minWidth: 0, height: '44px' }}
              />
                </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon="📦" title="В архиве пока нет заданий" />
          ) : (
            <div style={{ width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filtered.map((order) => (
                <div
                  key={order.id}
                  style={{
                    background: 'linear-gradient(160deg, #2a2a34 0%, #1e1e26 100%)',
                    borderRadius: '14px',
                    padding: '12px',
                    border: '1px solid rgba(255,255,255,0.07)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'stretch'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', width: '100%' }}>
                      <OrderTitleWithId title={order.title || 'Заказ'} id={Number(order.id)} />
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: 'var(--fc-text-muted)' }}>
                      Категория: <b style={{ color: 'var(--fc-text)' }}>{orderCategoryLabel(order.category)}</b>
                      {' · '}
                      Бюджет:{' '}
                      <b style={{ color: 'var(--fc-text)' }}>
                        {order.budget != null ? `${formatMicroUsdt(order.budget)} USDT` : '—'}
                      </b>
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: 'var(--fc-text-muted)' }}>
                      Статус:{' '}
                      <b style={{ color: 'var(--fc-text)' }}>
                        {order.status === 'finished'
                          ? '🎉 Завершено'
                          : order.status === 'cancelled'
                            ? '❌ Отменено'
                            : order.status}
                      </b>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '160px' }}>
                    <button type="button" onClick={() => viewOrderDetails(order)} className="fc-btn fc-btn--dark fc-btn--mt0">
                      👁️ Подробнее
                </button>
              </div>
                </div>
              ))}
            </div>
          )}
        </header>
      </div>
    );
  }

  if (showMyActiveOrders) {
    const filteredActive = myActiveOrders.filter((o: any) => {
      const catOk = !myActiveOrdersCategory || String(o.category || '') === myActiveOrdersCategory;
      const q = myActiveOrdersQuery.trim().toLowerCase();
      const qOk =
        !q ||
        String(o.title || '').toLowerCase().includes(q) ||
        String(o.description || '').toLowerCase().includes(q);
      return catOk && qOk;
    });
    return (
      <>
        {mainTabBar}
        {dealsSegmentBar}
      <div className="App">
        <header
          className="App-header"
          style={{
            justifyContent: 'flex-start',
            padding: '12px 0 12px',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto 10px',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0
            }}
          >
            <button
              type="button"
              onClick={() => {
                setShowMyActiveOrders(false);
                void refreshHomePreview();
              }}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
              }}
            >
              ←
            </button>
            <h2 style={{ margin: 0, flex: 1 }}>⚡ Мои активные</h2>
            <button
              type="button"
              className="fc-btn fc-btn--ghost"
              style={{ fontSize: 13, padding: '8px 10px' }}
              onClick={() => {
                setShowMyActiveOrders(false);
                void loadMyArchivedOrders();
              }}
            >
              Архив
            </button>
          </div>

          <div
            className="fc-filter-row"
            style={{ maxWidth: '420px', margin: '0 auto 10px', padding: '0 8px', flexShrink: 0 }}
          >
            <select
              value={myActiveOrdersCategory}
              onChange={(e) => setMyActiveOrdersCategory(e.target.value)}
              className="fc-select"
              style={{ flex: '0 0 170px', width: '170px', margin: 0 }}
            >
              <option value="">Все категории</option>
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              value={myActiveOrdersQuery}
              onChange={(e) => setMyActiveOrdersQuery(e.target.value)}
              placeholder="Поиск…"
              className="fc-input"
              style={{ flex: 1, minWidth: 0, margin: 0 }}
            />
          </div>

          {filteredActive.length === 0 ? (
            <EmptyState
              icon="🗂️"
              title="У вас нет активных заданий"
              subtitle="Здесь появятся сделки, как только вы начнёте работу с исполнителем."
            />
          ) : (
            <div
              className="fc-compact-order-list"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 'max(24px, env(safe-area-inset-bottom))'
              }}
            >
              {filteredActive.map((order) => {
                const deadlineMeta = order.execution_deadline_at
                  ? `до дедлайна ${formatOrderDeadlineRemaining(order.execution_deadline_at)}`
                  : order.execution_deadline_value && order.execution_deadline_unit
                    ? `срок ${formatDurationValueUnit(order.execution_deadline_value, order.execution_deadline_unit)}`
                    : 'срок не указан';
                const customerName = displayNameUser({
                  first_name: order.customer_first_name,
                  last_name: order.customer_last_name
                });
                return (
                  <CompactOrderListCard
                    key={order.id}
                    order={order}
                    metaLine={
                      <>
                        {customerName}
                        {' · '}
                        {orderStatusLabel(order.status)}
                        {' · '}
                        {deadlineMeta}
                        {order.is_urgent ? ' · 🔥 срочно' : null}
                      </>
                    }
                    onOpenDetails={() => viewOrderDetails(order)}
                    onOpenProfile={
                      order.customer_id ? () => setViewingProfile(order.customer_id) : undefined
                    }
                  />
                );
              })}
            </div>
          )}
        </header>
      </div>
      </>
    );
  }

  if (showMyArchivedOrders) {
    const filtered = myArchivedOrders.filter((o: any) => {
      const catOk = !myArchivedOrdersCategory || String(o.category || '') === myArchivedOrdersCategory;
      const q = myArchivedOrdersQuery.trim().toLowerCase();
      const qOk = !q || String(o.title || '').toLowerCase().includes(q) || String(o.description || '').toLowerCase().includes(q);
      return catOk && qOk;
    });
    return (
      <div className="App">
        <header className="App-header" style={{ justifyContent: 'flex-start', paddingTop: '12px' }}>
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto 10px',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <button
              type="button"
              onClick={() => setShowMyArchivedOrders(false)}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
              }}
            >
              ←
            </button>
            <h2 style={{ margin: 0 }}>📦 Архив заданий</h2>
          </div>

          <div style={{ width: '100%', maxWidth: '420px' }}>
            <div className="fc-filter-row fc-card fc-order-card" style={{ padding: '12px' }}>
              <select
                value={myArchivedOrdersCategory}
                onChange={(e) => setMyArchivedOrdersCategory(e.target.value)}
                className="fc-select"
                style={{ flex: '0 0 170px', width: '170px', minWidth: 0, height: '44px' }}
              >
                <option value="">Все категории</option>
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                value={myArchivedOrdersQuery}
                onChange={(e) => setMyArchivedOrdersQuery(e.target.value)}
                placeholder="Поиск по названию или описанию"
                className="fc-input"
                style={{ flex: 1, minWidth: 0, height: '44px' }}
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon="📦" title="В архиве пока нет заданий" />
          ) : (
            <div style={{ width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filtered.map((order) => (
                  <div 
                  key={order.id}
                    style={{
                    background: 'linear-gradient(160deg, #2a2a34 0%, #1e1e26 100%)',
                    borderRadius: '14px',
                    padding: '12px',
                    border: '1px solid rgba(255,255,255,0.07)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'stretch'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', width: '100%' }}>
                      <OrderTitleWithId title={order.title || 'Заказ'} id={Number(order.id)} />
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: 'var(--fc-text-muted)' }}>
                      Категория: <b style={{ color: 'var(--fc-text)' }}>{orderCategoryLabel(order.category)}</b>
                      {' · '}
                      Бюджет:{' '}
                      <b style={{ color: 'var(--fc-text)' }}>
                        {order.budget != null ? `${formatMicroUsdt(order.budget)} USDT` : '—'}
                      </b>
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: 'var(--fc-text-muted)' }}>
                      Статус:{' '}
                      <b style={{ color: 'var(--fc-text)' }}>
                        {order.status === 'finished'
                          ? '🎉 Завершено'
                          : order.status === 'cancelled'
                            ? '❌ Отменено'
                            : order.status}
                      </b>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '160px' }}>
                    <button type="button" onClick={() => viewOrderDetails(order)} className="fc-btn fc-btn--dark fc-btn--mt0">
                      👁️ Подробнее
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </header>
      </div>
    );
  }

  if (showPendingOrders) {
    const q = pendingOrdersQuery.trim().toLowerCase();
    const filtered = pendingOrders.filter((o) => {
      const catOk = !pendingOrdersCategory || String(o.category || '') === pendingOrdersCategory;
      if (!catOk) return false;
      if (!q) return true;
      const hay = `${o.title || ''} ${o.description || ''} ${o.category || ''}`.toLowerCase();
      return hay.includes(q);
    });

    return (
      <div className="App">
        <header
          className="App-header"
          style={{
            justifyContent: 'flex-start',
            padding: '18px 0 12px',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0
            }}
          >
            <button
              type="button"
              onClick={() => setShowPendingOrders(false)}
              style={{
                width: '44px',
                height: '44px',
                      borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
              }}
            >
              ←
            </button>
            <h2 style={{ margin: 0 }}>⏳ Задания в ожидании</h2>
                  </div>

          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              margin: '6px auto 10px',
              display: 'flex',
              gap: '6px',
              padding: '0 8px',
              boxSizing: 'border-box',
              alignItems: 'stretch',
              flexShrink: 0
            }}
          >
            <select
              value={pendingOrdersCategory}
              onChange={(e) => setPendingOrdersCategory(e.target.value)}
              style={{
                flex: '0 1 150px',
                minWidth: 0,
                margin: 0,
                padding: '10px 8px',
                borderRadius: '8px',
                border: '1px solid var(--fc-input-border)',
                fontSize: '14px',
                background: 'var(--fc-input-bg)',
                color: 'var(--fc-input-text)',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Все категории</option>
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              value={pendingOrdersQuery}
              onChange={(e) => setPendingOrdersQuery(e.target.value)}
              placeholder="Поиск…"
              style={{
                flex: 1,
                minWidth: 0,
                margin: 0,
                padding: '10px 10px',
                borderRadius: '8px',
                border: '1px solid var(--fc-input-border)',
                fontSize: '14px',
                background: 'var(--fc-input-bg)',
                color: 'var(--fc-input-text)',
                boxSizing: 'border-box'
              }}
            />
                </div>

          {filtered.length === 0 ? (
            <EmptyState icon="⏳" title="Нет заданий в ожидании" />
          ) : (
            <div
              className="fc-compact-order-list"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 'max(24px, env(safe-area-inset-bottom))'
              }}
            >
              {filtered.map((order) => {
                const deadlineLine =
                  order.execution_deadline_value && order.execution_deadline_unit
                    ? `срок ${formatDurationValueUnit(order.execution_deadline_value, order.execution_deadline_unit)}`
                    : 'срок не указан';
                const customerName = displayNameUser({
                  first_name: order.customer_first_name,
                  last_name: order.customer_last_name
                });
                const rating =
                  order.customer_rating != null && !Number.isNaN(Number(order.customer_rating))
                    ? Number(order.customer_rating).toFixed(1)
                    : '—';
                const awaitingEscrow =
                  order.my_response_status === 'pending_payment' ||
                  String(order.selected_response_status || '') === 'pending_payment';
                const waitingConfirm =
                  !awaitingEscrow && order.my_response_status === 'pending';

                return (
                  <CompactOrderListCard
                    key={order.id}
                    order={order}
                    budgetMicro={orderDealBudgetMicro(order)}
                    metaLine={
                      <>
                        {customerName} · ⭐ {rating}
                        {' · '}
                        {deadlineLine}
                      </>
                    }
                    statusAccent={
                      awaitingEscrow ? (
                        <span style={{ color: 'var(--fc-accent-strong)', fontWeight: 700 }}>ожидает оплаты эскроу</span>
                      ) : waitingConfirm ? (
                        <span style={{ color: 'var(--fc-text-muted)', fontWeight: 650 }}>ждём подтверждения</span>
                      ) : null
                    }
                    onOpenDetails={() => viewOrderDetails(order)}
                    onOpenProfile={
                      order.customer_id ? () => setViewingProfile(order.customer_id) : undefined
                    }
                  />
                );
              })}
            </div>
          )}
        </header>
      </div>
    );
  }

  if (showMyOffers) {
    return (
      <div className="App">
        <header
          className="App-header"
          style={{
            justifyContent: 'flex-start',
            padding: '12px 0 12px',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto 10px',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0
            }}
          >
                <button 
              type="button"
              onClick={() => setShowMyOffers(false)}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
              }}
            >
              ←
                </button>
            <h2 style={{ margin: 0 }}>⏳ Мои предложения</h2>
              </div>

          {myOffers.length === 0 ? (
            <p style={{ padding: '0 16px', color: 'var(--fc-text-muted)' }}>
              Пока нет предложений. Откройте задание и нажмите «Предложить» исполнителю.
            </p>
          ) : (
            <div
              className="fc-compact-order-list"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 'max(24px, env(safe-area-inset-bottom))'
              }}
            >
              {myOffers.map((offer) => {
                const orderLike = {
                  id: offer.order_id,
                  title: offer.order_title,
                  description: offer.order_description,
                  category: offer.order_category,
                  budget: offer.order_budget,
                  status: offer.order_status,
                  is_urgent: offer.order_is_urgent
                };
                const executorName = displayNameUser({
                  first_name: offer.executor_first_name,
                  last_name: offer.executor_last_name
                });
                const rating =
                  offer.executor_rating != null && !Number.isNaN(Number(offer.executor_rating))
                    ? Number(offer.executor_rating).toFixed(1)
                    : '—';
                const pending = String(offer.offer_status) === 'pending';
                return (
                  <CompactOrderListCard
                    key={offer.offer_id}
                    order={orderLike}
                    metaLine={
                      <>
                        {executorName} · ⭐ {rating}
                        {offer.order_is_urgent ? ' · 🔥 срочно' : null}
                      </>
                    }
                    statusAccent={
                      <span
                        style={{
                          color: pending ? 'var(--fc-accent-strong)' : offer.offer_status === 'accepted' ? 'var(--fc-usdt)' : 'var(--fc-text-muted)',
                          fontWeight: 700
                        }}
                      >
                        {offerStatusLabel(offer.offer_status)}
                      </span>
                    }
                    onOpenDetails={() => viewOrderDetails({ id: offer.order_id })}
                    onOpenProfile={
                      offer.executor_id ? () => setViewingProfile(offer.executor_id) : undefined
                    }
                    detailsLabel="К заданию"
                  />
                );
              })}
            </div>
          )}
        </header>
      </div>
    );
  }

  if (showLegalTerms) {
    return (
      <div className="App">
        <header
          className="App-header fc-legal-screen"
          style={{
            justifyContent: 'flex-start',
            padding: '12px 10px 24px',
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            boxSizing: 'border-box'
          }}
        >
          <div className="fc-legal-screen__top">
            <button
              type="button"
              className="fc-legal-screen__back"
              onClick={() => setShowLegalTerms(false)}
              aria-label="Назад"
            >
              ←
            </button>
            <h2 className="fc-legal-screen__title">📄 Условия сервиса</h2>
          </div>
          <div className="fc-legal-screen__body">
            <p className="fc-legal-screen__hint">
              Документы FreelanCYX (версия {legalDocsVersion}
              {legalDocs.length ? `, ${legalDocs.length} шт.` : ''}). В каждом — русский и
              английский текст.
            </p>
            {legalDocs.length ? (
              <ul className="fc-legal-doc-list">
                {legalDocs.map((doc) => (
                  <li key={doc.id}>
                    <button
                      type="button"
                      className="fc-legal-doc-item"
                      onClick={() => openExternalLink(doc.url)}
                    >
                      <span className="fc-legal-doc-item__title">{doc.title_ru}</span>
                      {doc.title_en ? (
                        <span className="fc-legal-doc-item__sub">{doc.title_en}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="fc-legal-screen__empty">Документы временно недоступны. Напишите в поддержку.</p>
            )}
          </div>
        </header>
      </div>
    );
  }

  if (showLeaderboard) {
    const isReferralsBoard = leaderboardTabRole === 'referrals';
    return (
      <div className="App">
        <header
          className="App-header fc-leaderboard-screen"
          style={{
            justifyContent: 'flex-start',
            padding: '12px 10px 12px',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
                  <div 
                    style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto 10px',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0
            }}
          >
            <button
              type="button"
              onClick={() => setShowLeaderboard(false)}
              style={{
                width: '44px',
                height: '44px',
                      borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                flexShrink: 0
              }}
            >
              ←
            </button>
            <h2 style={{ margin: 0, flex: '1 1 auto', minWidth: 0 }}>🏆 Таблица лидеров</h2>
            <div
              style={{
                flexShrink: 0,
                maxWidth: '52%',
                textAlign: 'right',
                fontSize: '12px',
                fontWeight: 800,
                color: 'var(--fc-accent)',
                lineHeight: 1.25
              }}
            >
              {!leaderboardLoading && !leaderboardError ? (
                leaderboardViewer && leaderboardViewer.rank != null ? (
                  <>Вы на {leaderboardViewer.rank} месте</>
                ) : (
                  <>{isReferralsBoard ? 'Пока без места' : 'Нет в таблице за период'}</>
                )
              ) : (
                '…'
              )}
                  </div>
                </div>

          <div className="fc-leaderboard-sticky fc-leaderboard-sticky--under-title">
            <div className="fc-leaderboard-sticky__label">
              {isReferralsBoard ? 'Ваш реферальный прогресс' : 'Ваш результат за период'}
            </div>
            {!isReferralsBoard ? (
              <div
                className="home-favorites-segment fc-leaderboard-sticky__period"
                role="tablist"
                aria-label="Период для вашего результата"
              >
                  <button 
                  type="button"
                  role="tab"
                  aria-selected={leaderboardPeriod === 'month'}
                  className={`home-favorites-segment__btn${leaderboardPeriod === 'month' ? ' home-favorites-segment__btn--active' : ''}`}
                  onClick={() => setLeaderboardPeriod('month')}
                >
                  Месяц
                  </button>
                  <button 
                  type="button"
                  role="tab"
                  aria-selected={leaderboardPeriod === 'year'}
                  className={`home-favorites-segment__btn${leaderboardPeriod === 'year' ? ' home-favorites-segment__btn--active' : ''}`}
                  onClick={() => setLeaderboardPeriod('year')}
                >
                  Год
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={leaderboardPeriod === 'all'}
                  className={`home-favorites-segment__btn${leaderboardPeriod === 'all' ? ' home-favorites-segment__btn--active' : ''}`}
                  onClick={() => setLeaderboardPeriod('all')}
                >
                  Всё время
                  </button>
                </div>
            ) : leaderboardPrizeHint ? (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--fc-accent)', lineHeight: 1.35 }}>
                {leaderboardPrizeHint}
              </p>
            ) : null}
            {leaderboardLoading ? (
              <p className="fc-leaderboard-sticky__loading">Загрузка…</p>
            ) : leaderboardError ? (
              <p className="fc-leaderboard-sticky__loading fc-leaderboard-sticky__loading--error">
                {leaderboardError}
              </p>
            ) : leaderboardViewer ? (
              isReferralsBoard ? (
                <div className="fc-leaderboard-sticky__metrics">
                  <div className="fc-leaderboard-sticky__cell" title="Засчитанные рефералы">
                    <span className="fc-leaderboard-sticky__cell-ico" aria-hidden>
                      ✅
                    </span>
                    <span className="fc-leaderboard-sticky__cell-text">
                      {leaderboardViewer.qualified_count ?? 0} засчитано
                    </span>
              </div>
                  <div className="fc-leaderboard-sticky__cell" title="Реферальный уровень">
                    <span className="fc-leaderboard-sticky__cell-ico" aria-hidden>
                      🎁
                    </span>
                    <span className="fc-leaderboard-sticky__cell-text">
                      {(leaderboardViewer.referral_level ?? 0) > 0
                        ? `Уровень ${leaderboardViewer.referral_level}`
                        : 'Уровень —'}
                    </span>
                  </div>
                  <div
                    className="fc-leaderboard-sticky__cell"
                    title="До следующего уровня"
                  >
                    <span className="fc-leaderboard-sticky__cell-ico" aria-hidden>
                      📈
                    </span>
                    <span className="fc-leaderboard-sticky__cell-text">
                      {leaderboardViewer.next_level != null
                        ? `ещё ${leaderboardViewer.remaining ?? 0} до L${leaderboardViewer.next_level}`
                        : 'макс. уровень'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="fc-leaderboard-sticky__metrics">
                  <div className="fc-leaderboard-sticky__cell" title="Текущий XP (всего)">
                    <span className="fc-leaderboard-sticky__cell-ico" aria-hidden>
                      🏆
                    </span>
                    <span className="fc-leaderboard-sticky__cell-text">
                      {leaderboardViewer.xp.toLocaleString('ru-RU')} XP
                    </span>
                  </div>
                  <div className="fc-leaderboard-sticky__cell" title="Завершённые сделки за период">
                    <span className="fc-leaderboard-sticky__cell-ico" aria-hidden>
                      🤝
                    </span>
                    <span className="fc-leaderboard-sticky__cell-text">
                      {leaderboardViewer.finished_count ?? 0}{' '}
                      {pluralRuOrders(leaderboardViewer.finished_count ?? 0)}
                    </span>
                  </div>
                  {leaderboardStickyMoneyCell(
                    Number(leaderboardViewer.volume_usdt_micro),
                    leaderboardTabRole as 'customer' | 'executor'
                  )}
                </div>
              )
            ) : (
              <div className="fc-leaderboard-sticky__empty">
                {isReferralsBoard
                  ? 'Пока нет засчитанных рефералов (нужна завершённая сделка у приглашённого)'
                  : 'Нет завершённых сделок в этой вкладке за выбранный период'}
              </div>
            )}
          </div>

          <p
            style={{
              margin: '0 12px 10px',
              fontSize: '13px',
              color: 'var(--fc-text-muted)',
              lineHeight: 1.35,
              maxWidth: '420px',
              alignSelf: 'center',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {isReferralsBoard
              ? 'Счёт — только засчитанные рефералы (приглашённый завершил ≥1 сделку). Уровень 1/2/3 — бейдж. Приз: раз в 10 дней VIP на 7 дней лучшему.'
              : 'Учитываются только завершённые заказы (сделка закрыта). Отменённые не входят в счёт. Вкладка «Заказчики» — только пользователи с ролью заказчика; «Исполнители» — только с ролью исполнителя (по профилю).'}
          </p>

          <div className="home-favorites-segment" style={{ margin: '0 auto 10px', maxWidth: '420px', width: '100%', padding: '0 8px', boxSizing: 'border-box' }} role="tablist" aria-label="Таблица лидеров: роль">
          <button 
              type="button"
              role="tab"
              aria-selected={leaderboardTabRole === 'customer'}
              className={`home-favorites-segment__btn${leaderboardTabRole === 'customer' ? ' home-favorites-segment__btn--active' : ''}`}
              onClick={() => setLeaderboardTabRole('customer')}
            >
              Заказчики
          </button>
            <button
              type="button"
              role="tab"
              aria-selected={leaderboardTabRole === 'executor'}
              className={`home-favorites-segment__btn${leaderboardTabRole === 'executor' ? ' home-favorites-segment__btn--active' : ''}`}
              onClick={() => setLeaderboardTabRole('executor')}
            >
              Исполнители
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={leaderboardTabRole === 'referrals'}
              className={`home-favorites-segment__btn${leaderboardTabRole === 'referrals' ? ' home-favorites-segment__btn--active' : ''}`}
              onClick={() => setLeaderboardTabRole('referrals')}
            >
              Рефералы
            </button>
      </div>

          {!isReferralsBoard ? (
            <div className="fc-filter-row" style={{ padding: '0 8px', flexShrink: 0, maxWidth: '420px', width: '100%', margin: '0 auto 10px', boxSizing: 'border-box' }}>
              <select
                value={leaderboardCategory}
                onChange={(e) => setLeaderboardCategory(e.target.value)}
                className="fc-select"
                style={{ flex: 1, margin: 0, width: '100%', boxSizing: 'border-box' }}
                aria-label="Категория в таблице лидеров"
              >
                <option value="">Все категории</option>
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
                <option value={LEADERBOARD_OTHER_CATEGORY}>Другие (своя категория)</option>
              </select>
            </div>
          ) : null}

          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto',
              boxSizing: 'border-box'
            }}
          >
            <div
              className="fc-leaderboard-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                padding: '0 8px 16px',
                boxSizing: 'border-box'
              }}
            >
              {leaderboardLoading ? (
                <p style={{ margin: '12px 0', color: 'var(--fc-text-muted)' }}>Загрузка…</p>
              ) : leaderboardError ? (
                <p style={{ margin: '12px 0', color: 'var(--fc-danger)' }}>{leaderboardError}</p>
              ) : leaderboardEntries.length === 0 ? (
                <p style={{ margin: '12px 0', color: 'var(--fc-text-muted)' }}>Пока нет данных за выбранные условия.</p>
              ) : (
                <ul className="fc-leaderboard-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {leaderboardEntries.map((row: any, idx: number) => {
                    const isPodium = row.rank <= 3 && !!row.medal;
                    const medal = leaderboardMedalEmoji(row.medal);
                    const name = displayNameUser(row);
                    const xp = Math.round(Number(row.xp) || 0);
                    const vol = Number(row.volume_usdt_micro || 0);
                    const podiumClass =
                      row.medal === 'gold'
                        ? 'fc-leaderboard-row--gold'
                        : row.medal === 'silver'
                          ? 'fc-leaderboard-row--silver'
                          : row.medal === 'bronze'
                            ? 'fc-leaderboard-row--bronze'
                            : '';
    return (
                      <li
                        key={row.user_id}
                        className={`fc-leaderboard-row fc-lb-row-appear ${podiumClass}`.trim()}
                        style={{ animationDelay: `${Math.min(idx, 24) * 0.035}s` }}
                      >
          <button 
                          type="button"
                          className={`fc-leaderboard-row__btn${isPodium ? ' fc-leaderboard-row__btn--podium' : ''}`.trim()}
                          onClick={() => row.user_id && setViewingProfile(Number(row.user_id))}
                        >
                          <span className="fc-leaderboard-row__rank">{row.rank}.</span>
                          <span className="fc-leaderboard-row__avatar-wrap">
                            {row.avatar_url ? (
                              <img src={row.avatar_url} alt="" className="fc-leaderboard-row__avatar" />
                            ) : (
                              <span className="fc-leaderboard-row__avatar fc-leaderboard-row__avatar--ph">
                                {row.first_name?.[0]}
                                {row.last_name?.[0]}
                              </span>
                            )}
                          </span>
                          <div className="fc-leaderboard-row__center">
                            <div className="fc-leaderboard-row__name-row">
                              <span className="fc-leaderboard-row__name">{name}</span>
                              {isPodium ? (
                                <span className="fc-leaderboard-row__medal-inline" aria-hidden>
                                  {medal}
                                </span>
                              ) : null}
                            </div>
                            <div className="fc-leaderboard-row__meta">
                              {isReferralsBoard ? (
                                <>
                                  <span title="Засчитанные приглашённые">
                                    ✅ {Number(row.qualified_count) || 0} приглашённых
                                  </span>
                                  <span className="fc-leaderboard-row__meta-sep" aria-hidden>
                                    |
                                  </span>
                                  <span title="Реферальный уровень">
                                    {(Number(row.referral_level) || 0) > 0
                                      ? `Уровень ${row.referral_level}`
                                      : '—'}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span title="Текущий XP">🏆 {xp.toLocaleString('ru-RU')} XP</span>
                                  <span className="fc-leaderboard-row__meta-sep" aria-hidden>
                                    |
                                  </span>
                                  <span title="Сделок за период">
                                    🤝 {row.finished_count}{' '}
                                    {pluralRuOrders(Number(row.finished_count) || 0)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="fc-leaderboard-row__money">
                            {isReferralsBoard ? (
                              <span className="fc-lb-usdt fc-lb-usdt--stack" title="Засчитанные рефералы">
                                <span className="fc-lb-usdt__ico" aria-hidden>
                                  👥
                                </span>
                                <span className="fc-lb-usdt__val">{Number(row.qualified_count) || 0}</span>
                              </span>
                            ) : (
                              leaderboardVolumeRightSlot(vol, leaderboardTabRole as 'customer' | 'executor')
                            )}
                          </div>
          </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </header>
      </div>
    );
  }

  if (showCatalog) {
    return (
      <>
        {mainTabBar}
        {dealsSegmentBar}
      <div className="App">
        <header
          className="App-header"
          style={{
            justifyContent: 'flex-start',
            padding: '12px 4px 12px',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          <div
            className="mk-catalog-topbar"
          >
            <button
              type="button"
              onClick={() => setShowCatalog(false)}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
              }}
            >
              ←
            </button>
            <h2 style={{ margin: 0, flex: 1 }}>Активные заказы</h2>
            <button
              type="button"
              className="fc-btn fc-btn--ghost"
              style={{ fontSize: 13, padding: '8px 10px' }}
              onClick={() => {
                setShowCatalog(false);
                void loadMyArchivedOrders();
              }}
            >
              Архив
            </button>
          </div>

          <div className="mk-chips" role="group" aria-label="Фильтр">
            <button
              type="button"
              className={`mk-chip${!catalogOnlyUrgent && !catalogOnlyTop && !catalogCategory ? ' mk-chip--on' : ''}`}
              onClick={() => {
                setCatalogOnlyUrgent(false);
                setCatalogOnlyTop(false);
                setCatalogCategory('');
                catalogFiltersRef.current = {
                  category: '',
                  q: catalogQuery,
                  onlyUrgent: false,
                  onlyTop: false
                };
                void fetchCatalogPage(1);
              }}
            >
              Все заказы
            </button>
            <button
              type="button"
              className={`mk-chip${catalogOnlyUrgent ? ' mk-chip--on' : ''}`}
              onClick={() => {
                setCatalogOnlyUrgent(true);
                setCatalogOnlyTop(false);
                setCatalogCategory('');
                catalogFiltersRef.current = {
                  category: '',
                  q: catalogQuery,
                  onlyUrgent: true,
                  onlyTop: false
                };
                void fetchCatalogPage(1);
              }}
            >
              🔥 Срочные
            </button>
            <button
              type="button"
              className={`mk-chip${catalogOnlyTop ? ' mk-chip--on' : ''}`}
              onClick={() => {
                setCatalogOnlyUrgent(false);
                setCatalogOnlyTop(true);
                setCatalogCategory('');
                catalogFiltersRef.current = {
                  category: '',
                  q: catalogQuery,
                  onlyUrgent: false,
                  onlyTop: true
                };
                void fetchCatalogPage(1);
              }}
            >
              ⭐ ТОП задания
            </button>
          </div>

          <div
            className="fc-filter-row mk-catalog-filters"
          >
            <select
              value={
                catalogOnlyUrgent ? '__urgent' : catalogOnlyTop ? '__top' : catalogCategory || ''
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === '__urgent') {
                  setCatalogOnlyUrgent(true);
                  setCatalogOnlyTop(false);
                  setCatalogCategory('');
                  catalogFiltersRef.current = {
                    category: '',
                    q: catalogQuery,
                    onlyUrgent: true,
                    onlyTop: false
                  };
                } else if (value === '__top') {
                  setCatalogOnlyUrgent(false);
                  setCatalogOnlyTop(true);
                  setCatalogCategory('');
                  catalogFiltersRef.current = {
                    category: '',
                    q: catalogQuery,
                    onlyUrgent: false,
                    onlyTop: true
                  };
                } else {
                  setCatalogOnlyUrgent(false);
                  setCatalogOnlyTop(false);
                  setCatalogCategory(value);
                  catalogFiltersRef.current = {
                    category: value,
                    q: catalogQuery,
                    onlyUrgent: false,
                    onlyTop: false
                  };
                }
                void fetchCatalogPage(1);
              }}
              className="fc-select"
              style={{ flex: '0 0 170px', width: '170px', margin: 0 }}
            >
              <option value="">Все категории</option>
              <option value="__urgent">Срочные</option>
              <option value="__top">ТОП</option>
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              value={catalogQuery}
              onChange={(e) => setCatalogQuery(e.target.value)}
              placeholder="Поиск…"
              className="fc-input"
              style={{ flex: 1, minWidth: 0, margin: 0 }}
            />
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '100%',
              margin: '0 auto',
              padding: '0 4px',
              boxSizing: 'border-box'
            }}
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                paddingRight: '4px',
                marginTop: 10
              }}
            >
          {catalogLoading && openOrders.length === 0 ? (
                <SkeletonOrderList />
          ) : openOrders.length === 0 ? (
                <EmptyState
                  icon="🔎"
                  title="Пока нет открытых заданий"
                  subtitle="Загляните чуть позже или измените фильтры поиска."
                />
          ) : (
                  <div 
                    style={{
                    width: '100%',
                      display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    paddingBottom: 12
                  }}
                >
                {openOrders.map((order) => {
                  const topActive =
                    Boolean(order.order_top_until) &&
                    new Date(String(order.order_top_until)).getTime() > Date.now();
                  const lastSlot =
                    order.responses_limit != null &&
                    Number(order.responses_limit) > 0 &&
                    Number(order.responses_limit) - Number(order.responses_count || 0) === 1;
                  const deadlineLabel =
                    order.execution_deadline_value && order.execution_deadline_unit
                      ? formatDurationValueUnit(
                          order.execution_deadline_value,
                          order.execution_deadline_unit
                        )
                      : 'срок не указан';
                  const responsesLabel =
                    order.responses_limit != null && Number(order.responses_limit) > 0
                      ? `${Number(order.responses_count || 0)} из ${Number(order.responses_limit)}`
                      : `${Number(order.responses_count || 0)} · безлимит`;
                  const customerName = [order.customer_first_name, order.customer_last_name]
                    .filter(Boolean)
                    .join(' ')
                    .trim();
                  const isFav = !!catalogFavoriteOrderIds[Number(order.id)];
                  const deadlineCell = deadlineLabel === 'срок не указан' ? '—' : deadlineLabel;
                  return (
                    <div
                      key={order.id}
                      className={
                        'mk-order-card' +
                        (topActive ? ' mk-order-card--top' : '') +
                        (lastSlot ? ' mk-order-card--last' : '')
                      }
                    >
                      <button
                        type="button"
                        className={`mk-order-card__fav${isFav ? ' mk-order-card__fav--on' : ''}`}
                        disabled={catalogFavoriteBusyId === Number(order.id)}
                        title={isFav ? 'Убрать из избранного' : 'В избранное'}
                        aria-label={isFav ? 'Убрать из избранного' : 'В избранное'}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void toggleCatalogOrderFavorite(Number(order.id));
                        }}
                      >
                        <FavoriteHeartIcon filled={isFav} />
                      </button>

                      <button
                        type="button"
                        className="mk-order-card__customer"
                        onClick={() => setViewingProfile(order.customer_id)}
                        title={customerName || 'Профиль заказчика'}
                      >
                        <span className="fc-avatar-presence-anchor" style={{ position: 'relative', display: 'block' }}>
                          {order.customer_avatar_url ? (
                            <img src={order.customer_avatar_url} alt="" className="mk-order-card__avatar" />
                          ) : (
                            <span className="mk-order-card__avatar-ph">
                              {order.customer_first_name?.[0] || 'З'}
                            </span>
                          )}
                          <PresenceStatusDot lastSeenAt={order.customer_last_seen_at} />
                        </span>
                        <span className="mk-order-card__customer-label">
                          Заказчик <b>{customerName || 'Аноним'}</b>
                        </span>
                      </button>

                      <button
                        type="button"
                        className="mk-order-card__main"
                        onClick={() => viewOrderDetails(order)}
                      >
                        <div className="mk-order-card__title-row">
                          <h3 className="mk-order-card__title">{order.title || 'Заказ'}</h3>
                          <span className="mk-order-card__ref">{orderRefLabel(Number(order.id))}</span>
                        </div>
                        <div className="mk-order-card__path-row">
                          <p className="mk-order-card__path">{orderCategoryLabel(order.category)}</p>
                          {Number(order.views_count) > 0 ? (
                            <span className="mk-order-card__views" title="Просмотров">
                              👁 {Number(order.views_count)}
                            </span>
                          ) : null}
                        </div>

                        {order.is_urgent || topActive ? (
                          <div className="mk-order-card__tags">
                            {order.is_urgent ? (
                              <span className="mk-order-card__tag mk-order-card__tag--hot">🔥 Срочный</span>
                            ) : null}
                            {topActive ? (
                              <span className="mk-order-card__tag mk-order-card__tag--top">⭐ ТОП</span>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="mk-order-card__grid">
                          <div className="mk-order-card__cell">
                            <span>Бюджет</span>
                            <b className="mk-order-card__price">
                              {order.budget != null ? `${formatMicroUsdt(order.budget)} USDT` : '—'}
                            </b>
                          </div>
                          <div className="mk-order-card__cell">
                            <span>Срок</span>
                            <b>{deadlineCell}</b>
                          </div>
                          <div className="mk-order-card__cell mk-order-card__cell--resp">
                            <span>Отклики</span>
                            <b style={{ color: lastSlot ? 'var(--fc-accent-strong)' : undefined }}>
                              {responsesLabel}
                            </b>
                            {Array.isArray(order.responders) && order.responders.length > 0 ? (
                              <div className="mk-order-card__responders">
                                {order.responders.slice(0, 3).map((r: any, i: number) => (
                                  <span
                                    key={r?.user_id ?? i}
                                    className="mk-order-card__responder-avatar"
                                    style={{ zIndex: 3 - i }}
                                  >
                                    {r?.avatar_url ? (
                                      <img src={r.avatar_url} alt="" />
                                    ) : (
                                      <span className="mk-order-card__responder-ph">
                                        {(r?.first_name || '?')[0]}
                                      </span>
                                    )}
                                  </span>
                                ))}
                                {Number(order.responses_count || 0) > 3 ? (
                                  <span className="mk-order-card__responder-more">
                                    +{Number(order.responses_count) - 3}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </button>

                      <div className="mk-order-card__act">
                        <button
                          type="button"
                          className="mk-order-card__cta"
                          onClick={() => {
                            setResponseBudgetMode('client');
                            setResponseBudget('');
                            setRespondingTo(order);
                          }}
                        >
                          Откликнуться
                        </button>
                        <button
                          type="button"
                          className="mk-order-card__details"
                          onClick={() => viewOrderDetails(order)}
                        >
                          Подробнее
                        </button>
                      </div>
                    </div>
                  );
                })}
                </div>
              )}
              {catalogTotalPages > 1 ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginTop: 12,
                    marginBottom: 8
                  }}
                >
                  <button
                    type="button"
                    className="fc-btn fc-btn--dark fc-btn--sm"
                    disabled={catalogPage <= 1}
                    onClick={() => void fetchCatalogPage(catalogPage - 1)}
                  >
                    ← Назад
            </button>
                  <span style={{ fontSize: 13, color: 'var(--fc-text-muted)' }}>
                    Стр. {catalogPage} / {catalogTotalPages}
                    {catalogTotalCount > 0 ? ` · ${catalogTotalCount} всего` : null}
                  </span>
                  <button
                    type="button"
                    className="fc-btn fc-btn--dark fc-btn--sm"
                    disabled={catalogPage >= catalogTotalPages}
                    onClick={() => void fetchCatalogPage(catalogPage + 1)}
                  >
                    Вперёд →
            </button>
          </div>
              ) : null}
          <button 
                type="button"
            onClick={() => setShowCatalog(false)} 
                className="fc-btn fc-btn--secondary fc-btn--block"
                style={{ marginTop: 4, marginBottom: 12 }}
          >
            На главную
          </button>
            </div>
          </div>
        </header>
      </div>
      </>
    );
  }

  if (servicesScreen) {
    return (
      <>
        {mainTabBar}
        {dealsSegmentBar}
      <ServicesCatalogScreen
        token={token!}
        mode={servicesScreen}
        catalogPane={servicesScreen === 'catalog' ? servicesCatalogPane : undefined}
        onCatalogPaneChange={setServicesCatalogPane}
        focusServiceId={servicesFocusId}
        onBack={() => {
          navigateMainTab('home');
        }}
        onOpenProfile={(id) => {
          pushNav({
            type: 'services',
            mode: servicesScreen,
            pane: servicesScreen === 'catalog' ? servicesCatalogPane : 'browse'
          });
          setServicesScreen(null);
          setServicesFocusId(null);
          setViewingProfile(id);
        }}
        onOrdered={(orderId) => {
          void refreshUserProfile();
          void refreshHomePreview();
          pushNav({
            type: 'services',
            mode: servicesScreen,
            pane: 'ordered'
          });
          setServicesCatalogPane('ordered');
          setServicesScreen(null);
          setServicesFocusId(null);
          if (orderId) {
            void viewOrderDetails({ id: orderId });
          }
        }}
        onOpenOrder={(orderId) => {
          pushNav({
            type: 'services',
            mode: servicesScreen,
            pane: servicesScreen === 'catalog' ? servicesCatalogPane : 'browse'
          });
          setServicesScreen(null);
          setServicesFocusId(null);
          void viewOrderDetails({ id: orderId });
        }}
      />
      </>
    );
  }

  if (showMyOrders) {
    const isCustomerOrderInWork = (o: any) => {
      const s = String(o?.status || '');
      if (s === 'in_progress' || s === 'awaiting_payment' || s === 'completed') return true;
      if (s === 'open') {
        const rs = String(o?.selected_response_status || '');
        return rs === 'accepted' || rs === 'pending_payment' || rs === 'negotiating';
      }
      return false;
    };
    const sourceList =
      myOrdersListMode === 'archive'
        ? myArchivedCustomerOrders
        : myOrdersListMode === 'in_work'
          ? myOrders.filter(isCustomerOrderInWork)
          : myOrders.filter((o: any) => !isCustomerOrderInWork(o));
    const filtered = sourceList.filter((o: any) => {
      const catOk = !myOrdersCategory || String(o.category || '') === myOrdersCategory;
      const q = myOrdersQuery.trim().toLowerCase();
      const qOk =
        !q ||
        String(o.title || '').toLowerCase().includes(q) ||
        String(o.description || '').toLowerCase().includes(q);
      return catOk && qOk;
    });
    const modeTitle =
      myOrdersListMode === 'archive'
        ? '📦 Архив'
        : myOrdersListMode === 'all'
          ? '📋 Все задания'
          : '⚡ В работе';
    const emptyHint =
      myOrdersListMode === 'archive'
        ? 'В архиве пока нет заданий'
        : myOrdersListMode === 'all'
          ? 'Нет открытых заданий, которые ждут исполнителя'
          : 'Нет заданий в работе — здесь появятся сделки с выбранным исполнителем';
    return (
      <>
        {mainTabBar}
        {dealsSegmentBar}
      <div className="App">
        <header
          className="App-header"
          style={{
            justifyContent: 'flex-start',
            padding: '12px 0 12px',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto 10px',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0
            }}
          >
                  <button 
              type="button"
              onClick={() => {
                setShowMyOrders(false);
                setMyOrdersListMode('in_work');
                void refreshHomePreview();
              }}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
              }}
            >
              ←
                  </button>
            <h2 style={{ margin: 0, flex: 1 }}>{modeTitle}</h2>
              </div>

          <div
            className="fc-deals-segment"
            role="tablist"
            aria-label="Раздел заданий"
            style={{ marginBottom: 10 }}
          >
            <button
              type="button"
              role="tab"
              className={
                'fc-deals-segment__btn' +
                (myOrdersListMode === 'in_work' ? ' fc-deals-segment__btn--on' : '')
              }
              onClick={() => setMyOrdersListMode('in_work')}
            >
              В работе
            </button>
            <button
              type="button"
              role="tab"
              className={
                'fc-deals-segment__btn' + (myOrdersListMode === 'all' ? ' fc-deals-segment__btn--on' : '')
              }
              onClick={() => setMyOrdersListMode('all')}
            >
              Все
            </button>
            <button
              type="button"
              role="tab"
              className={
                'fc-deals-segment__btn' +
                (myOrdersListMode === 'archive' ? ' fc-deals-segment__btn--on' : '')
              }
              onClick={() => setMyOrdersListMode('archive')}
            >
              Архив
            </button>
          </div>

          <div
            className="fc-filter-row"
            style={{ maxWidth: '420px', margin: '0 auto 10px', padding: '0 8px', flexShrink: 0 }}
          >
            <select
              value={myOrdersCategory}
              onChange={(e) => setMyOrdersCategory(e.target.value)}
              className="fc-select"
              style={{ flex: '0 0 170px', width: '170px', margin: 0 }}
            >
              <option value="">Все категории</option>
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              value={myOrdersQuery}
              onChange={(e) => setMyOrdersQuery(e.target.value)}
              placeholder="Поиск…"
              className="fc-input"
              style={{ flex: 1, minWidth: 0, margin: 0 }}
            />
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              icon={myOrdersListMode === 'archive' ? '📦' : myOrdersListMode === 'all' ? '📋' : '⚡'}
              title={emptyHint}
            />
          ) : (
            <div
              className="fc-compact-order-list"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 'max(24px, env(safe-area-inset-bottom))'
              }}
            >
              {filtered.map((order) => (
                <CompactOrderListCard
                  key={order.id}
                  order={order}
                  metaLine={
                    <>
                      {orderStatusLabel(order.status)}
                      {order.is_urgent ? ' · 🔥 срочно' : null}
                      {order.status === 'open' ? (
                        <>
                          {' · '}
                          откликов {Number(order.responses_count || 0)}
                          {order.responses_limit != null && Number(order.responses_limit) > 0
                            ? `/${Number(order.responses_limit)}`
                            : '/∞'}
                        </>
                      ) : null}
                    </>
                  }
                  onOpenDetails={() => viewOrderDetails(order)}
                  detailsLabel={order.executor_service_id ? 'Чат / детали' : 'Подробнее'}
                  extraActions={
                    order.status === 'open' && !order.executor_service_id ? (
                      <>
          <button 
                          type="button"
                          className="fc-btn fc-btn--ghost fc-btn--sm"
                          onClick={() => loadResponses(order.id)}
                        >
                          Отклики ({Number(order.responses_count || 0)}
                          {order.responses_limit != null && Number(order.responses_limit) > 0
                            ? `/${Number(order.responses_limit)}`
                            : '/∞'}
                          )
          </button>
                        <button
                          type="button"
                          className="fc-btn fc-btn--ghost fc-btn--sm"
                          onClick={() => openFindExecutorForOrder(order)}
                        >
                          Предложить
                        </button>
                      </>
                    ) : null
                  }
                />
              ))}
            </div>
          )}
        </header>
      </div>
      </>
    );
  }

  const recoTitle =
    effectiveHomeRole === 'customer' ? 'Рекомендуемые исполнители' : 'Рекомендуемые заказчики';
  const vipTitle = effectiveHomeRole === 'customer' ? 'VIP исполнители' : 'VIP заказчики';
  const oppositeNoun = effectiveHomeRole === 'customer' ? 'исполнителей' : 'заказчиков';
  const homeHasFavorites = homeFavoriteUsers.length > 0 || homeFavoriteOrders.length > 0;

  return (
    <>
      {mainTabBar}
    <div className="App">
      {homeNotifOpen && homeNotifDropdownStyle
        ? createPortal(
            <div
              className="home-market__notif"
              role="dialog"
              aria-label="Требует внимания"
              style={homeNotifDropdownStyle}
            >
              <div className="home-market__notif-head">Требует внимания</div>
              {homeAttentionList.length === 0 ? (
                <p className="home-market__notif-empty">Всё под контролем — новых напоминаний нет.</p>
              ) : (
                <ul className="home-market__notif-list">
                  {homeAttentionList.map((it) => (
                    <li key={it.orderId}>
                      <button
                        type="button"
                        className="home-market__notif-item"
                        onClick={() => {
                          setHomeNotifOpen(false);
                          void viewOrderDetails(it.order);
                        }}
                      >
                        <span className="home-market__notif-item-title">{it.label}</span>
                        <span className="home-market__notif-item-sub">{it.detail}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>,
            document.body
          )
        : null}
      <HomePage
        notificationOpen={homeNotifOpen}
        onCloseNotifications={() => setHomeNotifOpen(false)}
      >
        <HomeHeader
          balance={walletBalance != null ? `${formatMicroUsdt(walletBalance)} USDT` : '… USDT'}
          attentionCount={homeAttentionList.length}
          notificationOpen={homeNotifOpen}
          bellRef={homeNotifBellRef}
          onToggleNotifications={() => setHomeNotifOpen((v) => !v)}
        />

        <HomePromoShelf
          slides={
            homePromoSlides && homePromoSlides.length > 0
              ? homePromoSlides
              : defaultPromoSlides(
                  effectiveHomeRole === 'customer' || effectiveHomeRole === 'executor'
                    ? effectiveHomeRole
                    : null
                )
          }
          onCta={(cta: PromoSlideCta, slide) => {
            if (slide?.dbId != null) {
              try {
                let anonKey = '';
                try {
                  anonKey = localStorage.getItem('fc_promo_anon') || '';
                  if (!anonKey) {
                    anonKey = `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
                    localStorage.setItem('fc_promo_anon', anonKey);
                  }
                } catch {
                  anonKey = `a-${Date.now().toString(36)}`;
                }
                const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                if (token) headers.Authorization = `Bearer ${token}`;
                void fetch(apiUrl(`/api/public/home-promo/${slide.dbId}/click`), {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({ anon_key: anonKey })
                }).catch(() => {});
              } catch {
                /* ignore click track errors */
              }
            }
            if (cta.type === 'customer_create') {
              if (effectiveHomeRole === 'customer') setShowCreateOrder(true);
              else if (user?.id) setViewingProfile(user.id);
              return;
            }
            if (cta.type === 'executor_catalog') {
              if (effectiveHomeRole === 'executor') {
                setDealsSegment('orders');
                void openCatalog();
              } else openDealsSegment('services');
              return;
            }
            if (cta.type === 'services') {
              openDealsSegment('services');
              return;
            }
            if (cta.type === 'orders') {
              openDealsSegment('orders');
              return;
            }
            if (cta.type === 'profile') {
              if (user?.id) setViewingProfile(user.id);
              return;
            }
            if (cta.type === 'external') {
              openExternalLink(cta.url);
            }
          }}
        />

        <HomeQuickActions
          actions={[
            {
              id: 'profile',
              label: 'Профиль',
              onClick: () => user?.id && setViewingProfile(user.id),
              icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="3.5" /><path d="M5 19c1.6-3.2 4-4.8 7-4.8s5.4 1.6 7 4.8" /></svg>
            },
            {
              id: 'orders',
              label: 'Мои заказы',
              onClick: () => {
                setDealsSegment('orders');
                if (effectiveHomeRole === 'customer') void loadMyOrders();
                else void loadMyActiveOrders();
              },
              icon: <svg viewBox="0 0 24 24"><path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" /></svg>
            },
            {
              id: 'services',
              label: 'Услуги',
              onClick: () => openDealsSegment('services'),
              icon: <svg viewBox="0 0 24 24"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z" /></svg>
            },
            {
              id: 'leaders',
              label: 'Лидеры',
              onClick: () => {
                setLeaderboardTabRole(effectiveHomeRole === 'customer' ? 'customer' : 'executor');
                setLeaderboardPeriod('month');
                setLeaderboardCategory('');
                setShowLeaderboard(true);
              },
              icon: <svg viewBox="0 0 24 24"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4Z" /><path d="M7 6H4a2 2 0 0 0 2 4h1M17 6h3a2 2 0 0 1-2 4h-1" /></svg>
            },
            {
              id: 'support',
              label: 'Поддержка',
              onClick: () => {
                const uname = supportBotUsername.trim();
                if (!uname) {
                  showAlert('Ссылка на поддержку ещё не настроена');
                  return;
                }
                openExternalLink(`https://t.me/${uname}?start=support`);
              },
              icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><path d="M12 8v4l2.5 1.5" /></svg>
            },
            {
              id: 'legal',
              label: 'Условия сервиса',
              onClick: () => setShowLegalTerms(true),
              icon: <svg viewBox="0 0 24 24"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>
            }
          ]}
        />

        {homeHasFavorites ? (
          <>
        <div className="home-section-title" id="home-favorites-anchor">
          <span>Избранное</span>
          <button
            type="button"
            onClick={() => setHomeFavoritesCollapsed((v) => !v)}
            aria-expanded={!homeFavoritesCollapsed}
          >
            {homeFavoritesCollapsed ? 'Развернуть' : 'Свернуть'}
          </button>
        </div>
        {!homeFavoritesCollapsed ? (
          <>
            {effectiveHomeRole === 'executor' ? (
              <div className="home-favorites-segment" role="tablist" aria-label="Тип избранного">
                <button
                  type="button"
                  role="tab"
                  aria-selected={homeExecutorFavoritesTab === 'orders'}
                  className={`home-favorites-segment__btn${homeExecutorFavoritesTab === 'orders' ? ' home-favorites-segment__btn--active' : ''}`}
                  onClick={() => setHomeExecutorFavoritesTab('orders')}
                >
                  Заказы
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={homeExecutorFavoritesTab === 'customers'}
                  className={`home-favorites-segment__btn${homeExecutorFavoritesTab === 'customers' ? ' home-favorites-segment__btn--active' : ''}`}
                  onClick={() => setHomeExecutorFavoritesTab('customers')}
                >
                  Заказчики
                </button>
              </div>
            ) : null}
            <div className="home-favorites-scroll">
              {(() => {
            const showExecutorTabs = effectiveHomeRole === 'executor';
            const favUsers =
              showExecutorTabs && homeExecutorFavoritesTab === 'orders' ? [] : homeFavoriteUsers;
            const favOrders =
              showExecutorTabs && homeExecutorFavoritesTab === 'customers' ? [] : homeFavoriteOrders;
            const noUsers = favUsers.length === 0;
            const noOrders = favOrders.length === 0;
            if (showExecutorTabs && homeExecutorFavoritesTab === 'orders' && noOrders) {
              return (
                <div className="home-empty home-favorites-scroll__empty">
                  Нет избранных заказов — отметьте ♥ в каталоге «Найти заказы»
                </div>
              );
            }
            if (showExecutorTabs && homeExecutorFavoritesTab === 'customers' && noUsers) {
              return (
                <div className="home-empty home-favorites-scroll__empty">
                  Нет избранных заказчиков — отметьте ♥ в профиле заказчика
                </div>
              );
            }
            if (!showExecutorTabs && noUsers) {
              return (
                <div className="home-empty home-favorites-scroll__empty">
                  Пока пусто — нажмите ♥ в профиле исполнителя
                </div>
              );
            }
            return (
              <>
                {favUsers.map((u: any) => (
                  <button
                    key={`fav-user-${u.id}`}
                    type="button"
                    className="home-fav-card home-fav-card--user"
                    onClick={() => setViewingProfile(u.id)}
                  >
                    <span className="home-fav-card__badge" aria-hidden>
                      ♥
                    </span>
                    <span className="home-fav-card__avatar-wrap">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="home-fav-card__avatar" />
                      ) : (
                        <span className="home-fav-card__avatar home-fav-card__avatar--ph">
                          {u.first_name?.[0]}
                          {u.last_name?.[0]}
                        </span>
                      )}
                      <PresenceStatusDot lastSeenAt={u.last_seen_at} />
                    </span>
                    <span className="home-fav-card__name">{displayNameUser(u)}</span>
                    <span className="home-fav-card__meta">
                      {u.role === 'executor' ? 'Исполнитель' : u.role === 'customer' ? 'Заказчик' : ''}
                    </span>
                  </button>
                ))}
                {favOrders.map((order: any) => {
                  const favUrgent = Boolean(order.is_urgent);
                  return (
                    <button
                      key={`fav-order-${order.id}`}
                      type="button"
                      className={`home-order-card${favUrgent ? ' home-order-card--blaze' : ''}`}
                      onClick={() => void viewOrderDetails(order)}
                    >
                      <div
                        className={`home-order-card__fav-top${
                          order.customer_first_name || order.customer_last_name || order.customer_avatar_url
                            ? ''
                            : ' home-order-card__fav-top--heart-only'
                        }`}
                      >
                        {order.customer_first_name || order.customer_last_name || order.customer_avatar_url ? (
                          <div className="home-order-card__customer home-order-card__customer--fav-inline">
                            <span className="home-order-card__avatar-anchor">
                              {order.customer_avatar_url ? (
                                <img src={order.customer_avatar_url} alt="" className="home-order-card__avatar" />
                              ) : (
                                <span className="home-order-card__avatar home-order-card__avatar--ph">
                                  {order.customer_first_name?.[0]}
                                  {order.customer_last_name?.[0]}
                                </span>
                              )}
                              <PresenceStatusDot lastSeenAt={order.customer_last_seen_at} />
                            </span>
                            <span className="home-order-card__customer-name">
                              {displayNameUser({
                                first_name: order.customer_first_name,
                                last_name: order.customer_last_name
                              })}
                            </span>
                          </div>
                        ) : null}
                        <span className="home-order-card__fav-heart" aria-hidden>
                          ♥
                        </span>
                      </div>
                      <div className="home-order-card__title-row home-order-card__title-row--single">
                        <span className="home-order-card__title">{order.title || 'Заказ'}</span>
                      </div>
                      <div className="home-order-card__price">
                        {order.budget != null ? `${formatMicroUsdt(order.budget)} USDT` : '—'}
                      </div>
                      <div className="home-order-card__meta home-order-card__meta--split">
                        <span className="home-order-card__meta-primary">
                          {order.execution_deadline_at
                            ? formatOrderDeadlineRemaining(order.execution_deadline_at)
                            : order.execution_deadline_value && order.execution_deadline_unit
                              ? `${formatDurationValueUnit(order.execution_deadline_value, order.execution_deadline_unit)}`
                              : order.status
                                ? orderStatusLabel(order.status)
                                : '—'}
                        </span>
                        {favUrgent ? (
                          <div className="home-order-card__urgent-bottom-rail">
                            <span className="home-order-card__status home-order-card__status--urgent">🔥 Срочно</span>
                            <span className="home-order-card__rail-id" title="Номер заказа">
                              {orderRefLabel(order.id)}
                            </span>
                          </div>
                        ) : (
                          <span className="home-order-card__meta-id" title="Номер заказа">
                            {orderRefLabel(order.id)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </>
            );
              })()}
            </div>
          </>
        ) : null}
          </>
        ) : null}

        <HomeSectionHeader
          title="Активные заказы"
          actionLabel="Все ›"
          onAction={() => (effectiveHomeRole === 'customer' ? loadMyOrders() : loadMyActiveOrders())}
        />
        {homePreviewOrders.length === 0 ? (
          <button
            type="button"
            className="home-empty-cta"
            onClick={() =>
              effectiveHomeRole === 'customer' ? setShowCreateOrder(true) : void openCatalog()
            }
          >
            {effectiveHomeRole === 'customer'
              ? 'Нет активных — создать задание'
              : 'Нет активных — смотреть новые заказы'}
          </button>
        ) : (
        <div className="home-orders-scroll">
            {homePreviewOrders.map((order: any) => (
              <button
                key={order.id}
                type="button"
                className="home-order-card"
                data-deadline-tick={deadlineTick}
                onClick={() => void viewOrderDetails(order)}
              >
                <div className="home-order-card__header">
                  <span className="home-order-card__id" title="Номер заказа в FreelanCYX">
                    {orderRefLabel(order.id)}
                  </span>
                  {order.is_urgent ? (
                    <span className="home-order-card__status home-order-card__status--urgent">🔥 Срочно</span>
                  ) : order.status ? (
                    <span className="home-order-card__status">{orderStatusLabel(order.status)}</span>
                  ) : null}
                </div>
                {effectiveHomeRole === 'executor' &&
                (order.customer_first_name || order.customer_last_name || order.customer_avatar_url) ? (
                  <div className="home-order-card__customer">
                    <span className="home-order-card__avatar-anchor">
                      {order.customer_avatar_url ? (
                        <img src={order.customer_avatar_url} alt="" className="home-order-card__avatar" />
                      ) : (
                        <span className="home-order-card__avatar home-order-card__avatar--ph">
                          {order.customer_first_name?.[0]}
                          {order.customer_last_name?.[0]}
                        </span>
                      )}
                      <PresenceStatusDot lastSeenAt={order.customer_last_seen_at} />
                    </span>
                    <span className="home-order-card__customer-name">
                      {displayNameUser({
                        first_name: order.customer_first_name,
                        last_name: order.customer_last_name
                      })}
                    </span>
                  </div>
                ) : null}
                <div className="home-order-card__title-row">
                  <span className="home-order-card__title">{order.title || 'Заказ'}</span>
                </div>
                <div className="home-order-card__price">
                  {order.budget != null ? `${formatMicroUsdt(order.budget)} USDT` : '—'}
                </div>
                <div className="home-order-card__meta">
                  {order.execution_deadline_at
                    ? formatOrderDeadlineRemaining(order.execution_deadline_at)
                    : order.execution_deadline_value && order.execution_deadline_unit
                      ? `${formatDurationValueUnit(order.execution_deadline_value, order.execution_deadline_unit)}`
                      : order.category
                        ? orderCategoryLabel(order.category)
                        : '—'}
                </div>
              </button>
            ))}
        </div>
        )}

        {/* Услуги — сразу под активными заказами, такими же карточками */}
        {effectiveHomeRole === 'customer' && token ? (
          <HomePopularServicesStrip
            token={token}
            category={homePopularServicesCategory}
            onCategoryChange={setHomePopularServicesCategory}
            onOpenAll={() => setServicesScreen('popular')}
            onOpenProfile={(id) => setViewingProfile(id)}
            onOrdered={(oid) => {
              void refreshUserProfile();
              void refreshHomePreview();
              if (oid != null && Number.isFinite(oid)) {
                void viewOrderDetails({ id: oid });
              }
            }}
          />
        ) : null}

        {effectiveHomeRole === 'executor' ? (
          <>
            <HomeSectionHeader title="Задания" actionLabel="Каталог ›" onAction={() => void openCatalog()} />
            <SegmentedTabs
              label="Лента заданий"
              value={homeOrdersFeedTab}
              tabs={[{ id: 'new', label: 'Новые' }, { id: 'all', label: 'Все задания' }]}
              onChange={setHomeOrdersFeedTab}
            />
            <div className="home-reco-panels-scroll" aria-label={homeOrdersFeedTab === 'new' ? 'Новые задания' : 'Все задания'}>
              {homeOrderFeedPanels.length === 0 ? (
                <p className="home-empty-inline">
                  {homeOrdersFeedTab === 'new'
                    ? 'Пока нет новых заданий за 3 дня'
                    : 'Пока нет открытых заданий'}
                </p>
              ) : (
                homeOrderFeedPanels.map((panel, panelIdx) => (
                  <div key={`ord-feed-${panelIdx}`} className="home-reco-panel">
                    {panel.map((order: any, rowInPanel: number) => {
                      const globalIdx = panelIdx * 3 + rowInPanel;
                      const catLabel = order.category ? orderCategoryLabel(order.category) : '—';
                      return (
                        <button
                          key={order.id}
                          type="button"
                          className={`home-reco-row home-order-feed-row${
                            order.is_urgent ? ' home-order-feed-row--urgent' : ''
                          }${order.order_top_until ? ' home-order-feed-row--top' : ''}`}
                          onClick={() => void viewOrderDetails(order)}
                        >
                          <span className="home-reco-row__num">{globalIdx + 1}</span>
                          <div className="home-reco-row__avatar-wrap">
                            <div className="home-reco-row__avatar">
                              {order.customer_avatar_url ? (
                                <img src={order.customer_avatar_url} alt="" />
                              ) : (
                                <>
                                  {order.customer_first_name?.[0] || 'З'}
                                  {order.customer_last_name?.[0] || ''}
                                </>
                              )}
                            </div>
                            <PresenceStatusDot lastSeenAt={order.customer_last_seen_at} />
                          </div>
                          <div className="home-reco-row__body">
                            <div className="home-reco-row__name-line">
                              <div className="home-reco-row__name">{order.title || 'Заказ'}</div>
                              {order.is_urgent ? (
                                <span className="home-reco-row__hl-badge home-reco-row__hl-badge--urgent">🔥</span>
                              ) : null}
                              {order.order_top_until ? (
                                <span className="home-reco-row__hl-badge home-reco-row__hl-badge--top">ТОП</span>
                              ) : null}
                            </div>
                            <div className="home-reco-row__rating home-order-feed-row__price">
                              {order.budget != null ? `${formatMicroUsdt(order.budget)} USDT` : '—'}
                              {' · '}
                              {orderRefLabel(order.id)}
                            </div>
                            <div
                              className={`home-reco-row__cat${catLabel === '—' ? ' home-reco-row__cat--muted' : ''}`}
                            >
                              {catLabel}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            <HomeSectionHeader
              title="Срочные и ТОП"
              className="home-section-title--spaced"
              actionLabel="Все ›"
              onAction={() =>
                void (homeBoostOrdersTab === 'urgent' ? openCatalogUrgentOnly() : openCatalogTopOnly())
              }
            />
            <SegmentedTabs
              label="Срочные и ТОП"
              value={homeBoostOrdersTab}
              tabs={[{ id: 'urgent', label: 'Срочные' }, { id: 'top', label: 'ТОП задания' }]}
              onChange={setHomeBoostOrdersTab}
            />
            <div className="home-urgent-scroll">
              {homeBoostFeedOrders.length === 0 ? (
                <p className="home-empty-inline">
                  {homeBoostOrdersTab === 'urgent' ? 'Пока нет срочных заказов' : 'Пока нет заданий в ТОП'}
                </p>
              ) : (
                homeBoostFeedOrders.map((order: any) => (
                  <button
                    key={`boost-${homeBoostOrdersTab}-${order.id}`}
                    type="button"
                    className={`home-order-card${
                      homeBoostOrdersTab === 'urgent' ? ' home-order-card--blaze' : ' home-order-card--order-top'
                    }`}
                    data-deadline-tick={deadlineTick}
                    onClick={() => void viewOrderDetails(order)}
                  >
                    {order.customer_first_name || order.customer_last_name || order.customer_avatar_url ? (
                      <div className="home-order-card__customer home-order-card__customer--blaze-no-header">
                        <span className="home-order-card__avatar-anchor">
                          {order.customer_avatar_url ? (
                            <img src={order.customer_avatar_url} alt="" className="home-order-card__avatar" />
                          ) : (
                            <span className="home-order-card__avatar home-order-card__avatar--ph">
                              {order.customer_first_name?.[0]}
                              {order.customer_last_name?.[0]}
                            </span>
                          )}
                          <PresenceStatusDot lastSeenAt={order.customer_last_seen_at} />
                        </span>
                        <span className="home-order-card__customer-name">
                          {displayNameUser({
                            first_name: order.customer_first_name,
                            last_name: order.customer_last_name
                          })}
                        </span>
                      </div>
                    ) : null}
                    <div className="home-order-card__title-row home-order-card__title-row--single">
                      <span className="home-order-card__title">{order.title || 'Заказ'}</span>
                    </div>
                    <div className="home-order-card__price">
                      {order.budget != null ? `${formatMicroUsdt(order.budget)} USDT` : '—'}
                    </div>
                    <div className="home-order-card__meta home-order-card__meta--split">
                      <span className="home-order-card__meta-primary">
                        {order.execution_deadline_at
                          ? formatOrderDeadlineRemaining(order.execution_deadline_at)
                          : order.execution_deadline_value && order.execution_deadline_unit
                            ? `${formatDurationValueUnit(order.execution_deadline_value, order.execution_deadline_unit)}`
                            : order.status
                              ? orderStatusLabel(order.status)
                              : '—'}
                      </span>
                      <div className="home-order-card__urgent-bottom-rail">
                        <span
                          className={`home-order-card__status ${
                            homeBoostOrdersTab === 'urgent'
                              ? 'home-order-card__status--urgent'
                              : 'home-order-card__status--top-order'
                          }`}
                        >
                          {homeBoostOrdersTab === 'urgent' ? '🔥 Срочно' : 'ТОП'}
                        </span>
                        <span className="home-order-card__rail-id" title="Номер заказа">
                          {orderRefLabel(order.id)}
                        </span>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        ) : null}

        {effectiveHomeRole === 'customer' ? (
          <SegmentedTabs
            label="Исполнители и услуги"
            value={homeCustomerDiscoverTab}
            tabs={[
              { id: 'recommended', label: 'Рекомендуемые' },
              { id: 'vip', label: 'VIP' },
              { id: 'top', label: 'ТОП' }
            ]}
            onChange={setHomeCustomerDiscoverTab}
          />
        ) : null}

        {effectiveHomeRole === 'customer' && homeCustomerDiscoverTab === 'vip' ? (
          <>
            <div className="home-section-title home-top-section">
              <span>VIP исполнители</span>
              <div className="home-top-section__actions">
                <button
                  type="button"
                  className="home-section-linkish"
                  onClick={() => {
                    setExecVipDirectoryCategory('');
                    setExecVipDirectoryModalOpen(true);
                  }}
                >
                  Все
                </button>
                <button
                  type="button"
                  className="home-section-linkish"
                  onClick={() => {
                    setExecVipDirectoryCategory('');
                    setExecVipDirectoryModalOpen(true);
                  }}
                >
                  Категории
                </button>
              </div>
            </div>
            <div className="home-top-exec-strip-outer">
              {homeExecutorVipUsers.length === 0 ? (
                <p className="home-empty-inline">Пока нет исполнителей с VIP</p>
              ) : (
                <div className="home-hl-strip" aria-label="VIP исполнители">
                  {chunkPairs(homeExecutorVipUsers.slice(0, 25)).map((pair, colIdx) => (
                    <div key={`vip-col-${colIdx}`} className="home-hl-col">
                      {pair.map((u: any, rowInCol: number) => {
                        const idx = colIdx * 2 + rowInCol;
                        const catLabel = shortCategoryLineFromUser(u.categories);
                        return (
                          <button
                            key={u.id}
                            type="button"
                            className="home-reco-row home-reco-row--segment-vip"
                            onClick={() => setViewingProfile(u.id)}
                          >
                            <span className="home-reco-row__num">{idx + 1}</span>
                            <div className="home-reco-row__avatar-wrap">
                              <div className="home-reco-row__avatar">
                                {u.avatar_url ? (
                                  <img src={u.avatar_url} alt="" />
                                ) : (
                                  <>
                                    {u.first_name?.[0]}
                                    {u.last_name?.[0]}
                                  </>
                                )}
                              </div>
                              <PresenceStatusDot lastSeenAt={u.last_seen_at} />
                            </div>
                            <div className="home-reco-row__body">
                              <div className="home-reco-row__name-line">
                                <div className="home-reco-row__name">{displayNameUser(u)}</div>
                                <span className="home-reco-row__hl-badge home-reco-row__hl-badge--vip">VIP</span>
                              </div>
                              <div className="home-reco-row__rating">
                                ⭐ {Number(u.rating ?? 0).toFixed(2)} · {u.total_ratings ?? 0} оц.
                              </div>
                              <div
                                className={`home-reco-row__cat${catLabel === 'Не указано' ? ' home-reco-row__cat--muted' : ''}`}
                              >
                                {catLabel}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : null}

        {effectiveHomeRole === 'customer' && homeCustomerDiscoverTab === 'top' ? (
          <>
            <div className="home-section-title home-top-section">
              <span>ТОП исполнители</span>
              <div className="home-top-section__actions">
                <button
                  type="button"
                  className="home-section-linkish"
                  onClick={() => {
                    setTopDirectoryCategory('');
                    setTopDirectoryModalOpen(true);
                  }}
                >
                  Все
                </button>
                <button
                  type="button"
                  className="home-section-linkish"
                  onClick={() => {
                    setTopDirectoryCategory('');
                    setTopDirectoryModalOpen(true);
                  }}
                >
                  Категории
                </button>
              </div>
            </div>
            <div className="home-top-exec-strip-outer">
              {homeTopExecutors.length === 0 ? (
                <p className="home-empty-inline">Пока нет исполнителей с активным ТОП</p>
              ) : (
                <div className="home-hl-strip" aria-label="ТОП исполнители">
                  {chunkPairs(homeTopExecutors.slice(0, 25)).map((pair, colIdx) => (
                    <div key={`top-col-${colIdx}`} className="home-hl-col">
                      {pair.map((u: any, rowInCol: number) => {
                        const idx = colIdx * 2 + rowInCol;
                        const catLabel = shortCategoryLineFromUser(u.categories);
                        return (
                          <button
                            key={u.id}
                            type="button"
                            className="home-reco-row home-reco-row--segment-top"
                            onClick={() => setViewingProfile(u.id)}
                          >
                            <span className="home-reco-row__num">{idx + 1}</span>
                            <div className="home-reco-row__avatar-wrap">
                              <div className="home-reco-row__avatar home-reco-row__avatar--segment-top">
                                {u.avatar_url ? (
                                  <img src={u.avatar_url} alt="" />
                                ) : (
                                  <>
                                    {u.first_name?.[0]}
                                    {u.last_name?.[0]}
                                  </>
                                )}
                              </div>
                              <PresenceStatusDot lastSeenAt={u.last_seen_at} />
                            </div>
                            <div className="home-reco-row__body">
                              <div className="home-reco-row__name-line">
                                <div className="home-reco-row__name">{displayNameUser(u)}</div>
                                <span className="home-reco-row__hl-badge home-reco-row__hl-badge--top">ТОП</span>
                              </div>
                              <div className="home-reco-row__rating">
                                ⭐ {Number(u.rating ?? 0).toFixed(2)} · {u.total_ratings ?? 0} оц.
                              </div>
                              <div
                                className={`home-reco-row__cat${catLabel === 'Не указано' ? ' home-reco-row__cat--muted' : ''}`}
                              >
                                {catLabel}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : null}

        {effectiveHomeRole === 'executor' ||
        (effectiveHomeRole === 'customer' && homeCustomerDiscoverTab === 'recommended') ? (
          <>
        <div className="home-section-title home-reco-section">
          <span>{recoTitle}</span>
          <button
            type="button"
            onClick={() => {
              if (effectiveHomeRole === 'customer') {
                setOfferOrder(null);
                setExecutorQuery('');
                setExecutorCategory('');
                setExecutorResults([]);
                setExecutorSearchLoading(true);
                setShowFindExecutor(true);
                void searchExecutors('', '');
              } else {
                void openCatalog();
              }
            }}
          >
            Все ›
          </button>
        </div>
        <div className="home-reco-panels-scroll home-reco-panels-scroll--cols">
          {homeRecommendUsers.length === 0 ? (
            <p className="home-empty-inline">Пока нет {oppositeNoun} в каталоге</p>
          ) : (
            recoUserPanels.map((panel, panelIdx) => (
              <div key={panelIdx} className="home-reco-panel">
                {panel.map((u: any, rowInPanel: number) => {
                  const globalIdx = panelIdx * 3 + rowInPanel;
                  const catLabel = shortCategoryLineFromUser(u.categories);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={`home-reco-row${u.vip_active ? ' home-reco-row--vip' : ''}${
                        u.top_active ? ' home-reco-row--top' : ''
                      }${u.executor_vip_active ? ' home-reco-row--exec-vip' : ''}`}
                      onClick={() => setViewingProfile(u.id)}
                    >
                      <span className="home-reco-row__num">{globalIdx + 1}</span>
                      <div className="home-reco-row__avatar-wrap">
                        {u.vip_active ? (
                          <span className="home-reco-row__crown" aria-hidden>
                            👑
                          </span>
                        ) : null}
                        <div className="home-reco-row__avatar">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt="" />
                          ) : (
                            <>
                              {u.first_name?.[0]}
                              {u.last_name?.[0]}
                            </>
                          )}
                        </div>
                        <PresenceStatusDot lastSeenAt={u.last_seen_at} />
                      </div>
                      <div className="home-reco-row__body">
                        <div className="home-reco-row__name">{displayNameUser(u)}</div>
                        <div className="home-reco-row__rating">
                          ⭐ {Number(u.rating ?? 0).toFixed(2)} · {u.total_ratings ?? 0} оц.
                        </div>
                        <div
                          className={`home-reco-row__cat${catLabel === 'Не указано' ? ' home-reco-row__cat--muted' : ''}`}
                        >
                          {catLabel}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
          </>
        ) : null}


        {effectiveHomeRole === 'executor' && homeVipUsers.length > 0 ? (
        <div className="home-vip-block">
          <h2 className="home-vip-page-title">👑 {vipTitle}</h2>
          <p className="home-vip-page-desc">
            Участники с активной подпиской VIP — выше видимость и метка короны в профиле.
          </p>
          <div className="home-user-grid">
              {homeVipUsers.map((u: any) => (
                <button
                  key={u.id}
                  type="button"
                  className="home-user-card home-user-card--vip"
                  onClick={() => setViewingProfile(u.id)}
                >
                  <div className="home-user-card__avatar-wrap">
                    <span className="home-user-card__crown" aria-hidden>
                      👑
                    </span>
                    <div className="home-user-card__avatar">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" />
                      ) : (
                        <>
                          {u.first_name?.[0]}
                          {u.last_name?.[0]}
                        </>
                      )}
                    </div>
                    <PresenceStatusDot lastSeenAt={u.last_seen_at} />
                  </div>
                  <div>
                    <div className="home-user-card__name">{displayNameUser(u)}</div>
                    <div className="home-user-card__rating">
                      ⭐ {Number(u.rating ?? 0).toFixed(2)} · VIP
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
        ) : null}

        {topDirectoryModalOpen ? (
          <div
            className="home-top-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="ТОП исполнители"
          >
            <button
              type="button"
              className="home-top-modal-backdrop"
              aria-label="Закрыть"
              onClick={() => setTopDirectoryModalOpen(false)}
            />
            <div className="home-top-modal">
              <div className="home-top-modal__head">
                <h2 className="home-top-modal__title">ТОП исполнители</h2>
                <button
                  type="button"
                  className="home-top-modal__close"
                  onClick={() => setTopDirectoryModalOpen(false)}
                >
                  ✕
                </button>
              </div>
              <p className="home-top-modal__hint">Категория анкеты — фильтр списка.</p>
              <div className="home-top-modal__chips">
                <button
                  type="button"
                  className={
                    'home-top-chip' + (topDirectoryCategory === '' ? ' home-top-chip--on' : '')
                  }
                  onClick={() => setTopDirectoryCategory('')}
                >
                  Все
                </button>
                {CATEGORY_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={
                      'home-top-chip' +
                      (topDirectoryCategory === c.value ? ' home-top-chip--on' : '')
                    }
                    onClick={() => setTopDirectoryCategory(c.value)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {topDirectoryLoading ? (
                <p className="home-muted">Загрузка…</p>
              ) : topDirectoryUsers.length === 0 ? (
                <p className="home-muted">Никого в этой категории с активным ТОП.</p>
              ) : (
                <div className="home-top-modal__grid">
                  {topDirectoryUsers.map((u: any) => (
                    <button
                      key={u.id}
                      type="button"
                      className="home-top-modal-card"
                      onClick={() => {
                        setTopDirectoryModalOpen(false);
                        setViewingProfile(u.id);
                      }}
                    >
                      <div className="home-top-modal-card__avatar-wrap">
                        <div className="home-top-modal-card__avatar">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt="" />
                          ) : (
                            <>
                              {u.first_name?.[0]}
                              {u.last_name?.[0]}
                            </>
                          )}
                        </div>
                        <PresenceStatusDot lastSeenAt={u.last_seen_at} />
                      </div>
                      <span className="home-top-modal-card__pill">ТОП</span>
                      <span className="home-top-modal-card__name">{displayNameUser(u)}</span>
                      <span className="home-top-modal-card__meta">
                        ⭐ {Number(u.rating ?? 0).toFixed(2)} ·{' '}
                        {shortCategoryLineFromUser(u.categories)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {execVipDirectoryModalOpen ? (
          <div
            className="home-top-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="VIP исполнители"
          >
            <button
              type="button"
              className="home-top-modal-backdrop"
              aria-label="Закрыть"
              onClick={() => setExecVipDirectoryModalOpen(false)}
            />
            <div className="home-top-modal">
              <div className="home-top-modal__head">
                <h2 className="home-top-modal__title">VIP исполнители</h2>
                <button
                  type="button"
                  className="home-top-modal__close"
                  onClick={() => setExecVipDirectoryModalOpen(false)}
                >
                  ✕
                </button>
              </div>
              <p className="home-top-modal__hint">Подписка VIP исполнителя. Фильтр по категории анкеты.</p>
              <div className="home-top-modal__chips">
                <button
                  type="button"
                  className={
                    'home-top-chip' +
                    (execVipDirectoryCategory === '' ? ' home-top-chip--on' : '')
                  }
                  onClick={() => setExecVipDirectoryCategory('')}
                >
                  Все
                </button>
                {CATEGORY_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={
                      'home-top-chip' +
                      (execVipDirectoryCategory === c.value ? ' home-top-chip--on' : '')
                    }
                    onClick={() => setExecVipDirectoryCategory(c.value)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {execVipDirectoryLoading ? (
                <p className="home-muted">Загрузка…</p>
              ) : execVipDirectoryUsers.length === 0 ? (
                <p className="home-muted">Никого в этой категории с активным VIP.</p>
              ) : (
                <div className="home-top-modal__grid">
                  {execVipDirectoryUsers.map((u: any) => (
                    <button
                      key={u.id}
                      type="button"
                      className="home-top-modal-card"
                      onClick={() => {
                        setExecVipDirectoryModalOpen(false);
                        setViewingProfile(u.id);
                      }}
                    >
                      <div className="home-top-modal-card__avatar-wrap">
                        <div className="home-top-modal-card__avatar">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt="" />
                          ) : (
                            <>
                              {u.first_name?.[0]}
                              {u.last_name?.[0]}
                            </>
                          )}
                        </div>
                        <PresenceStatusDot lastSeenAt={u.last_seen_at} />
                      </div>
                      <span className="home-top-modal-card__pill home-top-modal-card__pill--vip">VIP</span>
                      <span className="home-top-modal-card__name">{displayNameUser(u)}</span>
                      <span className="home-top-modal-card__meta">
                        ⭐ {Number(u.rating ?? 0).toFixed(2)} ·{' '}
                        {shortCategoryLineFromUser(u.categories)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </HomePage>
    </div>
    </>
  );
}

export default App;